// @ts-nocheck

import React, { Suspense, lazy, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
// === Eager imports — chỉ giữ lại các component cốt lõi (LCP / shell / dashboard / modal-mounted-always) ===
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import ContactButtons from './components/common/ContactButtons';
import Dashboard from './components/Dashboard';
import TeacherDashboard from './components/TeacherDashboard';
import ParentDashboard from './components/ParentDashboard';
import DocumentLibrary from './components/DocumentLibrary';
import type { ScientificSimulation } from './features/scientific-simulation/types';
import { ThietKe3DPlayer } from './features/thiet-ke-3d';
import RouteTransitionFallback from './components/common/RouteTransitionFallback';
import LoadingDataIndicator from './components/common/LoadingDataIndicator';
import LoginRequired from './components/common/LoginRequired';
import ComingSoon from './components/common/ComingSoon';
import Login from './components/Login';
import RoleSelection from './components/RoleSelection';
import ClassManagement from './components/ClassManagement';
import ChildDashboard from './components/ChildDashboard';
import NotFound404 from './components/NotFound404';
import LearningModuleView from './components/LearningModuleView';
import FloatingMenu from './components/common/FloatingMenu';
import Feedback from './components/common/Feedback';
import OfflineSyncManager, { queueOfflineAction } from './components/common/OfflineSyncManager';
import OutOfCreditsModal from './components/common/OutOfCreditsModal';
import MustChangePasswordModal from './components/common/MustChangePasswordModal';
import BannerPopup from './components/common/BannerPopup';
import PaymentModal from './components/common/PaymentModal';
import Toast from './components/common/Toast';
import { ToastProvider } from './components/common/ToastContext';
import { AdSlot } from './components/common/AdSlot';
import { Breadcrumbs } from './components/common/Breadcrumbs';
import { View, Document, LessonPlan, Quiz, Game, Presentation, Homework, Activity, DrawingLesson, UserSettings, Class, Student, ChildLearningPlan, LearningModule, LearningHistory, PlanStatus, ImageResource, VideoResource, VideoProject, Notification, CreditPackage, PurchaseRecord, CreditShareLog, Assignment, Submission, Banner, UploadTask, ALL_QUICK_MENU_ITEMS_CONFIG } from './types';
import { mockDocuments, mockLessonPlans, mockQuizzes, mockGames, mockPresentations, mockHomeworks, mockActivities, mockStudents, mockClasses, mockChildren } from './data/mockData';
import { segmentDocument, fetchResource, fetchAssignmentById, fetchAllUserData, updateUserSettings, saveAllUserData, syncResource, syncWithRollback, duplicateResource, getAdsConfig, getBanners, getAnnouncements, getGroupCreditsSummary, setEduappAccessToken, getClassUpdates, getEduappAuthType, markNotificationAsRead, markAllNotificationsAsRead, deleteReadNotifications, submitStudentWork, uploadSubmissionAttachment, uploadLibraryDocument, uploadLibraryImage, uploadLibraryVideo, combineLibraryDocuments, EDUAPP_CREDIT_BALANCE_UPDATED_EVENT, captureReferralFromUrl, getUnifiedLearningPlans } from './services/apiService';
import { INITIAL_USER_SETTINGS, CREDIT_PACKAGES, APP_MODE, UI_CONFIG, API_EDU_PREFIX, API_BASE_URL, normalizeAdSenseClientId, PAYOS_RESULT_BROADCAST_CHANNEL, PAYOS_RESULT_STORAGE_KEY, FEATURE_FLAGS } from './config';
import { payosDebugLog, payosDebugWarn } from './utils/payosDebug';

import { saveUserDataToLocal, loadUserDataFromLocal, updateLocalResource } from './utils/storageUtils';
import { writeResourceCarrier, readResourceCarrier, extractCarrierKey } from './utils/resourceCarrier';
import { getCanvasReturnContext, clearCanvasReturnContext } from './features/deep-workspace/canvas/canvasLinkBridge';
import { lessonPlanFocusedMarkdown, lessonPlanToMarkdown } from './utils/lessonPlanSerializer';
import { formatGrade, SUBMISSION_STATUS } from './utils/assignmentUtils';
import { normalizeGameRecord } from './utils/gameManualUtils';
import { configService } from './services/configService';
import { updatePageSEO } from './utils/seoUtils';
import { consumeReturnUrl, buildLoginUrl, saveReturnUrl } from './utils/returnUrl';
import { getHomeworkFeatureLabel } from './utils/homeworkLabels';
import { canShowAdSlot, canShowDisplayAds } from './utils/adPolicy';

const LessonPlanCreator = lazy(() => import('./components/LessonPlanCreator'));
const QuizCreator = lazy(() => import('./components/QuizCreator'));
const GameCreator = lazy(() => import('./components/GameCreator').then(module => ({ default: module.GameCreator })));
const SlideCreator = lazy(() => import('./components/SlideCreator'));
const HomeworkCreator = lazy(() => import('./components/HomeworkCreator'));
const ActivityCreator = lazy(() => import('./components/ActivityCreator'));
const VideoCreator = lazy(() => import('./components/VideoCreator'));
const MasterSlideCreator = lazy(() => import('./components/MasterSlideCreator'));
const SKKNCreator = lazy(() => import('./components/SKKNCreator'));
const YearPlanCreator = lazy(() => import('./components/YearPlanCreator').then(m => ({ default: m.YearPlanCreator })));
const MeetingMinutesCreator = lazy(() => import('./components/MeetingMinutesCreator').then(m => ({ default: m.MeetingMinutesCreator })));
const ProfessionalPortfolio = lazy(() => import('./components/ProfessionalPortfolio').then(m => ({ default: m.ProfessionalPortfolio })));
const OralQuizCreator = lazy(() => import('./components/OralQuizCreator'));
const OralQuizRunner = lazy(() => import('./components/OralQuizRunner'));
const OnlineQuizTaker = lazy(() => import('./features/quiz-creator-v2').then(m => ({ default: m.OnlineQuizTaker })));
const LiveQuizPlayer = lazy(() => import('./features/live-quiz/LiveQuizPlayer'));
const QrCardQuizScanner = lazy(() => import('./features/qr-card-quiz/QrCardQuizScanner'));
const QuizAttemptResult = lazy(() => import('./features/quiz-creator-v2').then(m => ({ default: m.QuizAttemptResult })));
const ScientificSimulationPage = lazy(() => import('./features/scientific-simulation').then(module => ({ default: module.ScientificSimulationPage })));
const ThietKe3DPage = lazy(() => import('./features/thiet-ke-3d').then(module => ({ default: module.ThietKe3DPage })));
const InteractiveVideoPage = lazy(() => import('./features/interactive-video').then(module => ({ default: module.InteractiveVideoPage })));
const ClassroomToolbox = lazy(() => import('./features/classroom-toolbox').then(module => ({ default: module.ClassroomToolbox })));
const MindmapStudio = lazy(() => import('./features/mindmap').then(module => ({ default: module.MindmapStudio })));
const DocumentWorkspace = lazy(() => import('./components/DocumentWorkspace'));
const DrawingStudio = lazy(() => import('./components/DrawingStudio'));
const DesignCenter = lazy(() => import('./components/DesignCenter'));
const FeatureLanding = lazy(() => import('./features/feature-landing').then(module => ({ default: module.FeatureLanding })));
const FeatureLandingHub = lazy(() => import('./features/feature-landing').then(module => ({ default: module.FeatureLandingHub })));
const AudienceLanding = lazy(() => import('./features/feature-landing').then(module => ({ default: module.AudienceLanding })));

// === Lazy routes Tier-1 — pages ít truy cập / nội dung tĩnh / kéo theo dep nặng (framer-motion, …)
//     Tất cả đều được dùng trong <Routes> nên đã nằm trong cây <Suspense fallback={<RouteTransitionFallback/>}>.
//     Fallback được thiết kế "im lặng" 200ms đầu → đa số chuyển trang user không thấy spinner.
const AILab = lazy(() => import('./components/AILab'));
const CareerJourney = lazy(() => import('./components/CareerJourney'));
const AIEthics = lazy(() => import('./components/AIEthics'));
const AIFactCheck = lazy(() => import('./components/AIFactCheck'));
const PronunciationGuide = lazy(() => import('./components/PronunciationGuide'));
const JoinClassPage = lazy(() => import('./components/JoinClassPage'));
const JoinClassRouteGuard = lazy(() => import('./components/JoinClassRouteGuard'));
const TeacherJoinInvitationPage = lazy(() => import('./components/TeacherJoinInvitationPage'));
const SchoolJoinInvitationPage = lazy(() => import('./components/SchoolJoinInvitationPage'));
const SchoolManagement = lazy(() => import('./components/SchoolManagement'));
const SharedResourceView = lazy(() => import('./components/SharedResourceView').then(m => ({ default: m.SharedResourceView })));
const SharedLibrary = lazy(() => import('./components/SharedLibrary').then(m => ({ default: m.SharedLibrary })));
const CategoryDetailPage = lazy(() => import('./components/CategoryDetailPage').then(m => ({ default: m.CategoryDetailPage })));
const AssignmentDetailView = lazy(() => import('./components/AssignmentDetailView'));
const HistoryStoryteller = lazy(() => import('./components/HistoryStoryteller'));
const LiteratureAssistant = lazy(() => import('./components/LiteratureAssistant'));
const ScienceExplainer = lazy(() => import('./components/ScienceExplainer'));
const TransactionHistory = lazy(() => import('./components/TransactionHistory'));
const DrawingLessonViewer = lazy(() => import('./components/DrawingLessonViewer'));
const TermsOfUse = lazy(() => import('./components/TermsOfUse'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const CreditStore = lazy(() => import('./components/CreditStore'));
const PremiumBenefits = lazy(() => import('./components/PremiumBenefits'));
const AffiliateDashboard = lazy(() => import('./components/AffiliateDashboard'));
const AffiliateOrgDashboard = lazy(() => import('./components/AffiliateOrgDashboard'));
const AffiliateOrgJoin = lazy(() => import('./components/AffiliateOrgJoin'));
const UserGuide = lazy(() => import('./components/UserGuide'));
const GuideHub = lazy(() => import('./features/user-guide').then(module => ({ default: module.GuideHub })));
const GuideDetail = lazy(() => import('./features/user-guide').then(module => ({ default: module.GuideDetail })));
const Settings = lazy(() => import('./components/Settings'));
const GuestTryPage = lazy(() => import('./components/GuestTryPage'));
const GuestCreatorHost = lazy(() => import('./components/GuestCreatorHost'));
// === Deep Workspace (Phase 0: shell đa role — GV / HS / PH) ===
const TeacherWorkspacePage = lazy(() => import('./features/deep-workspace').then(m => ({ default: m.TeacherWorkspacePage })));
const StudentWorkspacePage = lazy(() => import('./features/deep-workspace').then(m => ({ default: m.StudentWorkspacePage })));
const StudentGoalsTasksPage = lazy(() => import('./features/deep-workspace/student/StudentGoalsTasksPage'));
const StudentProgressPage   = lazy(() => import('./features/deep-workspace/student/StudentProgressPage'));
const StudentTutorPage      = lazy(() => import('./features/deep-workspace/student/StudentTutorPage'));
const StudentSchedulePage   = lazy(() => import('./features/deep-workspace/student/StudentSchedulePage'));
const StudentLearningPathPage = lazy(() => import('./features/deep-workspace/student/StudentLearningPathPage'));
const StudentPracticePage = lazy(() => import('./features/deep-workspace/student/StudentPracticePage'));
const ParentWorkspacePage = lazy(() => import('./features/deep-workspace').then(m => ({ default: m.ParentWorkspacePage })));
const ParentProgressPage  = lazy(() => import('./features/deep-workspace/parent/ParentProgressPage'));
const ParentGradesPage    = lazy(() => import('./features/deep-workspace/parent/ParentGradesPage'));
const ParentAlertsPage    = lazy(() => import('./features/deep-workspace/parent/ParentAlertsPage'));
const ParentPlanPage      = lazy(() => import('./features/deep-workspace/parent/ParentPlanPage'));
const ParentActivityPage  = lazy(() => import('./features/deep-workspace/parent/ParentActivityPage'));
const ParentCareerPage    = lazy(() => import('./features/deep-workspace/parent/ParentCareerPage'));
const ParentChildrenPage  = lazy(() => import('./features/deep-workspace/parent/ParentChildrenPage'));
const SharedAchievementPage = lazy(() => import('./components/achievement/SharedAchievementPage'));
const InvitePage = lazy(() => import('./components/invite/InvitePage'));
const ParentAssignmentsPage = lazy(() => import('./features/deep-workspace/parent/ParentAssignmentsPage'));
const ParentLearningPathPage = lazy(() => import('./features/deep-workspace/parent/ParentLearningPathPage'));
// Shell workspace toàn cục — import trực tiếp (không qua index) để KHÔNG kéo pages/canvas vào bundle eager.
import { AppWorkspaceShell } from './features/deep-workspace/components/AppWorkspaceShell';
import { TeacherContextBreadcrumb } from './features/deep-workspace/teacher/TeacherContextBreadcrumb';
import { TeacherOnboardingWizard } from './features/deep-workspace/teacher/TeacherOnboardingWizard';
import { ParentOnboardingWizard } from './features/deep-workspace/parent/ParentOnboardingWizard';
import UpgradeModal from './components/common/UpgradeModal';

/**
 * Điều hướng tới trang đăng nhập nhưng GIỮ LẠI URL đích hiện tại (pathname + query + hash)
 * dưới dạng ?returnUrl=… để sau khi đăng nhập quay lại đúng trang. Thay cho <LoginRedirect />
 * ở mọi route bảo vệ → deep-link login hoạt động thống nhất cho toàn app.
 */
const LoginRedirect: React.FC = () => {
  const loc = useLocation();
  const target = `${loc.pathname}${loc.search || ''}${loc.hash || ''}`;
  return <Navigate to={buildLoginUrl(target)} replace />;
};

const ViewPath: Record<View, string> = {
  [View.Dashboard]: '/',
  [View.Login]: '/dang-nhap',
  [View.RoleSelection]: '/chon-vai-tro',
  [View.Drawing]: '/hoc-ve',
  [View.HistoryStoryteller]: '/ke-chuyen-lich-su',
  [View.LiteratureAssistant]: '/tro-ly-van-hoc',
  [View.ScienceExplainer]: '/giai-thich-khoa-hoc',
  [View.TermsOfUse]: '/dieu-khoan',
  [View.PrivacyPolicy]: '/chinh-sach-bao-mat',
  [View.CreditStore]: '/cua-hang-credit',
  [View.Pronunciation]: '/luyen-phat-am',
  [View.ClassroomToolbox]: '/hop-cong-cu-lop',
  [View.TeacherTools]: '/cong-cu-giao-vien',
  [View.ChildDashboard]: '/bang-hoc-cua-con/:slugWithId?',
  [View.ParentDashboard]: '/bang-dieu-khien-phu-huynh',
  [View.StudentDashboard]: '/workspace/hoc-sinh',
  [View.LearningModuleView]: '/bai-hoc/:planSlugWithId/:moduleSlugWithId',
  [View.ClassManagement]: '/quan-ly-lop-hoc/:slugWithId?',
  [View.LessonPlanCreator]: '/tao-giao-an/:slugWithId?',
  [View.QuizCreator]: '/tao-de-kiem-tra/:slugWithId?',
  [View.GameCreator]: '/tao-tro-choi/:slugWithId?',
  [View.SlideCreator]: '/tao-trinh-chieu/:slugWithId?',
  [View.DocumentLibrary]: '/thu-vien',
  [View.HomeworkCreator]: '/tao-bai-tap/:slugWithId?',
  [View.OralQuizRunner]: '/kiem-tra-kien-thuc/chay/:slugWithId?',
  [View.OnlineQuizTaker]: '/lam-de/:slugWithId?',
  [View.QuizAttemptResult]: '/ket-qua-de/:attemptId.html?',
  [View.OralQuizCreator]: '/kiem-tra-kien-thuc/tao',
  [View.OralQuizView]: '/kiem-tra-kien-thuc/:slugWithId.html',
  [View.OralQuizEdit]: '/kiem-tra-kien-thuc/:slugWithId.html/edit',
  [View.OralQuizHistory]: '/kiem-tra-kien-thuc/lich-su',
  [View.ScientificSimulationCreator]: '/mo-phong-khoa-hoc/tao',
  [View.ScientificSimulationView]: '/mo-phong-khoa-hoc/:slugWithId.html',
  [View.ScientificSimulationEdit]: '/mo-phong-khoa-hoc/:slugWithId.html/edit',
  [View.MindmapStudio]: '/so-do-tu-duy',
  [View.AILab]: '/phong-lab-ai',
  [View.AIEthics]: '/thu-thach-dao-duc-ai',
  [View.AIFactCheck]: '/kiem-chung-thong-tin-ai',
  [View.CareerJourney]: '/hanh-trinh-kham-pha',
  [View.ThietKe3DCreator]: '/thiet-ke-3d/tao',
  [View.ThietKe3DView]: '/thiet-ke-3d/:slugWithId.html',
  [View.ThietKe3DEdit]: '/thiet-ke-3d/:slugWithId.html/edit',
  [View.ActivityCreator]: '/hoat-dong-sang-tao/:slugWithId?',
  [View.VideoCreator]: '/tao-video/:slugWithId?',
  [View.MasterSlideCreator]: '/slide-mau',
  [View.Settings]: '/cai-dat',
  [View.DocumentWorkspace]: '/soan-thao/:slugWithId?',
  [View.DesignCenter]: '/thiet-ke',
  [View.InteractiveVideo]: '/video-tuong-tac/:slugWithId.html?',
  [View.InteractiveVideoEdit]: '/video-tuong-tac/:slugWithId.html/edit',
  [View.ComingSoon]: '/sap-ra-mat',
  [View.TransactionHistory]: '/lich-su-giao-dich',
  [View.SKKNCreator]: '/tao-sang-kien-kinh-nghiem/:slugWithId?',
  [View.YearPlanCreator]: '/ke-hoach-nam',
  [View.MeetingMinutes]: '/bien-ban-hop-to',
  [View.ProfessionalPortfolio]: '/ho-so-chuyen-mon',
  [View.SharedLibrary]: '/thu-vien-cong-dong',
  [View.CategoryDetailPage]: '/thu-vien-cong-dong/:category',
  [View.SharedResourceView]: '/chia-se/:resource/:slugWithId.html',
  [View.StudentAssignmentDetail]: '/:typePrefix/:slugWithId.html',
  [View.AssignmentDetail]: '/:typePrefix/:classIdentifier/:slugWithId.html',
  [View.UserGuide]: '/huong-dan',
  [View.AboutApp]: '/gioi-thieu',
  [View.AffiliateDashboard]: '/affiliate-dashboard',
  [View.TeacherJoinInvitation]: '/tham-gia-lop-giao-vien',
  [View.SchoolJoinInvitation]: '/tham-gia-truong',
  [View.SchoolManagement]: '/quan-ly-truong-hoc',

  // --- Resource Viewers (Existing saved content) ---
  [View.LessonPlanView]: '/giao-an/:slugWithId.html',
  [View.LessonPlanEdit]: '/giao-an/:slugWithId.html/edit',
  [View.QuizView]: '/de-kiem-tra/:slugWithId.html',
  [View.QuizEdit]: '/de-kiem-tra/:slugWithId.html/edit',
  [View.GameView]: '/tro-choi/:slugWithId.html',
  [View.GameEdit]: '/tro-choi/:slugWithId.html/edit',
  [View.SlideView]: '/trinh-chieu/:slugWithId.html',
  [View.SlideEdit]: '/trinh-chieu/:slugWithId.html/edit',
  [View.HomeworkView]: '/kho-bai-tap/:slugWithId.html',
  [View.HomeworkEdit]: '/kho-bai-tap/:slugWithId.html/edit',
  [View.ActivityView]: '/kho-hoat-dong/:slugWithId.html',
  [View.ActivityEdit]: '/kho-hoat-dong/:slugWithId.html/edit',
  [View.DrawingLessonView]: '/bai-hoc-ve/:slugWithId.html',
  [View.DrawingLessonEdit]: '/bai-hoc-ve/:slugWithId.html/edit',
};

export const RESOURCE_TYPE_URL_MAP: Record<string, string> = {
  'lesson-plans': 'giao-an',
  'drawing-lessons': 'bai-hoc-ve',
  'quizzes': 'de-kiem-tra',
  'games': 'tro-choi',
  'presentations': 'trinh-chieu',
  'homeworks': 'kho-bai-tap',
  'activities': 'kho-hoat-dong',
  'documents': 'tai-lieu',
  'video-resources': 'video',
  'video-projects': 'video-tuong-tac',
  'skkn-projects': 'skkn',
  'year-plans': 'ke-hoach-nam',
  'scientific-simulations': 'mo-phong-khoa-hoc',
  'thiet-ke-3d': 'thiet-ke-3d',
  'ai-fact-check-sessions': 'kiem-chung-thong-tin-ai',
  'ai-ethics-challenge-sessions': 'thu-thach-dao-duc-ai',
  'ai-lab-sessions': 'phong-lab-ai',
};

export const REVERSE_RESOURCE_TYPE_URL_MAP: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(RESOURCE_TYPE_URL_MAP).map(([k, v]) => [v, k])
  ),
  'de-kiem-tra': 'quizzes',
  'bai-hoc-ve': 'drawing-lessons',
  'bai-tap': 'homeworks',
  'hoat-dong': 'activities',
  // Backend slug nguồn (ngắn) cũng phải được nhận đúng tên bảng/resource khi truy cập
  'kiem-chung-ai': 'ai-fact-check-sessions',
};

function useLocalStorage<T>(key: string, initialValue: T, userId: string | null): [T, React.Dispatch<React.SetStateAction<T>>] {
  const userKey = userId ? `${userId}_${key}` : null;
  const [value, setValue] = useState<T>(initialValue);
  const initialRef = useRef(initialValue);
  initialRef.current = initialValue;

  useEffect(() => {
    const initial = initialRef.current;
    if (!userKey) {
      setValue(initial);
      return;
    }
    try {
      const item = window.localStorage.getItem(userKey);
      if (item !== null) {
        setValue(JSON.parse(item));
      } else {
        setValue(initial);
        window.localStorage.setItem(userKey, JSON.stringify(initial));
      }
    } catch (error) {
      console.error(`Lỗi đọc localStorage key “${userKey}”:`, error);
      setValue(initial);
    }
  // Chỉ phụ thuộc userKey/key; bỏ initialValue để tránh vòng lặp khi caller truyền [], {} mỗi render
  }, [userKey, key]);

  const setStoredValue = useCallback((newValue: React.SetStateAction<T>) => {
    setValue(prev => {
      const valueToStore = newValue instanceof Function ? newValue(prev) : newValue;
      if (userKey) {
        try {
          window.localStorage.setItem(userKey, JSON.stringify(valueToStore));
        } catch (error) {
          console.error(`Lỗi ghi vào localStorage key “${userKey}”:`, error);
        }
      }
      return valueToStore;
    });
  }, [userKey]);

  return [value, setStoredValue];
}

type HistoryEntry = {
  view: View;
  payload: any;
};

const protectedViews = new Set([
    View.TeacherTools, View.LessonPlanCreator, View.QuizCreator, View.GameCreator,
    View.SlideCreator, View.DocumentLibrary, View.HomeworkCreator, View.ActivityCreator,
    View.MasterSlideCreator, View.Settings, View.DocumentWorkspace, View.ClassManagement,
    View.ChildDashboard, View.LearningModuleView, View.StudentDashboard, View.VideoCreator,
    View.CreditStore, View.SKKNCreator, View.YearPlanCreator, View.MeetingMinutes, View.ProfessionalPortfolio,
    View.ScientificSimulationCreator, View.ScientificSimulationView, View.ScientificSimulationEdit,
    View.ThietKe3DCreator, View.ThietKe3DView, View.ThietKe3DEdit,
    View.InteractiveVideo,
    View.LessonPlanView, View.QuizView, View.GameView,
    View.SlideView, View.HomeworkView, View.ActivityView,
    View.LessonPlanEdit, View.QuizEdit, View.GameEdit,
    View.SlideEdit, View.HomeworkEdit, View.ActivityEdit,
    View.DrawingLessonView, View.DrawingLessonEdit
]);

const comingSoonViews = new Set([
    View.ComingSoon,
]);

const AffiliateOrgDashboardRouteWrapper: React.FC<{
  user: any;
  onBack: () => void;
  onNavigate: (path: string) => void;
  onShowToast: (toast: { message: string; type: 'success' | 'error' | 'warning' | 'info' }) => void;
}> = ({ user, onBack, onNavigate, onShowToast }) => {
  const params = useParams<{ orgId?: string }>();
  const orgId = params.orgId ? parseInt(params.orgId, 10) : undefined;
  return (
    <AffiliateOrgDashboard
      user={user}
      orgId={Number.isFinite(orgId as any) ? (orgId as number) : undefined}
      onBack={onBack}
      onNavigate={onNavigate}
      onShowToast={onShowToast}
    />
  );
};

const ChildDashboardRouteWrapper: React.FC<{
  user: UserSettings | null;
  children: Child[];
  navigationPayload: any;
  creatorProps: any;
  assignments: Assignment[];
  homeworks: Homework[];
  submissions: Submission[];
  classes: Class[];
  students: Student[];
  notifications: Notification[];
  joinRequests: any[];
  childLearningPlans: ChildLearningPlan[];
  learningHistory: LearningHistory[];
  onSavePlan: any;
  onUpdatePlan: any;
  onDeletePlan: any;
  onStudentSubmit: any;
  onNotificationRead: any;
  isLoadingData: boolean;
  resourceLoadingStates: any;
}> = ({
  user,
  children,
  navigationPayload,
  creatorProps,
  assignments,
  homeworks,
  submissions,
  classes,
  students,
  notifications,
  joinRequests,
  childLearningPlans,
  learningHistory,
  onSavePlan,
  onUpdatePlan,
  onDeletePlan,
  onStudentSubmit,
  onNotificationRead,
  isLoadingData,
  resourceLoadingStates
}) => {
  const { slugWithId } = useParams();
  
  // Bóc tách ID từ format slug-ten-con-i9, p9, c9
  const urlChildId = useMemo(() => {
    if (!slugWithId) return null;
    const match = slugWithId.match(/-[ipcs](\d+)$/);
    return match ? match[1] : slugWithId;
  }, [slugWithId]);

  const isWorkingMode = useMemo(() => {
    return slugWithId?.includes('-c') || slugWithId?.includes('-s');
  }, [slugWithId]);
  
  if (!user) return <LoginRedirect />;
  if (user.role !== 'parent') return <Navigate to="/" />;

  const resolvedChild = navigationPayload.child ?? 
    (urlChildId ? children.find((c: Child) => String(c.id) === String(urlChildId)) : null) ??
    (navigationPayload.childId ? children.find((c: Child) => String(c.id) === String(navigationPayload.childId)) : null) ?? 
    (children.length > 0 ? children[0] : null);

  if (!resolvedChild) return <Navigate to="/bang-dieu-khien-phu-huynh" />;

  // SEO: Cập nhật thông tin con vào title
  useEffect(() => {
    if (resolvedChild) {
      updatePageSEO(`/bang-hoc-cua-con/${resolvedChild.id}`, {
        title: `Bảng học của ${resolvedChild.name} (${isWorkingMode ? 'Học sinh' : 'Phụ huynh'})`,
        description: `Theo dõi tiến độ học tập và kết quả của con ${resolvedChild.name} trên hệ thống.`
      });
    }
  }, [resolvedChild, isWorkingMode]);

  return (
    <ChildDashboard 
      {...creatorProps} 
      child={resolvedChild} 
      isWorkingMode={isWorkingMode}
      focusSection={navigationPayload.focusSection} 
      assignments={assignments} 
      homeworks={homeworks} 
      submissions={submissions} 
      classes={classes} 
      students={students} 
      allChildren={children} 
      notifications={notifications} 
      joinRequests={joinRequests} 
      learningPlans={childLearningPlans.filter((p: ChildLearningPlan) => p.childId === resolvedChild.id)} 
      learningHistory={learningHistory.filter((h: LearningHistory) => childLearningPlans.some((p: ChildLearningPlan) => p.id === h.planId && p.childId === resolvedChild.id))} 
      onSavePlan={onSavePlan} 
      onUpdatePlan={onUpdatePlan} 
      onDeletePlan={onDeletePlan} 
      onStudentSubmit={onStudentSubmit} 
      onNotificationRead={onNotificationRead} 
      isLoading={isLoadingData}
      resourceLoadingStates={resourceLoadingStates}
    />
  );
};

const LearningModuleViewRouteWrapper: React.FC<{
  onBack: any;
  onNavigate: any;
  navigationPayload: any;
  onComplete: any;
  learningPlans: ChildLearningPlan[];
  planBackPath?: string;
}> = ({ onBack, onNavigate, navigationPayload, onComplete, learningPlans, planBackPath }) => {
  const { planSlugWithId, moduleSlugWithId } = useParams();
  
  // Bóc tách ID từ slug-lo-trinh-i2 và slug-bai-hoc-m123
  const planId = useMemo(() => {
    if (!planSlugWithId) return null;
    const match = planSlugWithId.match(/-i(\d+)$/);
    return match ? match[1] : planSlugWithId;
  }, [planSlugWithId]);

  const moduleId = useMemo(() => {
    if (!moduleSlugWithId) return null;
    // ID nằm sau dấu '-m' CUỐI CÙNG. Dùng lastIndexOf vì tiêu đề bài thường chứa '-m'
    // (vd "bi-mat", "thong-minh") khiến regex khớp dấu '-m' đầu tiên sẽ tách SAI id.
    const idx = moduleSlugWithId.lastIndexOf('-m');
    return idx >= 0 ? moduleSlugWithId.slice(idx + 2) : moduleSlugWithId;
  }, [moduleSlugWithId]);
  
  // Ưu tiên plan từ App-state (đã cập nhật trạng thái completed qua onComplete) để
  // thanh tiến độ tăng đều khi học liền nhiều bài; payload.plan chỉ là fallback.
  const plan = learningPlans.find(p => String(p.id) === String(planId)) ?? navigationPayload.plan;
  const module = navigationPayload.module ?? plan?.dailyPlans?.flatMap(dp => dp.modules).find(m => String(m.id) === String(moduleId));

  const isPreview = !!navigationPayload.isPreview;

  // Chuỗi module phẳng (theo ngày) để điều hướng liền mạch + tính tiến độ.
  const sequence = useMemo(() => {
    const flat: { module: LearningModule; dayLabel: string; dayIndex: number; posInDay: number; dayCount: number }[] = [];
    (plan?.dailyPlans ?? []).forEach((dp: any, di: number) => {
      const mods = dp?.modules ?? [];
      mods.forEach((m: LearningModule, mi: number) => {
        flat.push({ module: m, dayLabel: dp?.day ?? `Ngày ${di + 1}`, dayIndex: di, posInDay: mi + 1, dayCount: mods.length });
      });
    });
    return flat;
  }, [plan]);

  // Dùng id của module ĐÃ RESOLVE (từ payload/lookup) để tìm vị trí — không lệ thuộc
  // việc tách id từ URL, tránh sai khi tiêu đề có '-m'.
  const curIdx = useMemo(
    () => sequence.findIndex(x => String(x.module.id) === String(module?.id ?? moduleId)),
    [sequence, module?.id, moduleId],
  );
  const curEntry = curIdx >= 0 ? sequence[curIdx] : null;
  const nextEntry = curIdx >= 0 ? sequence[curIdx + 1] : null;
  const overallDone = useMemo(() => sequence.filter(x => x.module.status === 'completed').length, [sequence]);
  const isLastOfPlan = curIdx === sequence.length - 1;
  const isLastOfDay = !!(curEntry && nextEntry && nextEntry.dayIndex !== curEntry.dayIndex);

  const goToModule = (m: LearningModule) => {
    onNavigate(View.LearningModuleView, { planId: String(planId), moduleId: String(m.id), plan, module: m, isPreview });
  };

  if (!plan || !module) {
    return (
      <div className="p-8 text-center bg-white rounded-2xl shadow-sm border border-slate-100">
        <p className="text-slate-500 mb-4">Không tìm thấy nội dung bài học này.</p>
        <button onClick={onBack} className="px-4 py-2 bg-teal-600 text-white rounded-lg">Quay lại</button>
      </div>
    );
  }

  // SEO: Cập nhật thông tin bài học vào title
  useEffect(() => {
    if (module) {
      updatePageSEO(`/bai-hoc/${planSlugWithId}/${moduleSlugWithId}`, {
        title: `${module.title}${isPreview ? ' (Xem thử)' : ''} - Bài học AI`,
        description: module.content?.slice(0, 160) || `Học tập bài "${module.title}" với trợ lý giáo dục AI.`
      });
    }
  }, [module, planSlugWithId, moduleSlugWithId, isPreview]);

  return (
    <LearningModuleView
      onBack={onBack}
      onBackToPlan={planBackPath ? () => onNavigate(planBackPath) : undefined}
      onNavigate={onNavigate}
      planId={String(planId)}
      module={module}
      onComplete={(pid, mid, correct) => onComplete(pid, mid, correct, isPreview)}
      isPreview={isPreview}
      planTitle={plan?.title}
      dayLabel={curEntry?.dayLabel}
      posInDay={curEntry?.posInDay}
      dayModuleCount={curEntry?.dayCount}
      overallDone={overallDone}
      overallTotal={sequence.length}
      nextModule={nextEntry?.module ?? null}
      nextDayLabel={isLastOfDay ? nextEntry?.dayLabel : undefined}
      isLastOfDay={isLastOfDay}
      isLastOfPlan={isLastOfPlan}
      onGoToModule={goToModule}
    />
  );
};

const ClassManagementRouteWrapper: React.FC<{
  commonProps: any;
  user: UserSettings;
  classes: Class[];
  students: Student[];
  homeworks: Homework[];
  assignments: Assignment[];
  submissions: Submission[];
  joinRequests: any[];
  onSaveClass: any;
  onUpdateClass: any;
  onDeleteClass: any;
  onSaveStudent: any;
  onSaveMultipleStudents: any;
  onUpdateStudent: any;
  onDeleteStudent: any;
  onAssignHomework: any;
  onDeleteAssignment: any;
  onGradeSubmission: any;
  navigationPayload: any;
  onRefreshData: any;
  onRefreshJoinRequests: any;
  onRefreshClassUpdates: any;
  onUpdateSettings?: React.Dispatch<React.SetStateAction<UserSettings | null>>;
  isLoading: boolean;
}> = (props) => {
  const { slugWithId } = useParams();
  const classId = useMemo(() => {
    if (!slugWithId) return null;
    const match = slugWithId.match(/-i(\d+)$/);
    return match ? match[1] : slugWithId;
  }, [slugWithId]);
  
  const { commonProps, ...rest } = props;
  
  // Mặc định luôn hiển thị 1 lớp đang active: ưu tiên payload điều hướng → lớp theo slug
  // → lớp đầu danh sách (khi vào /quan-ly-lop-hoc không kèm slug).
  const resolvedClass = props.navigationPayload.activeClass ??
    (classId
      ? props.classes.find(c => String(c.id) === String(classId))
      : (props.classes[0] ?? null));

  // SEO: Cập nhật thông tin lớp học vào title
  useEffect(() => {
    if (resolvedClass) {
      updatePageSEO(`/quan-ly-lop-hoc/${resolvedClass.id}`, {
        title: `Quản lý lớp ${resolvedClass.name}`,
        description: `Quản lý học sinh, bài tập và tiến độ của lớp ${resolvedClass.name}.`
      });
    }
  }, [resolvedClass]);

  return (
    <ClassManagement 
      {...commonProps} 
      {...rest}
      activeClass={resolvedClass}
      initialView={resolvedClass ? 'classDetail' : undefined}
    />
  );
};

/** Map of features and which roles are allowed to access them in Edit/Create mode */
const FeaturePermissions: Record<string, string[]> = {
  // Tạo đề kiểm tra V2: GV có wizard 4 bước đầy đủ; PH/HS có luồng quick-flow đơn giản hoá
  // (xem QuizCreator.tsx — branch render theo `audience`).
  [View.QuizCreator]: ['teacher', 'parent', 'student'],
  [View.QuizEdit]: ['teacher', 'parent', 'student'],
  [View.QuizView]: ['teacher', 'parent', 'student'],
  // Phase 2 — Làm đề online + xem kết quả: ai cũng được làm (kể cả GV thử đề)
  [View.OnlineQuizTaker]: ['teacher', 'parent', 'student'],
  [View.QuizAttemptResult]: ['teacher', 'parent', 'student'],
  [View.HomeworkCreator]: ['teacher', 'parent', 'student'],
  [View.HomeworkEdit]: ['teacher', 'parent', 'student'],
  [View.HomeworkView]: ['teacher', 'parent', 'student'],
  [View.SKKNCreator]: ['teacher'],
  [View.YearPlanCreator]: ['teacher'],
  [View.MeetingMinutes]: ['teacher'],
  [View.ProfessionalPortfolio]: ['teacher'],
  [View.ClassManagement]: ['teacher'],
  
  // Những màn SOẠN THẢO (Cho phép cả PH để tạo cho con)
  [View.LessonPlanCreator]: ['teacher', 'parent'],
  [View.LessonPlanEdit]: ['teacher', 'parent'],
  [View.LessonPlanView]: ['teacher', 'parent'],
  [View.SlideCreator]: ['teacher', 'parent', 'student'],
  [View.SlideEdit]: ['teacher', 'parent', 'student'],
  [View.SlideView]: ['teacher', 'parent', 'student'],
  [View.GameCreator]: ['teacher', 'parent'],
  [View.GameEdit]: ['teacher', 'parent'],
  [View.GameView]: ['teacher', 'parent'],
  [View.ActivityCreator]: ['teacher', 'parent'],
  [View.ActivityEdit]: ['teacher', 'parent'],
  [View.ActivityView]: ['teacher', 'parent'],
  [View.DrawingLessonEdit]: ['teacher', 'parent'],
  [View.DrawingLessonView]: ['teacher', 'parent'],
  [View.VideoCreator]: ['teacher', 'parent'],
  [View.DocumentWorkspace]: ['teacher', 'parent'],
  [View.DesignCenter]: ['teacher', 'parent', 'student'],
  [View.ScientificSimulationCreator]: ['teacher', 'parent'],
  [View.ScientificSimulationView]: ['teacher', 'parent'],
  [View.ScientificSimulationEdit]: ['teacher', 'parent'],
  [View.MindmapStudio]: ['teacher', 'parent', 'student'],
  [View.AILab]: ['student', 'teacher', 'parent'],
  [View.AIEthics]: ['student', 'teacher', 'parent'],
  [View.AIFactCheck]: ['student', 'teacher', 'parent'],
  [View.CareerJourney]: ['student', 'teacher', 'parent'],
  [View.ThietKe3DCreator]: ['teacher', 'parent'],
  [View.ThietKe3DView]: ['teacher', 'parent', 'student'],
  [View.ThietKe3DEdit]: ['teacher', 'parent'],
  [View.InteractiveVideo]: ['teacher', 'parent', 'student'],
  [View.InteractiveVideoEdit]: ['teacher', 'parent'],

  // Những màn XEM chung
  [View.DocumentLibrary]: ['teacher', 'parent', 'student'],
};

const UnauthorizedRedirect: React.FC<{ dashboardPath: string; onAddNotification: any }> = ({ dashboardPath, onAddNotification }) => {
  useEffect(() => {
    if (onAddNotification) {
      onAddNotification({ 
        type: 'deadline', 
        message: "Bạn không có quyền truy cập chức năng này hoặc tài nguyên này.", 
        icon: 'lock' 
      }, true);
    }
  }, [onAddNotification]);
  return <Navigate to={dashboardPath} replace />;
};

/** Banner nổi "← Quay về bài giảng" khi GV mở học liệu từ canvas node. */
const CanvasReturnBanner: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [ctx, setCtx] = React.useState(() => getCanvasReturnContext());

  // Cập nhật khi route thay đổi (user điều hướng sang trang khác)
  React.useEffect(() => {
    setCtx(getCanvasReturnContext());
  }, [location.pathname]);

  if (!ctx) return null;

  const handleReturn = () => {
    clearCanvasReturnContext();
    navigate(ctx.returnPath);
  };

  const handleDismiss = () => {
    clearCanvasReturnContext();
    setCtx(null);
  };

  return (
    <div
      style={{ zIndex: 9999 }}
      className="fixed top-0 left-0 right-0 flex items-center justify-between gap-3 px-4 py-2.5 bg-indigo-600 text-white text-sm shadow-md"
    >
      <button
        onClick={handleReturn}
        className="flex items-center gap-2 font-medium hover:underline focus:outline-none"
      >
        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Quay về bài giảng{ctx.canvasTitle ? `: ${ctx.canvasTitle}` : ''}
        {ctx.nodeLabel ? <span className="opacity-80 text-xs font-normal">({ctx.nodeLabel})</span> : null}
      </button>
      <button
        onClick={handleDismiss}
        aria-label="Đóng"
        className="opacity-70 hover:opacity-100 focus:outline-none"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

const CreatorRouteWrapper: React.FC<{
  view: View;
  Component: any;
  creatorProps: any;
  navState: any;
  displayUser: UserSettings | null;
  [key: string]: any;
}> = ({ view, Component, creatorProps, navState, displayUser, ...rest }) => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const slugWithId = params.slugWithId;
  const normalizedSlugWithId = stripHtmlSuffix(slugWithId);
  const editItemId = extractSlugId(normalizedSlugWithId);
  const isExistingResourceDetail =
    (view.toString().endsWith('View') || view.toString().endsWith('Edit')) && !!editItemId;

  /** Xóa query seed (initialContent, topic…) khi đang xem/sửa tài nguyên đã có ID — tránh URL dài & nhầm chế độ tạo mới */
  useEffect(() => {
    if (!isExistingResourceDetail) return;
    const sp = new URLSearchParams(location.search);
    const stripKeys = ['initialContent', 'detailedInstruction', 'topic', 'sourceId', 'carrier', 'step', 'buoc'];
    let changed = false;
    stripKeys.forEach((k) => {
      if (sp.has(k)) {
        sp.delete(k);
        changed = true;
      }
    });
    if (!changed) return;
    const next = `${location.pathname}${sp.toString() ? `?${sp.toString()}` : ''}`;
    navigate(next, { replace: true, state: location.state });
  }, [isExistingResourceDetail, editItemId, location.pathname, location.search, navigate, location.state]);

  /**
   * Đọc carrier (snapshot tài nguyên trong localStorage) NGAY ĐẦU component
   * để giữ thứ tự hooks ổn định bất kể role check phía dưới có early-return.
   * `useMemo` đảm bảo `readResourceCarrier` chỉ được gọi 1 lần (consume-once)
   * cho mỗi instance của route — kể cả khi component re-render nhiều lần.
   */
  const carrierKey = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return extractCarrierKey(sp);
  }, [location.search]);
  const carrier = useMemo(() => readResourceCarrier(carrierKey), [carrierKey]);

  // ROLE CHECK: Chỉ những role được phép mới được vào các màn Creator/Editor
  const user = creatorProps.userSettings;
  const role = displayUser?.role || 'student';
  const allowedRoles = FeaturePermissions[view] || ['teacher']; // Mặc định chỉ giáo viên

  // Chặn học sinh đăng nhập mã lớp (student_code) khỏi màn soạn thảo; học sinh tài khoản đầy đủ: theo FeaturePermissions (vd. trình chiếu).
  if (user?.authType === 'student_code' || !allowedRoles.includes(role)) {
    // ĐIỀU HƯỚNG THÔNG MINH: Nếu đang vào link nội bộ của tài nguyên (ví dụ /de-kiem-tra/...)
    // nhưng không có quyền (do là Phụ huynh/Học sinh), thử điều hướng sang link chia sẻ tương ứng.
    if (normalizedSlugWithId) {
      const pathPrefix = location.pathname.split('/')[1]; // Lấy 'de-kiem-tra', 'kiem-tra', v.v.
      const internalResourceTypes = ['de-kiem-tra', 'kiem-tra', 'de-thi', 'bai-kiem-tra', 'giao-an', 'bai-hoc-ve', 'tro-choi', 'trinh-chieu', 'kho-bai-tap', 'kho-hoat-dong'];
      
      if (internalResourceTypes.includes(pathPrefix)) {
        // Chuẩn hóa prefix cho link chia sẻ
        const quizPrefixes = ['de-kiem-tra', 'kiem-tra', 'de-thi', 'bai-kiem-tra'];
        const sharedPrefix = quizPrefixes.includes(pathPrefix) ? 'de-kiem-tra' : pathPrefix;
        return <Navigate to={`/chia-se/${sharedPrefix}/${ensureHtmlSuffix(normalizedSlugWithId)}`} replace />;
      }
    }

    const dashboardPath = role === 'parent' ? '/workspace/phu-huynh' : '/workspace/hoc-sinh';
    
    // Nếu user đã login nhưng không có quyền, bắn thông báo và điều hướng
    if (user && !allowedRoles.includes(role)) {
       return <UnauthorizedRedirect dashboardPath={dashboardPath} onAddNotification={creatorProps.onAddNotification} />;
    }
    
    return <Navigate to={dashboardPath} replace />;
  }

  // Lấy sourceId từ Query String nếu có (carrier đã được đọc ở đầu component)
  const searchParams = new URLSearchParams(location.search);
  const sourceId = !isExistingResourceDetail
    ? (searchParams.get('sourceId') || carrier?.sourceId || navState?.sourceId)
    : undefined;

  // Dữ liệu mồi chỉ áp dụng khi TẠO MỚI từ giáo án/tài liệu — không đọc từ URL khi xem/sửa bản ghi đã lưu
  const initialTopic = !isExistingResourceDetail
    ? (carrier?.initialTopic || searchParams.get('topic') || searchParams.get('initialTopic'))
    : undefined;
  const initialContent = !isExistingResourceDetail
    ? (carrier?.initialContent || searchParams.get('initialContent'))
    : undefined;
  const detailedInstruction = !isExistingResourceDetail
    ? (carrier?.detailedInstruction || searchParams.get('detailedInstruction'))
    : undefined;
  const sourceLessonPlanFromCarrier = carrier?.sourceKind === 'lesson_plan' ? carrier?.sourceSnapshot : undefined;

  const isEditModeFromUrl = location.pathname.endsWith('/edit');

  /** Buộc mount lại khi đổi route/slug hoặc chuyển tạo mới ↔ xem — tránh giữ state creator khi URL đã đổi */
  const creatorInstanceKey = `${location.pathname}-${view}-${normalizedSlugWithId || 'root'}-${isEditModeFromUrl ? 'edit' : 'view'}`;

  return (
    <>
      {isExistingResourceDetail && <CanvasReturnBanner />}
      <Component
        key={creatorInstanceKey}
        {...creatorProps}
        {...navState}
        {...rest}
        view={view}
        editItemId={editItemId}
        sourceId={sourceId}
        sourceLessonPlan={sourceLessonPlanFromCarrier || (navState?.sourceLessonPlan) || (rest?.sourceLessonPlan)}
        initialTopic={initialTopic || (navState?.initialTopic) || (rest?.initialTopic)}
        initialContent={initialContent || (navState?.initialContent) || (rest?.initialContent)}
        detailedInstruction={detailedInstruction || (navState?.detailedInstruction) || (rest?.detailedInstruction)}
        featurePayload={carrier?.featurePayload || navState?.featurePayload || rest?.featurePayload}
        isEditModeFromUrl={isEditModeFromUrl}
      />
    </>
  );
};

const stripHtmlSuffix = (value?: string | null) => value ? value.replace(/\.html$/i, '') : '';

const ensureHtmlSuffix = (value?: string | null) => {
  const normalized = stripHtmlSuffix(value);
  return normalized ? `${normalized}.html` : '';
};

const extractSlugId = (value?: string | null, pattern: RegExp = /-i([A-Za-z0-9_]+)$/) => {
  const normalized = stripHtmlSuffix(value);
  if (!normalized) return null;
  const match = normalized.match(pattern);
  return match ? match[1] : normalized;
};

const LegacyOralQuizRedirect: React.FC = () => {
  const params = useParams();
  const location = useLocation();
  const slugWithId = params.slugWithId;
  const isRunRoute = location.pathname.includes('/chay/');
  const isEditRoute = location.pathname.endsWith('/edit');

  let nextPath = '/kiem-tra-kien-thuc/tao';
  if (isRunRoute) {
    nextPath = `/kiem-tra-kien-thuc/chay/${slugWithId || ''}`;
  } else if (isEditRoute) {
    nextPath = `/kiem-tra-kien-thuc/${slugWithId || ''}/edit`;
  } else if (slugWithId) {
    nextPath = `/kiem-tra-kien-thuc/${slugWithId}`;
  }

  return <Navigate to={`${nextPath}${location.search}`} replace />;
};

/** Chuyển URL cũ /tao-skkn → /tao-sang-kien-kinh-nghiem (giữ slug và query) */
const LegacySkknRedirect: React.FC = () => {
  const location = useLocation();
  const to = location.pathname.replace('/tao-skkn', '/tao-sang-kien-kinh-nghiem') + location.search;
  return <Navigate to={to} replace />;
};

/** Chuẩn hóa URL detail sang dạng .html để ổn định SEO và tránh nhập nhằng slug. */
const LegacyHtmlDetailRedirect: React.FC<{
  basePath: string;
  fallbackPath?: string;
}> = ({ basePath, fallbackPath = basePath }) => {
  const { slugWithId, resource } = useParams();
  const location = useLocation();
  const isEditRoute = location.pathname.endsWith('/edit');

  if (!slugWithId) {
    return <Navigate to={fallbackPath} replace />;
  }

  const nextPath = `${basePath}${resource ? `/${resource}` : ''}/${ensureHtmlSuffix(slugWithId)}${isEditRoute ? '/edit' : ''}`;
  return <Navigate to={`${nextPath}${location.search}`} replace />;
};

/** Chuẩn hóa URL mô phỏng khoa học sang dạng .html để ổn định SEO và tránh nhập nhằng slug. */
const LegacyScientificSimulationRedirect: React.FC = () => {
  const { slugWithId } = useParams();
  const location = useLocation();
  const isEditRoute = location.pathname.endsWith('/edit');

  if (!slugWithId) {
    return <Navigate to="/mo-phong-khoa-hoc/tao" replace />;
  }

  const normalizedSlugWithId = ensureHtmlSuffix(slugWithId);
  const nextPath = isEditRoute
    ? `/mo-phong-khoa-hoc/${normalizedSlugWithId}/edit`
    : `/mo-phong-khoa-hoc/${normalizedSlugWithId}`;

  return <Navigate to={`${nextPath}${location.search}`} replace />;
};

/** Helper: Tạo slug từ chuỗi tiếng Việt */
const createSlug = (str: string) => {
  if (!str) return '';
  return str.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Bỏ dấu
    .replace(/[đĐ]/g, 'd')
    .replace(/([^0-9a-z-\s])/g, '') // Bỏ ký tự đặc biệt
    .replace(/(\s+)/g, '-') // Thay khoảng trắng bằng -
    .replace(/-+/g, '-') // Bỏ - thừa
    .replace(/^-+|-+$/g, ''); // Bỏ - đầu cuối
};

const hasTemporaryClientId = (id: string | number | null | undefined): boolean => {
  if (id === null || id === undefined) return true;
  const idStr = String(id);
  return (
    idStr.startsWith('id_') ||
    idStr.startsWith('temp_') ||
    idStr.startsWith('draft_') ||
    idStr.startsWith('sample_') ||
    idStr.startsWith('vproj_') ||
    idStr.startsWith('notif_') ||
    idStr.startsWith('mod-')
  );
};

const normalizeAssetIdentity = (value?: string | null): string => {
  if (!value) return '';
  return String(value)
    .trim()
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/^\/+/, '')
    .split('?')[0];
};

const App: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // Deep-link login: khi ở trang đăng nhập kèm ?returnUrl=…, ghi nhớ URL đích vào sessionStorage
  // để sống sót qua social-login round-trip / refresh, trước khi handleLogin tiêu thụ.
  useEffect(() => {
    if (location.pathname === '/dang-nhap') {
      const rp = new URLSearchParams(location.search).get('returnUrl');
      if (rp) { try { saveReturnUrl(decodeURIComponent(rp)); } catch { /* noop */ } }
    }
  }, [location.pathname, location.search]);

  // === Idle-prefetch các route lazy hay được click từ Header/Footer ===
  // Sau khi browser idle (không impact LCP/TBT), pre-tải chunk JS để khi user click vào
  // các trang Cài đặt / Cửa hàng credit / Hướng dẫn / … chunk đã sẵn ở cache → không
  // còn nháy "Đang tải trang...". Skip nếu trình duyệt đang Save-Data hoặc mạng 2G/3G.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const conn = (navigator as any).connection;
    if (conn?.saveData) return;
    if (conn?.effectiveType && /^([23])g$/.test(conn.effectiveType)) return;

    const runIdle = (cb: () => void) => {
      const ric = (window as any).requestIdleCallback as undefined | ((fn: () => void, opts?: any) => number);
      if (ric) ric(cb, { timeout: 3000 });
      else window.setTimeout(cb, 1500);
    };

    runIdle(() => {
      // Vite dedupes các dynamic import có cùng path → cùng chunk với lazy() đã khai báo,
      // nên gọi lại ở đây chỉ "warm cache", không tạo bundle mới.
      void import('./components/Settings');
      void import('./components/CreditStore');
      void import('./components/UserGuide');
      void import('./components/TermsOfUse');
      void import('./components/PrivacyPolicy');
      void import('./components/AffiliateDashboard');
      void import('./components/TransactionHistory');
      void import('./components/PremiumBenefits');
    });
  }, []);

  // 1. CÁC STATE CỐT LÕI (Thứ tự cực kỳ quan trọng)
  const [toasts, setToasts] = useState<{ id: string; message: string; type: 'success' | 'error' | 'info' }[]>([]);
  const addToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'success') => {
    const id = `toast_${Date.now()}_${Math.random()}`;
    setToasts(prev => [...prev, { id, message, type }]);
  }, []);
  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Tài khoản GV bulk-import kèm mật khẩu tạm (Phase 5.4) — chặn thao tác cho
  // tới khi đổi mật khẩu. Cờ do Login.tsx set dựa trên requiresPasswordChange.
  const [requiresPasswordChange, setRequiresPasswordChange] = useState<boolean>(() => {
    try { return localStorage.getItem('EDUAPP_REQUIRES_PW_CHANGE') === 'true'; } catch { return false; }
  });

  const [user, setUser] = useState<UserSettings | null>(() => {
    const userDataJson = localStorage.getItem('userData');
    const authJson = localStorage.getItem('EDUAPP_AUTH');
    if (!userDataJson) return null;
    if (authJson) {
      try {
        const auth = JSON.parse(authJson);
        if (auth?.accessToken) return JSON.parse(userDataJson);
      } catch {}
    }
    localStorage.removeItem('userData');
    return null;
  });

  const [activeRole, setActiveRole] = useState<'parent' | null>(() => {
    return localStorage.getItem('EDUAPP_ACTIVE_ROLE') === 'parent' ? 'parent' : null;
  });

  const displayUser = useMemo(() => {
    if (!user) return null;
    if (user.role !== 'teacher') return user;
    if (activeRole === 'parent') {
      return { ...user, role: 'parent' };
    }
    return user;
  }, [user, activeRole]);

  // --- THIẾT LẬP ROLE TẬP TRUNG (SESSION-BASED ROLE CONFIG) ---
  const activeRoleConfig = useMemo(() => {
    const role = displayUser?.role || 'guest';
    const accountRole = user?.role || 'guest';
    const isTeacher = role === 'teacher';
    const isParent = role === 'parent';
    const isStudent = role === 'student';
    const isGuest = !user;
    const canSwitchRole = accountRole === 'teacher';
    const isRoleSwitched = canSwitchRole && role !== accountRole;
    
    // Dashboard mặc định & URL gốc
    // Deep Workspace là điểm "về nhà" mới cho người dùng đã đăng nhập (thay landing cũ).
    // Các dashboard cũ (TeacherTools/ParentDashboard/StudentDashboard) vẫn truy cập được
    // qua nav trong workspace + URL trực tiếp — không xóa chức năng nào.
    const dashboardView = isTeacher ? View.TeacherTools : isParent ? View.ParentDashboard : isStudent ? View.StudentDashboard : View.Dashboard;
    const workspacePath = isTeacher
        ? '/workspace/giao-vien/tong-quan'
        : isParent
            ? '/workspace/phu-huynh'
            : isStudent
                ? '/workspace/hoc-sinh'
                : '/';
    const dashboardPath = isGuest ? '/' : workspacePath;
    
    // Text hiển thị cho dashboard
    const dashboardLabel = isStudent ? 'Lộ trình của tôi' : 'Bảng điều khiển';

    // Cấu hình Menu & Tính năng (Centralized Feature Control)
    const features = {
        showTeacherTools: isTeacher,
        showParentTools: isParent,
        showStudentTools: isStudent,
        showCreative: isTeacher || (isParent && user?.authType !== 'student_code') || (isStudent && user?.authType !== 'student_code'),
        showAssistant: true,
        showAITools: true,
        showEntertainment: isTeacher || isParent,
        homeworkLabel: getHomeworkFeatureLabel(role),
    };

    return {
        role,
        accountRole,
        isTeacher,
        isParent,
        isStudent,
        isGuest,
        canSwitchRole,
        isRoleSwitched,
        dashboardView,
        dashboardPath,
        workspacePath,
        dashboardLabel,
        features,
    };
  }, [displayUser, user?.authType]);

  const currentView = useMemo(() => {
    const path = location.pathname;
    // Match concrete prefix paths first; bo qua cac entry co basePath rong (vi du
    // `/:typePrefix/:slugWithId`) vi chung match moi path (gay sai breadcrumb).
    const entry = Object.entries(ViewPath)
      .filter(([_, vPath]) => {
        const base = vPath.split('/:')[0];
        return base.length > 1 || vPath === '/';
      })
      .sort((a, b) => b[1].split('/:')[0].length - a[1].split('/:')[0].length)
      .find(([_, vPath]) => {
        if (vPath === '/') return path === '/';
        const basePath = vPath.split('/:')[0];
        return path.startsWith(basePath);
      });
    if (entry) {
      return entry[0] as View;
    }
    // Cac route assignment khong co prefix co dinh trong ViewPath - check tay.
    const ASSIGNMENT_PREFIXES = ['/thong-bao/', '/bai-tap/', '/nhiem-vu/'];
    if (ASSIGNMENT_PREFIXES.some((prefix) => path.startsWith(prefix))) {
      const segmentCount = path.split('/').filter(Boolean).length;
      return (segmentCount >= 3 ? View.AssignmentDetail : View.StudentAssignmentDetail) as View;
    }
    return View.Dashboard;
  }, [location.pathname]);

  const showDisplayAds = useMemo(
    () => canShowDisplayAds(location.pathname, displayUser),
    [location.pathname, displayUser],
  );

  // SEO: cap nhat title/meta/og:image/JSON-LD moi khi route thay doi.
  // Cac component creator van co the goi updatePageSEO sau (vd them ten hoc lieu)
  // - se override SEO global mac dinh nay.
  // Route /huong-dan (hub + chi tiet tung chuc nang) tu quan SEO rieng qua GuideHub/GuideDetail
  // → bo qua o day de meta dong khong bi ghi de.
  useEffect(() => {
    if (location.pathname === '/huong-dan' || location.pathname.startsWith('/huong-dan/')) return;
    updatePageSEO(location.pathname);
  }, [location.pathname]);

  // Các state giao diện và hệ thống
  const [isOutOfCreditsModalOpen, setIsOutOfCreditsModalOpen] = useState(false);
  // resetAt/reason chỉ có giá trị khi backend chạy pricing_enforcement_mode='plan' (xem apiService.ts)
  const [outOfCreditsInfo, setOutOfCreditsInfo] = useState<{ reason?: 'window' | 'cycle'; resetAt?: string }>({});
  const [paymentModalPackage, setPaymentModalPackage] = useState<CreditPackage | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [lastActivity, setLastActivity] = useState<Activity | null>(null);
  const [adsConfig, setAdsConfig] = useState<any | null>(null);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [guestAnnouncements, setGuestAnnouncements] = useState<Notification[]>([]);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [headerConfig, setHeaderConfig] = useState<{ showAppName: boolean; appName: string }>({ showAppName: true, appName: UI_CONFIG.APP_NAME || 'Bookai' });
  const isInitialLoading = useRef(true);
  const [postLoginRedirect, setPostLoginRedirect] = useState<HistoryEntry | null>(null);

  // Capture mã giới thiệu (?ref=...) trên mọi route ngay khi URL thay đổi.
  // Nhờ đó link chia sẻ không bị mất nếu người dùng đi vào homepage hay
  // bất kỳ trang nào khác trước khi quay lại đăng nhập.
  useEffect(() => {
    try { captureReferralFromUrl(); } catch {}
  }, [location.pathname, location.search]);

  useEffect(() => {
    let isMounted = true;

    try { captureReferralFromUrl(); } catch {}

    const initializeRuntimeConfig = async () => {
      try {
        await configService.initialize();
      } finally {
        if (!isMounted) return;
        setHeaderConfig({
          showAppName: UI_CONFIG.SHOW_APP_NAME_IN_HEADER !== false,
          appName: UI_CONFIG.APP_NAME || 'Bookai',
        });
        setConfigLoaded(true);
      }
    };

    initializeRuntimeConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  // --- CÁC HÀM XỬ LÝ ĐIỀU HƯỚNG ---
  const handleNavigate = useCallback((view: View | string, payload: any = null) => {
      if (typeof view === 'string' && view.startsWith('/')) {
        navigate(view, { state: payload != null ? payload : null });
        return;
      }
      const template = ViewPath[view as View] || '/';
      let path = template;
      const queryParams: string[] = [];
      
      if (APP_MODE !== 'PRODUCTION') {
          console.log(`[Navigation] Target View: ${view}`, payload);
      }

      let id = null;
      let title = 'soan-thao';
      if (payload) {
          // Chỉ lấy ID của chính tài nguyên đó, không lấy sourceId làm ID chính cho slug nếu là màn tạo mới
          const isCreatorView = [
              View.LessonPlanCreator, View.QuizCreator, View.GameCreator, View.SlideCreator, 
              View.HomeworkCreator, View.ActivityCreator, View.VideoCreator,
              View.ScientificSimulationCreator, View.ThietKe3DCreator, View.OralQuizCreator
          ].includes(view as View);

          id = payload.id || payload.editItemId || payload.gameId || payload.quizId || payload.slideId || 
               payload.videoProjectId || (payload.data && (payload.data.id || payload.data._id || payload.data.gameId)) || 
               (payload.item && (payload.item.id || payload.item._id));
          
          // Nếu không có ID chính và không phải màn tạo mới, mới lấy sourceId làm fallback cho slug
          if (!id && !isCreatorView) {
              id = payload.sourceId || (typeof payload === 'string' || typeof payload === 'number' ? payload : null);
          }

          title = payload.title || payload.topic || payload.name || 
                  (payload.data && (payload.data.title || payload.data.topic || payload.data.name)) || 
                  (payload.item && (payload.item.title || payload.item.topic || payload.item.name)) || 
                  (payload.attempt && payload.attempt.snapshotMeta && payload.attempt.snapshotMeta.title) ||
                  'soan-thao';
      }

      if (view === View.ChildDashboard) {
          const cid = payload?.childId || (payload?.child && (payload.child.id || payload.child.childId));
          const slug = createSlug(payload?.child?.name || payload?.name || 'con');
          const typePrefix = payload?.isWorkingMode ? 'c' : (displayUser?.role === 'parent' ? 'p' : 'c');
          path = cid ? `/bang-hoc-cua-con/${slug}-${typePrefix}${cid}` : '/workspace/phu-huynh';
      } else if (view === View.ClassManagement) {
          const cid = payload?.classId || payload?.id || id;
          path = cid ? `/quan-ly-lop-hoc/${createSlug(title)}-i${cid}` : '/quan-ly-lop-hoc';
      } else if (view === View.LearningModuleView) {
          const pid = payload?.planId || payload?.plan?.id;
          const mid = payload?.moduleId || payload?.module?.id;
          if (pid && mid) {
            const pSlug = createSlug(payload.plan?.title || payload.title || 'lo-trinh');
            const mSlug = createSlug(payload.module?.title || payload.moduleTitle || 'bai-hoc');
            path = `/bai-hoc/${pSlug}-i${pid}/${mSlug}-m${mid}`;
          } else path = displayUser?.role === 'student' ? '/workspace/hoc-sinh/lo-trinh' : '/workspace/phu-huynh';
      } else if (
          displayUser?.role === 'student' &&
          (view === View.QuizCreator || view === View.OnlineQuizTaker || view === View.QuizAttemptResult)
      ) {
          // Học sinh: giữ URL luyện đề trong đúng "họ" /workspace/hoc-sinh/luyen-de/*
          // thay vì các path legacy dùng chung với giáo viên/phụ huynh
          // (/tao-de-kiem-tra, /lam-de, /ket-qua-de). Layout hiển thị (CreatorRouteWrapper)
          // không đổi — chỉ đổi URL cho nhất quán với phần còn lại của workspace.
          const base = view === View.QuizCreator
              ? '/workspace/hoc-sinh/luyen-de/tao-de'
              : view === View.OnlineQuizTaker
                  ? '/workspace/hoc-sinh/luyen-de/lam-bai'
                  : '/workspace/hoc-sinh/luyen-de/ket-qua';
          path = id ? `${base}/${createSlug(title)}-i${id}` : base;
      } else if ((view === View.AssignmentDetail || view === View.StudentAssignmentDetail) && payload?.assignmentId) {
          const slug = createSlug(title);
          let typePrefix = user?.authType === 'student_code' ? 's' : (displayUser?.role === 'parent' ? 'p' : (displayUser?.role === 'student' ? 's' : 'i'));
          if (payload.isWorkingMode) typePrefix = 'c';
          const aType = payload.assignment?.type || payload.type || 'homework';
          const urlType = aType === 'announcement' ? 'thong-bao' : (aType === 'homework' ? 'bai-tap' : 'nhiem-vu');
          path = payload.classCode ? `/${urlType}/lop-${payload.classCode.toLowerCase()}/${slug}-${typePrefix}${payload.assignmentId}` : `/${urlType}/${slug}-${typePrefix}${payload.assignmentId}`;
      } else if (template.includes('/:')) {
          if (id) path = template.replace(/:[a-zA-Z0-9_]+/, `${createSlug(title)}-i${id}`);
          else if (payload?.sourceId) path = template.split('/:')[0] + `?sourceId=${payload.sourceId}`;
          else path = template.split('/:')[0];
      }

                  if (path.includes('/:')) {
                      const isOptional = path.includes('?/') || path.endsWith('?');
                      if (!isOptional) {
                          if (APP_MODE !== 'PRODUCTION') addToast(`Lỗi: Không tìm thấy ID cho ${view}`, 'error');
                          path = activeRoleConfig.dashboardPath;
                      } else path = path.split('/:')[0];
                  }

      if (payload && payload.buoc) queryParams.push(`buoc=${encodeURIComponent(String(payload.buoc))}`);
      if (payload && payload.step !== undefined) queryParams.push(`step=${payload.step}`);
      
      // Sẽ được set nếu cần dùng "carrier" (snapshot tài nguyên qua localStorage)
      let carrierKey: string | null = null;

      if (payload) {
          const pTopic = payload.topic || payload.initialTopic || payload.title || (payload.sourceLessonPlan && payload.sourceLessonPlan.title) || (payload.lessonPlan && payload.lessonPlan.title);
          const pInstruction = payload.detailedInstruction || payload.specialTask || payload.description || (payload.payload && payload.payload.detailedInstruction);

          /**
           * Cải tiến lớn: tự động sinh `initialContent` dạng MARKDOWN từ
           * `sourceLessonPlan` nếu payload không có `initialContent` riêng.
           * Markdown giúp AI ở trang đích bám sát mục tiêu, hoạt động trọng
           * tâm và sản phẩm dự kiến — thay vì JSON.stringify khó hiểu.
           */
          let pContent = payload.initialContent || payload.contextContent || payload.content || (payload.payload && payload.payload.initialContent);
          // Bảo vệ: nếu pContent là object/array (vd: AI/lessonPlan truyền object sai chỗ),
          // chuyển sang JSON string để tránh tạo URL chứa "[object Object]".
          if (pContent && typeof pContent !== 'string') {
              try { pContent = JSON.stringify(pContent); } catch { pContent = String(pContent); }
          }
          if (!pContent && payload.sourceLessonPlan && typeof payload.sourceLessonPlan === 'object') {
              try {
                  const focusIdx = typeof payload.focusStepIndex === 'number' ? payload.focusStepIndex : undefined;
                  pContent = focusIdx !== undefined
                      ? lessonPlanFocusedMarkdown(payload.sourceLessonPlan, focusIdx)
                      : lessonPlanToMarkdown(payload.sourceLessonPlan);
              } catch (e) {
                  if (APP_MODE !== 'PRODUCTION') console.warn('[Navigation] Lỗi serialize sourceLessonPlan ra markdown:', e);
              }
          }

          const pSourceId = payload.sourceId || payload.id || (payload.sourceLessonPlan && payload.sourceLessonPlan.id);
          const pSourceTitle = (payload.sourceLessonPlan && payload.sourceLessonPlan.title) || payload.sourceTitle;

          // FIX: Tránh thêm topic/sourceId vào query string khi đang xem/sửa một tài nguyên đã tồn tại (đã có ID chính trên URL)
          // Điều này giúp tránh xung đột trong SlideCreator (ưu tiên edit mode thay vì create-from-source mode)
          const isExistingResourceView = view.toString().endsWith('View') || view.toString().endsWith('Edit');
          const hasRealId = id && !String(id).startsWith('id_');

          /**
           * Quyết định "carrier" vs "query string thuần":
           * - Khi mở tab MỚI (window.open) — `location.state` mất → bắt buộc
           *   dùng carrier nếu có nội dung dài (markdown) hoặc snapshot
           *   `sourceLessonPlan` (object không thể đặt vào URL).
           * - Khi nội dung ngắn (vd chỉ topic + instruction), vẫn dùng query
           *   string để giữ tương thích với deep-link / chia sẻ URL.
           */
          const needsCarrier = !!payload?.openInNewTab && (
              (typeof pContent === 'string' && pContent.length > 1500) ||
              !!payload.sourceLessonPlan
          );

          if (needsCarrier && !(isExistingResourceView && hasRealId)) {
              // Lọc bỏ các object lớn khỏi featurePayload để tránh lặp dữ liệu và quá tải carrier
              const { sourceLessonPlan: _unused, ...safeFeaturePayload } = payload;

              carrierKey = writeResourceCarrier({
                  initialTopic: pTopic || undefined,
                  initialContent: pContent || undefined,
                  detailedInstruction: pInstruction || undefined,
                  sourceId: pSourceId ? String(pSourceId) : undefined,
                  sourceTitle: pSourceTitle || undefined,
                  focusStepIndex: typeof payload.focusStepIndex === 'number' ? payload.focusStepIndex : undefined,
                  sourceSnapshot: payload.sourceLessonPlan || undefined,
                  sourceKind: payload.sourceLessonPlan ? 'lesson_plan' : undefined,
                  featurePayload: safeFeaturePayload,
              });
              if (carrierKey) {
                  queryParams.push(`carrier=${encodeURIComponent(carrierKey)}`);
                  // Vẫn truyền topic ngắn để URL có ngữ nghĩa (tăng SEO/UX)
                  if (pTopic && !path.includes('topic=')) queryParams.push(`topic=${encodeURIComponent(pTopic)}`);
                  if (pSourceId && !path.includes('sourceId=')) queryParams.push(`sourceId=${pSourceId}`);
              }
          } else {
              if (pTopic && !path.includes('topic=') && !(isExistingResourceView && hasRealId)) queryParams.push(`topic=${encodeURIComponent(pTopic)}`);
              if (pInstruction && !path.includes('detailedInstruction=') && !(isExistingResourceView && hasRealId)) {
                  queryParams.push(`detailedInstruction=${encodeURIComponent(pInstruction)}`);
              }
              // Không gắn content/initialContent khi mở tài nguyên đã lưu (payload spread {...game} từng làm URL phình to)
              if (pContent && !path.includes('initialContent=') && !(isExistingResourceView && hasRealId)) {
                  const safeContent = pContent.length > 6000 ? pContent.slice(0, 6000) : pContent;
                  queryParams.push(`initialContent=${encodeURIComponent(safeContent)}`);
              }
              if (pSourceId && !path.includes('sourceId=') && !(isExistingResourceView && hasRealId)) queryParams.push(`sourceId=${pSourceId}`);
          }
      }

      if (queryParams.length > 0) {
          const queryString = queryParams.join('&');
          path += (path.includes('?') ? (path.endsWith('?') ? '' : '&') : '?') + queryString;
      }

      if (view === View.Login && payload?.redirect) setPostLoginRedirect(payload.redirect);
      if (view === View.Settings && payload?.hash) path += '#' + String(payload.hash).replace(/^#/, '');

      if (payload?.openInNewTab) {
          window.open(path, '_blank');
      } else {
          navigate(path, { state: payload != null ? payload : null });
      }
  }, [navigate, user, displayUser, addToast]);

  const handleSwitchRole = useCallback((targetRole: 'teacher' | 'parent') => {
    if (!user || user.role !== 'teacher') return;

    const nextActiveRole = targetRole === 'parent' ? 'parent' : null;
    setActiveRole(nextActiveRole);

    if (nextActiveRole) {
      localStorage.setItem('EDUAPP_ACTIVE_ROLE', nextActiveRole);
    } else {
      localStorage.removeItem('EDUAPP_ACTIVE_ROLE');
    }

    addToast(`Đã chuyển sang chế độ ${targetRole === 'teacher' ? 'Giáo viên' : 'Phụ huynh'}`, 'info');
    handleNavigate(targetRole === 'teacher' ? View.TeacherTools : View.ParentDashboard);
  }, [user, addToast, handleNavigate]);

  /**
   * Wrapper render khi teacher truy cap thang vao route Phu huynh (vd tu CareerJourney
   * bam "Them con moi"). Tu dong activate parent role va re-render route -
   * tranh redirect ve "/" gay confused UX.
   *
   * Co the show toast "Da chuyen sang phu huynh" 1 lan duy nhat de user nhan biet.
   */
  const ParentRouteAutoSwitch: React.FC = () => {
    useEffect(() => {
      if (user?.role === 'teacher' && activeRole !== 'parent') {
        setActiveRole('parent');
        try { localStorage.setItem('EDUAPP_ACTIVE_ROLE', 'parent'); } catch {}
      }
      // Khong goi handleSwitchRole(): tranh navigate lap (chinh route nay da la dich)
      // va tranh toast lap khi user chu dong navigate.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
        <div className="w-12 h-12 rounded-full border-4 border-violet-200 border-t-violet-500 animate-spin mb-4" />
        <p className="text-slate-600 font-bold">Đang chuyển sang chế độ phụ huynh...</p>
        <p className="text-slate-400 text-sm mt-1">Tài khoản giáo viên đang được kích hoạt vai trò phụ huynh.</p>
      </div>
    );
  };

  const userId = user?.email || null;
  const navigationPayload = location.state || {};

  const [documents, setDocuments] = useState<Document[]>([]);
  const [activeUploads, setActiveUploads] = useState<UploadTask[]>([]);
  const [lessonPlans, setLessonPlans] = useState<LessonPlan[]>([]);
  const [drawingLessons, setDrawingLessons] = useState<DrawingLesson[]>([]);
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [oralQuizzes, setOralQuizzes] = useState<OralQuiz[]>([]);
  const [oralQuizSessions, setOralQuizSessions] = useState<OralQuizSession[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [presentations, setPresentations] = useState<Presentation[]>([]);
  const [scientificSimulations, setScientificSimulations] = useState<ScientificSimulation[]>([]);
  const [homeworks, setHomeworks] = useState<Homework[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [images, setImages] = useState<ImageResource[]>([]);
  const [videoResources, setVideoResources] = useState<VideoResource[]>([]);
  const [videoProjects, setVideoProjects] = useState<VideoProject[]>([]);
  const [interactiveVideos, setInteractiveVideos] = useState<InteractiveVideo[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [children, setChildren] = useState<Child[]>([]);
  const [joinRequests, setJoinRequests] = useState<any[]>([]);
  const [childLearningPlans, setChildLearningPlans] = useState<ChildLearningPlan[]>([]);
  const [learningHistory, setLearningHistory] = useState<LearningHistory[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [studentStats, setStudentStats] = useState<any[]>([]);
  const [skknProjects, setSkknProjects] = useState<any[]>([]);

  // Học sinh (đăng nhập email): lộ trình ĐỒNG NHẤT = của chính mình + phụ huynh
  // tạo cho con đã liên kết. Giữ ở state RIÊNG để không bị luồng nạp thường ghi
  // đè. Mã học sinh (student_code) không gọi được endpoint auth:api → để rỗng.
  const [studentUnifiedPlans, setStudentUnifiedPlans] = useState<ChildLearningPlan[]>([]);
  const refreshUnifiedPlans = useCallback(() => {
    if (user?.role !== 'student' || (user as any)?.authType === 'student_code') { setStudentUnifiedPlans([]); return; }
    getUnifiedLearningPlans()
      .then(plans => { if (Array.isArray(plans)) setStudentUnifiedPlans(plans); })
      .catch(() => {});
  }, [user?.role, user?.id, (user as any)?.authType]);

  useEffect(() => { refreshUnifiedPlans(); }, [refreshUnifiedPlans]);

  // Expose a way to reset to mock data globally for the demo
  useEffect(() => {
    (window as any).resetToMockData = () => {
        setClasses(mockClasses);
        setStudents(mockStudents);
        setChildren(mockChildren);
        setDocuments(mockDocuments);
        setLessonPlans(mockLessonPlans);
        setDrawingLessons([]);
        setQuizzes(mockQuizzes);
        setGames(mockGames);
        setPresentations(mockPresentations);
        setHomeworks(mockHomeworks);
        setActivities(mockActivities);
        alert('Dữ liệu mẫu đã được nạp thành công!');
    };
  }, [setClasses, setStudents, setDocuments, setLessonPlans, setQuizzes, setGames, setPresentations, setHomeworks, setActivities]);

  // Load Ads Config once. AdSense script is loaded later only on approved public routes.
  const isFetchingAds = useRef(false);
  const adsenseScriptLoaded = useRef(false);
  useEffect(() => {
    if (isFetchingAds.current) return;
    isFetchingAds.current = true;
    (window as any).API_EDU_PREFIX = API_EDU_PREFIX;
    getAdsConfig().then(cfg => {
      setAdsConfig(cfg);
      (window as any).__EDUAPP_ADS__ = cfg;
    }).catch(() => setAdsConfig(null));
  }, []);

  useEffect(() => {
    if (adsenseScriptLoaded.current || !adsConfig) return;
    const slots = adsConfig?.slots || {};
    const adsenseSlot = Object.entries(slots).find(([slotId, s]: [string, any]) => (
      s?.type === 'adsense'
      && s?.enabled !== false
      && canShowAdSlot(location.pathname, slotId, displayUser)
    ))?.[1] as any;
    if (!adsenseSlot) return;

    const testMode = !!(adsConfig?.test_mode);
    const raw = (adsenseSlot?.client || '').trim();
    const client = testMode ? 'ca-pub-3940256099942544' : (raw ? normalizeAdSenseClientId(raw) : null);
    if (!client) return;

    adsenseScriptLoaded.current = true;
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`;
    script.crossOrigin = 'anonymous';
    document.head.appendChild(script);
  }, [adsConfig, location.pathname, displayUser]);

  // Load banners: cả khi đã đăng nhập và chưa đăng nhập (PRODUCTION)
  useEffect(() => {
    if (APP_MODE !== 'PRODUCTION') {
      setBanners([]);
      return;
    }
    getBanners().then(setBanners).catch(() => setBanners([]));
  }, [user?.id]);

  // Load thông báo cho khách (chưa đăng nhập)
  useEffect(() => {
    if (APP_MODE !== 'PRODUCTION' || user) {
      setGuestAnnouncements([]);
      return;
    }
    getAnnouncements().then((arr) => {
      setGuestAnnouncements(arr.map((a: any) => ({
        id: a.id,
        type: (a.type || 'info') as Notification['type'],
        message: a.message || a.title,
        timestamp: a.timestamp || new Date().toISOString(),
        isRead: false,
        link: a.link || undefined,
        icon: (a.icon || 'info') as Notification['icon'],
      })));
    }).catch(() => setGuestAnnouncements([]));
  }, [user]);

  const dataLoadedFor = useRef<string | null>(null);
  const lastLibraryLoadTime = useRef<number>(0);
  const loadedResources = useRef<Set<string>>(new Set());
  const isFetchingData = useRef(false);

  const lastActivityRef = useRef<number>(Date.now());
  const INACTIVITY_TIMEOUT = 4 * 60 * 60 * 1000; // 4 hours in ms
  const LAST_ACTIVITY_KEY = 'eduapp_last_activity';

  // 1. Logout: central point to clear everything
  const handleLogout = useCallback(() => {
    // 1. Xóa token và auth ngay lập tức (trước khi navigate) để tránh request dùng token cũ
    setEduappAccessToken(null, null);
    localStorage.removeItem('userData');
    localStorage.removeItem('EDUAPP_ACTIVE_ROLE');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    // 2. Reset trạng thái load để không hiển thị "Đang thiết lập người dùng"
    setIsLoadingData(false);
    dataLoadedFor.current = null;
    loadedResources.current.clear();
    isFetchingData.current = false;
    // 3. Xóa user và điều hướng
    setUser(null);
    navigate('/dang-nhap');
  }, [navigate]);

  // Monitor inactivity and auth events
  useEffect(() => {
    if (!user) return;

    const onAuthFailure = (e: any) => {
        addToast("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.", 'info');
        handleLogout();
    };

    const updateActivity = () => {
        const now = Date.now();
        lastActivityRef.current = now;
        // Share activity timestamp across tabs
        localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    };

    const checkInactivity = () => {
        const now = Date.now();
        // Check local storage first (to see if other tabs are active)
        const lastGlobalActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
        const effectiveLastActivity = Math.max(lastActivityRef.current, lastGlobalActivity);
        
        if (now - effectiveLastActivity > INACTIVITY_TIMEOUT) {
            addToast("Đã tự động đăng xuất do quá lâu không hoạt động để bảo mật.", 'info');
            handleLogout();
        }
    };

    // Listen for logout events or activity updates from other tabs
    const onStorageChange = (e: StorageEvent) => {
        if (e.key === 'EDUAPP_AUTH' && !e.newValue) {
            handleLogout();
        }
    };

    // Mở OutOfCreditsModal khi bất kỳ API nào trả về HTTP 402 (hết credits).
    const onOutOfCredits = (e: Event) => {
        const detail = (e as CustomEvent).detail as { reason?: 'window' | 'cycle'; resetAt?: string } | undefined;
        setOutOfCreditsInfo({ reason: detail?.reason, resetAt: detail?.resetAt });
        setIsOutOfCreditsModalOpen(true);
    };

    window.addEventListener('eduapp-logout', onAuthFailure);
    window.addEventListener('eduapp-out-of-credits', onOutOfCredits);
    window.addEventListener('storage', onStorageChange);
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('mousedown', updateActivity);
    window.addEventListener('touchstart', updateActivity);

    // Initial activity set
    updateActivity();

    const interval = setInterval(checkInactivity, 60000); // Check every minute

    return () => {
        window.removeEventListener('eduapp-logout', onAuthFailure);
        window.removeEventListener('eduapp-out-of-credits', onOutOfCredits);
        window.removeEventListener('storage', onStorageChange);
        window.removeEventListener('mousemove', updateActivity);
        window.removeEventListener('keydown', updateActivity);
        window.removeEventListener('mousedown', updateActivity);
        window.removeEventListener('touchstart', updateActivity);
        clearInterval(interval);
    };
  }, [user, handleLogout, addToast]);

  const [resourceLoadingStates, setResourceLoadingStates] = useState<Record<string, boolean>>({});

  // 2.1 API theo lớp: Chỉ tải dữ liệu của 1 lớp sau approve/reject (tối ưu)
  const loadClassUpdates = useCallback(async (classId: string | number, removeRequestId?: string | number) => {
    if (APP_MODE !== 'PRODUCTION') return;
    try {
      const data = await getClassUpdates(classId);
      if (!data) return;
      React.startTransition(() => {
        setJoinRequests(prev => {
            const filteredPrev = (prev || []).filter((r: any) => String(r.class_id) !== String(classId));
            const newRequests = (data.joinRequests || [])
                .map((r: any) => ({ ...r, id: String(r.id) }))
                .filter((r: any) => removeRequestId ? String(r.id) !== String(removeRequestId) : true);
            return [...filteredPrev, ...newRequests];
        });
        setStudents(prev => {
          const byId = new Map<string, any>(prev.map(s => [String(s.id), { ...s, id: String(s.id) }]));
          (data.students || []).forEach((s: any) => {
            const mapped = {
              ...s,
              id: String(s.id),
              studentId: s.student_id_code || s.studentId || '',
              realName: s.real_name || s.realName || '',
              dob: s.dob || s.date_of_birth || s.dateOfBirth || '',
              loginCode: s.login_code || s.loginCode,
              linked_to_parent: s.linked_to_parent
            };
            byId.set(String(s.id), mapped);
          });
          return Array.from(byId.values());
        });
        if (data.class) {
          setClasses(prev => prev.map(c => String(c.id) === String(classId) ? { ...c, studentIds: data.class?.studentIds || c.studentIds } : c));
        }
      });
    } catch (e) { console.error('loadClassUpdates:', e); }
  }, []);

  // Fetch data from backend on login in PRODUCTION mode
  // resources: 'classes,students,documents,lessonPlans,quizzes,games,presentations,homeworks,activities,images,videoResources,videoProjects,children,joinRequests,notifications,submissions,skknProjects'
  const loadProductionData = useCallback(async (resources: string = '', force = false, silent = false) => {
    if (APP_MODE === 'PRODUCTION' && user?.email) {
        const resourceKey = resources || 'all';
        
        // Robust guards
        if (isFetchingData.current && !force && resourceKey === 'all') return;
        if (!force && dataLoadedFor.current === user.email && loadedResources.current.has(resourceKey)) return;
        
        if (resourceKey === 'all') isFetchingData.current = true;
        setResourceLoadingStates(prev => ({ ...prev, [resourceKey]: true }));
        
        // 1. Try to load from localStorage first for immediate UI update
        // Khi load partial (vd: joinRequests), CHỈ cập nhật resource đó - KHÔNG ghi đè classes/students
        // để tránh mất dữ liệu vừa tải từ core,teacher (bug: "Chưa có lớp học" sau khi data đã hiện)
        const localData = loadUserDataFromLocal(user.email);
        let canSkipFetch = false;
        const loadOnlyJoinRequests = resources === 'joinRequests';

        if (localData) {
            React.startTransition(() => {
                if (loadOnlyJoinRequests) {
                    if (localData.joinRequests) setJoinRequests(localData.joinRequests.map((i: any) => ({ ...i, id: String(i.id) })));
                } else {
                if (localData.documents) setDocuments(localData.documents.map((i: any) => ({ ...i, id: String(i.id) })));
                if (localData.lessonPlans) setLessonPlans(localData.lessonPlans.map((i: any) => ({ ...i, id: String(i.id) })));
                if (localData.drawingLessons) setDrawingLessons(localData.drawingLessons.map((i: any) => ({ ...i, id: String(i.id), savedDate: i.savedDate || i.saved_date })));
                if (localData.skknProjects || localData.skkn_projects) setSkknProjects((localData.skknProjects || localData.skkn_projects).map((i: any) => ({ ...i, id: String(i.id) })));
                if (localData.quizzes) setQuizzes(localData.quizzes.map((i: any) => ({ ...i, id: String(i.id) })));
                if (localData.games) {
                    setGames(localData.games.map((i: any) => normalizeGameRecord(i)).filter(Boolean) as Game[]);
                }
                if (localData.presentations) setPresentations(localData.presentations.map((i: any) => ({ ...i, id: String(i.id) })));
                if (localData.homeworks) setHomeworks(localData.homeworks.map((i: any) => ({ ...i, id: String(i.id) })));
                if (localData.activities) setActivities(localData.activities.map((i: any) => ({ ...i, id: String(i.id) })));
                if (localData.oralQuizzes) setOralQuizzes(localData.oralQuizzes.map((i: any) => ({ ...i, id: String(i.id) })));
                if (localData.oralQuizSessions) setOralQuizSessions(localData.oralQuizSessions.map((i: any) => ({ ...i, id: String(i.id) })));
                if (localData.images) setImages(localData.images.map((i: any) => ({ ...i, id: String(i.id) })));
                if (localData.videoResources) setVideoResources(localData.videoResources.map((i: any) => ({ ...i, id: String(i.id) })));
                if (localData.videoProjects) setVideoProjects(localData.videoProjects.map((i: any) => ({ ...i, id: String(i.id) })));
                if (localData.classes) {
                    setClasses(localData.classes.map((c: any) => ({
                        ...c,
                        id: String(c.id),
                        studentIds: (c.student_ids || c.studentIds || []).map(String)
                    })));
                }
                if (localData.students) {
                    setStudents(localData.students.map((s: any) => ({
                        ...s,
                        id: String(s.id),
                        studentId: s.student_id_code || s.studentId || s.studentIdCode || ''
                    })));
                }
                if (localData.children) setChildren(localData.children.map((i: any) => ({ 
                    ...i, 
                    id: String(i.id), 
                    studentId: String(i.student_id ?? i.studentId ?? ''),
                    avatarUrl: i.avatarUrl || i.avatar || '',
                    avatar: i.avatar || i.avatarUrl || null,
                    relationship: i.relationship || ''
                })));
                if (localData.joinRequests) setJoinRequests(localData.joinRequests.map((i: any) => ({ ...i, id: String(i.id) })));
                if (localData.notifications) setNotifications(localData.notifications.map((i: any) => ({ ...i, id: String(i.id) })));
                if (localData.submissions) {
                    setSubmissions(localData.submissions.map((i: any) => ({
                        ...i,
                        id: String(i.id),
                        assignmentId: String(i.assignmentId ?? i.assignment_id ?? ''),
                        studentId: String(i.studentId ?? i.student_id ?? '')
                    })));
                }
                if (localData.learningPlans) {
                    setChildLearningPlans(localData.learningPlans.map((p: any) => ({
                        ...p,
                        id: String(p.id),
                        dailyPlans: (p.dailyPlans || p.daily_plans || []).map((day: any, dIdx: number) => ({
                            ...day,
                            modules: (day.modules || []).map((mod: any, mIdx: number) => ({
                                ...mod,
                                id: mod.id || `mod-${p.id}-${dIdx}-${mIdx}`
                            }))
                        }))
                    })));
                }
                if (localData.learningHistory) {
                    setLearningHistory(localData.learningHistory.map((h: any) => ({ ...h, id: String(h.id) })));
                }
                }
            });

            // Nếu cache còn mới, chúng ta ưu tiên nạp ngầm (silent) thay vì hiện loading xoay xoay
            if (!force) {
                silent = true;
                
                // Nếu đã nạp tài nguyên này trong session hiện tại, có thể bỏ qua fetch
                if (loadedResources.current.has(resourceKey)) {
                    canSkipFetch = true;
                }
            }
        }

        if (canSkipFetch) {
            isFetchingData.current = false;
            return;
        }

        if (!silent) setIsLoadingData(true);
        else setIsBackgroundLoading(true);
        try {
            // 2. Fetch fresh metadata from backend (core + role-based or specific resources)
            const allData = await fetchAllUserData(resources);
            
            if (allData) {
                // Hỗ trợ cả trường hợp dữ liệu bị bọc trong một lớp 'data' nữa (do backend config)
                const actualData = (allData as any).data && !allData.userSettings ? (allData as any).data : allData;
                
                dataLoadedFor.current = user.email;
                loadedResources.current.add(resourceKey);
                
                // Nếu nạp mặc định (lúc mới login), ta cũng đánh dấu các group theo role là đã nạp
                if (!resources && actualData.userSettings) {
                    const role = (actualData.userSettings as any).role;
                    if (role) loadedResources.current.add(role);
                    loadedResources.current.add('core');
                }
                
                React.startTransition(() => {
                    if (actualData.userSettings && typeof actualData.userSettings === 'object' && !Array.isArray(actualData.userSettings) && (actualData.userSettings.role || actualData.userSettings.email)) {
                        let settings = actualData.userSettings as UserSettings;
                        
                        // Tự động bật tất cả Menu nhanh nếu cấu hình yêu cầu và user chưa có cấu hình riêng
                        if (FEATURE_FLAGS.AUTO_ENABLE_ALL_QUICK_MENU && settings.role && (!settings.quickMenuItems || settings.quickMenuItems.length === 0)) {
                            const availableItems = ALL_QUICK_MENU_ITEMS_CONFIG.filter(item => item.roles.includes(settings.role!));
                            settings = {
                                ...settings,
                                quickMenuItems: availableItems.map(item => ({ key: item.key, label: item.label, view: item.view }))
                            };
                        }

                        setUser(prev => (prev ? { ...prev, ...settings } : settings));
                        const merged = settings;
                        localStorage.setItem('userData', JSON.stringify(merged));
                    }
                    if (actualData.documents) setDocuments(actualData.documents.map((i: any) => ({ ...i, id: String(i.id), uploadDate: i.uploadDate || i.upload_date })));
                    if (actualData.lessonPlans) setLessonPlans(actualData.lessonPlans.map((i: any) => ({ ...i, id: String(i.id), savedDate: i.savedDate || i.saved_date })));
                    if (actualData.drawingLessons) setDrawingLessons(actualData.drawingLessons.map((i: any) => ({ ...i, id: String(i.id), savedDate: i.savedDate || i.saved_date })));
                    if (actualData.quizzes) setQuizzes(actualData.quizzes.map((i: any) => ({ ...i, id: String(i.id), savedDate: i.savedDate || i.saved_date })));
                    if (actualData.games) {
                        setGames(
                            actualData.games
                                .map((i: any) => normalizeGameRecord(i))
                                .filter(Boolean) as Game[]
                        );
                    }
                    if (actualData.presentations) setPresentations(actualData.presentations.map((i: any) => ({ ...i, id: String(i.id), savedDate: i.savedDate || i.saved_date })));
                    if (actualData.scientificSimulations) {
                        setScientificSimulations(actualData.scientificSimulations.map((i: any) => ({
                            ...i,
                            id: String(i.id),
                            publicHtml5Url: i.publicHtml5Url || i.public_html5_url || null,
                            shareToken: i.shareToken || i.share_token || null,
                            shareScope: i.shareScope || i.share_scope || null,
                            createdAt: i.createdAt || i.created_at,
                            updatedAt: i.updatedAt || i.updated_at,
                        })));
                    }
                    if (actualData.homeworks) setHomeworks(actualData.homeworks.map((i: any) => ({ ...i, id: String(i.id), savedDate: i.savedDate || i.saved_date })));
                    if (actualData.activities) setActivities(actualData.activities.map((i: any) => ({ ...i, id: String(i.id), savedDate: i.savedDate || i.saved_date })));
                    if (actualData.oralQuizzes) setOralQuizzes(actualData.oralQuizzes.map((i: any) => ({ ...i, id: String(i.id), savedDate: i.savedDate || i.saved_date })));
                    if (actualData.oralQuizSessions) setOralQuizSessions(actualData.oralQuizSessions.map((i: any) => ({ ...i, id: String(i.id), startedAt: i.startedAt || i.started_at })));
                    if (actualData.images) setImages(actualData.images.map((i: any) => ({ ...i, id: String(i.id), createdAt: i.createdAt || i.created_at })));
                    if (actualData.videoResources) setVideoResources(actualData.videoResources.map((i: any) => ({ ...i, id: String(i.id), createdAt: i.createdAt || i.created_at })));
                    if (actualData.videoProjects) setVideoProjects(actualData.videoProjects.map((i: any) => ({ ...i, id: String(i.id), createdAt: i.createdAt || i.created_at })));
                    if (actualData.interactiveVideos) setInteractiveVideos(actualData.interactiveVideos.map((i: any) => ({ ...i, id: String(i.id), createdAt: i.createdAt || i.created_at })));
                    
                    // Chỉ cập nhật classes khi API trả về classes (tránh ghi đè = [] khi poll joinRequests)
                    // Hợp nhất dữ liệu thông minh: chỉ ghi đè nếu dữ liệu trả về có giá trị HOẶC là tải đầy đủ
                    const isFullLoad = !resources || resources === 'all' || resources === 'core';
                    const hasClasses = actualData.classes !== undefined || actualData.parentClasses !== undefined || actualData.studentClasses !== undefined;
                    
                    if (hasClasses) {
                        // Gộp tất cả các nguồn lớp học (teacher, parent, student)
                        const rawClasses = [
                            ...(actualData.classes || []),
                            ...(actualData.parentClasses || []),
                            ...(actualData.studentClasses || [])
                        ];
                        
                        // Nếu là tải partial (ví dụ poll joinRequests) và rawClasses rỗng, KHÔNG ghi đè data hiện tại
                        if (rawClasses.length > 0 || isFullLoad) {
                            // Loại bỏ các lớp trùng lặp theo ID
                            const uniqueClassesMap = new Map();
                            rawClasses.forEach((c: any) => {
                                if (c && c.id) uniqueClassesMap.set(String(c.id), c);
                            });
                            const classesToUse = Array.from(uniqueClassesMap.values());
                            
                            const mappedClasses: Class[] = classesToUse.map((c: any) => ({
                                ...c,
                                id: String(c.id),
                                academicYear: c.academic_year || c.academicYear || '',
                                gradeLevel: c.grade_level || c.gradeLevel || '',
                                classType: c.class_type || c.classType || 'hybrid',
                                enrollmentMode: (c.enrollment_mode === 'student_code' ? 'manual' : (c.enrollment_mode || c.enrollmentMode || 'manual')),
                                allowJoinFromOutside: c.allow_join_from_outside ?? (c.enrollment_mode !== 'manual'),
                                studentIds: (c.student_ids || c.studentIds || []).map(String),
                                teacherName: c.teacher_name || c.teacherName || '',
                                teacherPhone: c.teacher_phone || c.teacherPhone || '',
                                inviteCode: c.invite_code || c.inviteCode,
                                inviteExpiryDate: c.invite_expiry_date || c.inviteExpiryDate,
                                securityQuestion: c.security_question || c.securityQuestion,
                                isLocked: !!c.is_locked,
                            }));
                            setClasses(mappedClasses);
                        }
                    }
                    // Tương tự cho students
                    const hasStudents = actualData.students !== undefined || actualData.parentStudents !== undefined || actualData.studentStudents !== undefined;
                    if (hasStudents) {
                        const rawStudents = [
                            ...(actualData.students || []),
                            ...(actualData.parentStudents || []),
                            ...(actualData.studentStudents || [])
                        ];
                        
                        if (rawStudents.length > 0 || isFullLoad) {
                            const uniqueStudentsMap = new Map();
                            rawStudents.forEach((s: any) => {
                                if (s && s.id) uniqueStudentsMap.set(String(s.id), s);
                            });
                            const studentsToUse = Array.from(uniqueStudentsMap.values());
                            
                            const mappedStudents: Student[] = studentsToUse.map((s: any) => ({
                                ...s,
                                id: String(s.id),
                                studentId: s.student_id_code || s.studentId || s.studentIdCode || '',
                                realName: s.real_name || s.realName || '',
                                dob: s.dob || s.date_of_birth || s.dateOfBirth || '',
                                joinedAt: s.joined_at || s.joinedAt,
                                loginCode: s.login_code || s.loginCode,
                            }));
                            setStudents(mappedStudents);
                        }
                    }
                    if (actualData.children) {
                        setChildren(actualData.children.map((i: any) => ({ 
                            ...i, 
                            id: String(i.id), 
                            studentId: i.student_id ?? i.studentId,
                            avatarUrl: i.avatarUrl || i.avatar || '',
                            avatar: i.avatar || i.avatarUrl || null,
                            relationship: i.relationship || ''
                        })));
                    }
                    if (actualData.joinRequests) setJoinRequests(actualData.joinRequests.map((i: any) => ({ ...i, id: String(i.id) })));
                    if (actualData.notifications) setNotifications(actualData.notifications.map((i: any) => ({ ...i, id: String(i.id) })));
                    if (actualData.parentStudentStats || actualData.studentStudentStats) {
                        setStudentStats(actualData.parentStudentStats || actualData.studentStudentStats || []);
                    }
                    const assignmentsToUse = actualData.parentAssignments ?? actualData.assignments;
                    const submissionsToUse = actualData.parentSubmissions ?? actualData.submissions;
                    const homeworksToUse = actualData.parentHomeworks ?? actualData.homeworks;
                    if (assignmentsToUse !== undefined) {
                        setAssignments(assignmentsToUse.map((i: any) => ({
                            ...i,
                            id: String(i.id),
                            classId: String(i.classId ?? i.class_id ?? i.classId ?? ''),
                            homeworkId: String(i.homeworkId ?? i.homework_id ?? i.homeworkId ?? ''),
                        })));
                    }
                    if (submissionsToUse !== undefined) {
                        setSubmissions(submissionsToUse.map((i: any) => ({ 
                            ...i, 
                            id: String(i.id),
                            assignmentId: String(i.assignmentId ?? i.assignment_id ?? ''),
                            studentId: String(i.studentId ?? i.student_id ?? '')
                        })));
                    }
                    if (homeworksToUse !== undefined) {
                        setHomeworks(homeworksToUse.map((i: any) => ({ ...i, id: String(i.id) })));
                    }

                    if (actualData.learningPlans) {
                        setChildLearningPlans(actualData.learningPlans.map((p: any) => ({
                            ...p,
                            id: String(p.id),
                            dailyPlans: (p.dailyPlans || p.daily_plans || []).map((day: any, dIdx: number) => ({
                                ...day,
                                modules: (day.modules || []).map((mod: any, mIdx: number) => ({
                                    ...mod,
                                    id: mod.id || `mod-${p.id}-${dIdx}-${mIdx}`
                                }))
                            }))
                        })));
                    }

                    if (actualData.learningHistory) {
                        setLearningHistory(actualData.learningHistory.map((h: any) => ({ ...h, id: String(h.id) })));
                    }

                    const skknList = actualData.skknProjects || actualData.skkn_projects || [];
                    if (skknList.length > 0) setSkknProjects(skknList.map((i: any) => ({ ...i, id: String(i.id) })));
                });
            }
        } catch (error: any) {
            console.error("Failed to load aggregate metadata:", error);
            // Auto logout if unauthorized
            if (error.message?.includes('Unauthorized') || error.message?.includes('401')) {
                handleLogout();
            }
        } finally {
            isInitialLoading.current = false;
            if (!silent) setIsLoadingData(false);
            setIsBackgroundLoading(false);
            isFetchingData.current = false;
        }
    } else if (APP_MODE !== 'PRODUCTION') {
        // In dev mode, always use mock data
        setDocuments(mockDocuments);
        setLessonPlans(mockLessonPlans);
        setDrawingLessons([]);
        setQuizzes(mockQuizzes);
        setGames(mockGames);
        setPresentations(mockPresentations);
        setHomeworks(mockHomeworks);
        setActivities(mockActivities);
        setStudents(mockStudents);
        setClasses(mockClasses);
        setChildren(mockChildren);
        isInitialLoading.current = false;
    }
  }, [user?.email, handleLogout]);

  // Polling cho các tài liệu đang phân tích tự động ngầm (AI tự động phân đoạn)
  // Đã tắt theo yêu cầu để tránh quá tải, chuyển sang xử lý ngầm định kỳ ở Backend
  /*
  const pollCountRef = useRef(0);
  useEffect(() => {
    const hasAnalyzing = documents.some(d => d.status === 'analyzing');
    if (!hasAnalyzing) {
        pollCountRef.current = 0; // Reset khi không còn file nào đang phân tích
        return;
    }
    if (!user?.email) return;

    const interval = setInterval(() => {
      if (pollCountRef.current >= 5) {
        clearInterval(interval);
        return;
      }
      pollCountRef.current++;
      loadProductionData('documents', true, true);
    }, 15000); // Giảm tần suất polling xuống 15 giây để đỡ tốn tài nguyên

    return () => clearInterval(interval);
  }, [documents, user?.email, loadProductionData]);
  */

  useEffect(() => {
    loadProductionData();
  }, [loadProductionData]);

  useEffect(() => {
        if (currentView === View.ClassManagement && activeRoleConfig.isTeacher) {
        // Hiển thị danh sách lớp ngay (đã có từ dashboard), tải joinRequests nền sau
        loadProductionData('core,teacher').then(() => loadProductionData('joinRequests', false, true));
    } else if (currentView === View.TeacherTools && activeRoleConfig.isTeacher) {
        // Tải trước dữ liệu lớp học khi vào dashboard giáo viên (nạp ngầm để ko hiện loading)
        loadProductionData('core,teacher', false, true);
    } else if (currentView === View.DocumentLibrary || currentView === View.SKKNCreator) {
        // Tối ưu: Chỉ force reload nếu cache quá 5 phút hoặc chưa nạp
        const now = Date.now();
        const cacheTimeout = 5 * 60 * 1000; // 5 mins
        const needsReload = (now - lastLibraryLoadTime.current > cacheTimeout);
        
        if (needsReload || !loadedResources.current.has('library')) {
            loadProductionData(activeRoleConfig.isTeacher ? 'library,teacher' : 'library', needsReload).then(() => {
                lastLibraryLoadTime.current = Date.now();
            });
        }
    } else if (currentView === View.ParentDashboard && activeRoleConfig.isParent) {
        // Force refresh khi vào dashboard phụ huynh để luôn thấy dữ liệu mới sau khi GV phê duyệt
        loadProductionData('core,parent', true, true);
    } else if (currentView === View.ChildDashboard && activeRoleConfig.isParent) {
        // Child dashboard: luôn tải core,parent để đảm bảo có assignments/tasks/notifications
        // Đồng thời tải thêm lộ trình và lịch sử học tập của con
        loadProductionData('core,parent,learningPlans,learningHistory,submissions', true, true);
    } else if (location.pathname === '/tham-gia-lop' && activeRoleConfig.isStudent) {
        loadProductionData('core,joinRequests', true);
    } else if (currentView === View.StudentDashboard && activeRoleConfig.isStudent) {
        // Students need core (joinRequests), student data, and library
        loadProductionData('core,student,library');
    }
  }, [currentView, activeRoleConfig.isTeacher, activeRoleConfig.isParent, activeRoleConfig.isStudent, loadProductionData, location.pathname]);

  // Tự động đồng bộ cài đặt người dùng (Profile/Role) về Backend khi có thay đổi (PRODUCTION mode)
  const handleUpdateUserSettings = useCallback((newSettings: React.SetStateAction<UserSettings | null>) => {
      setUser(prevUser => {
          const updatedUser = newSettings instanceof Function ? newSettings(prevUser) : newSettings;
          if (updatedUser) {
            localStorage.setItem('userData', JSON.stringify(updatedUser));
            if (APP_MODE === 'PRODUCTION' && updatedUser.authType !== 'student_code') {
                updateUserSettings(updatedUser).catch(err => console.error("Failed to sync settings to backend:", err));
            }
          }
          return updatedUser;
      });
  }, []);

  const buildBreadcrumbs = (): { label: string; onClick?: () => void }[] => {
    const go = (v: View) => () => handleNavigate(v);
    // "Trang chủ" của người dùng đã đăng nhập = Deep Workspace (string path).
    const goDashboard = () => handleNavigate(activeRoleConfig.dashboardPath);
    const home = { label: 'Trang chủ', onClick: displayUser ? goDashboard : go(View.Dashboard) };
    const dashboardCrumb = displayUser ? { label: activeRoleConfig.dashboardLabel, onClick: goDashboard } : null;

    const withDashboard = (crumbs: { label: string; onClick?: () => void }[]) => {
      if (dashboardCrumb && crumbs.length >= 2) {
        return [home, dashboardCrumb, ...crumbs.slice(1)];
      }
      if (dashboardCrumb && crumbs.length === 1 && crumbs[0].label !== 'Trang chủ') {
        return [home, dashboardCrumb, crumbs[0]];
      }
      return crumbs;
    };

    switch (currentView) {
      case View.DocumentLibrary:
        return withDashboard([home, { label: 'Thư viện' }]);
      case View.DocumentWorkspace:
        return withDashboard([home, { label: 'Tài liệu' }]);
      case View.LessonPlanCreator:
        return withDashboard([home, { label: 'Tạo giáo án' }]);
      case View.QuizCreator:
        return withDashboard([home, { label: 'Tạo bài kiểm tra' }]);
      case View.GameCreator:
        return withDashboard([home, { label: 'Tạo trò chơi' }]);
      case View.SlideCreator:
        return withDashboard([home, { label: 'Tạo trình chiếu' }]);
      case View.HomeworkCreator:
        return withDashboard([home, { label: getHomeworkFeatureLabel(displayUser?.role) }]);
      case View.OralQuizCreator:
        return withDashboard([home, { label: 'Tạo nội dung kiểm tra kiến thức' }]);
      case View.OralQuizView:
        return withDashboard([home, { label: 'Chi tiết kiểm tra kiến thức' }]);
      case View.OralQuizEdit:
        return withDashboard([home, { label: 'Chỉnh sửa kiểm tra kiến thức' }]);
      case View.OralQuizRunner:
        return withDashboard([home, { label: 'Đang kiểm tra kiến thức' }]);
      case View.OnlineQuizTaker:
        return withDashboard([home, { label: 'Làm đề kiểm tra' }]);
      case View.QuizAttemptResult:
        return withDashboard([home, { label: 'Kết quả bài làm' }]);
      case View.ActivityCreator:
        return withDashboard([home, { label: 'Tạo hoạt động' }]);
      case View.VideoCreator:
        return withDashboard([home, { label: 'Tạo video' }]);
      case View.InteractiveVideo:
      case View.InteractiveVideoEdit:
        return withDashboard([home, { label: 'Video tương tác' }]);
      case View.AILab:
        return withDashboard([home, { label: 'Phòng Lab AI' }]);
      case View.AIEthics:
        return withDashboard([home, { label: 'Thử thách Đạo đức AI' }]);
      case View.AIFactCheck:
        return withDashboard([home, { label: 'Kiểm chứng AI' }]);
      case View.CareerJourney:
        return withDashboard([home, { label: 'Hành trình khám phá' }]);
      case View.Settings:
        return withDashboard([home, { label: 'Cài đặt' }]);
      case View.CreditStore:
        return withDashboard([home, { label: `Mua ${UI_CONFIG.CREDIT_LABEL || 'Tín dụng'}` }]);
      case View.ClassManagement:
        return withDashboard([home, { label: 'Quản lý lớp học' }]);
      case View.SKKNCreator:
        return withDashboard([home, { label: 'Tạo SKKN' }]);
      case View.YearPlanCreator:
        return withDashboard([home, { label: 'Kế hoạch năm học (PPCT)' }]);
      case View.MeetingMinutes:
        return withDashboard([home, { label: 'Biên bản họp tổ chuyên môn' }]);
      case View.ProfessionalPortfolio:
        return withDashboard([home, { label: 'Hồ sơ chuyên môn số' }]);
      case View.DesignCenter:
        return withDashboard([home, { label: 'Trung tâm thiết kế' }]);
      case View.LessonPlanView:
      case View.LessonPlanEdit:
        return withDashboard([home, { label: 'Chi tiết giáo án' }]);
      case View.QuizView:
      case View.QuizEdit:
        return withDashboard([home, { label: 'Chi tiết đề thi' }]);
      case View.GameView:
      case View.GameEdit:
        return withDashboard([home, { label: 'Chi tiết trò chơi' }]);
      case View.SlideView:
      case View.SlideEdit:
        return withDashboard([home, { label: 'Chi tiết trình chiếu' }]);
      case View.HomeworkView:
      case View.HomeworkEdit:
        return withDashboard([home, { label: 'Chi tiết bài tập' }]);
      case View.ActivityView:
      case View.ActivityEdit:
        return withDashboard([home, { label: 'Chi tiết hoạt động' }]);
      case View.SharedLibrary:
        return withDashboard([home, { label: 'Thư viện cộng đồng' }]);
      case View.CategoryDetailPage:
        const catSlug = location.pathname.split('/').pop() || '';
        const internalCat = REVERSE_RESOURCE_TYPE_URL_MAP[catSlug] || catSlug;
        return withDashboard([home, { label: 'Thư viện cộng đồng', onClick: go(View.SharedLibrary) }, { label: TYPE_LABELS[internalCat] || 'Danh mục' }]);
      case View.SharedResourceView:
        return withDashboard([home, { label: 'Thư viện cộng đồng', onClick: go(View.SharedLibrary) }, { label: 'Chi tiết tài nguyên' }]);
      case View.TeacherTools:
        return displayUser ? [home, { label: 'Bảng điều khiển' }] : [home];
      case View.ParentDashboard:
        return displayUser ? [home, { label: 'Bảng điều khiển' }] : [home];
      case View.ChildDashboard:
        if (displayUser?.role === 'parent') {
          const pathSegments = location.pathname.split('/');
          const urlChildId = pathSegments[pathSegments.length - 1] !== 'bang-hoc-cua-con' ? pathSegments[pathSegments.length - 1] : null;
          const child = navigationPayload.child ?? 
            (urlChildId ? children.find((c: Child) => String(c.id) === String(urlChildId)) : null) ??
            (navigationPayload.childId ? children.find((c: Child) => String(c.id) === String(navigationPayload.childId)) : null) ?? 
            (children.length > 0 ? children[0] : null);
          const childLabel = child?.name ? `Chi tiết ${child.name}` : 'Bảng học của con';
          return withDashboard([{ label: childLabel }]);
        }
        return user ? [home, { label: 'Lộ trình của tôi' }] : [home];
      case View.StudentDashboard:
        return user ? [home, { label: 'Lộ trình của tôi' }] : [home];
      case View.AssignmentDetail:
      case View.StudentAssignmentDetail:
        return withDashboard([home, { label: 'Chi tiết nhiệm vụ' }]);
      default:
        return [];
    }
  };

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleRefreshChildren = useCallback(() => loadProductionData('children', true, true), [loadProductionData]);
  
  const handleLogin = async (userSkeleton: UserSettings) => {
    // Đồng bộ cờ bắt buộc đổi mật khẩu ngay sau khi đăng nhập trong cùng phiên
    // SPA (Login.tsx set localStorage nhưng state ở đây chỉ đọc localStorage
    // lúc mount, không tự re-render khi login xảy ra giữa phiên).
    try { setRequiresPasswordChange(localStorage.getItem('EDUAPP_REQUIRES_PW_CHANGE') === 'true'); } catch {}

    // 1. Lưu token để sẵn sàng gọi API (giữ authType nếu đăng nhập mã)
    const authData = localStorage.getItem('EDUAPP_AUTH');
    const parsed = authData ? JSON.parse(authData) : {};
    const token = parsed.accessToken || null;
    const authType = parsed.authType === 'student_code' ? 'student_code' : (userSkeleton.authType === 'student_code' ? 'student_code' : null);
    setEduappAccessToken(token, userSkeleton.email, authType ?? undefined);

    let finalUser = { ...INITIAL_USER_SETTINGS, ...userSkeleton };

    // 2. Nạp dữ liệu CORE (Settings, Role, Credit, Notifications) - Rất nhanh
    if (APP_MODE === 'PRODUCTION') {
        setIsLoadingData(true);
        try {
            // Chỉ nạp nhóm 'core' để lấy Role và thông tin cơ bản
            const allData = await fetchAllUserData('core');
            if (allData && allData.userSettings) {
                finalUser = { ...finalUser, ...(allData.userSettings as UserSettings) };
                
                // Đánh dấu đã nạp core để không nạp lại
                dataLoadedFor.current = finalUser.email;
                loadedResources.current.add('core');
                loadedResources.current.add('default');

                // Cập nhật thông báo (core không còn joinRequests - lazy load khi cần)
                React.startTransition(() => {
                    if (allData.notifications) setNotifications(allData.notifications.map((i: any) => ({ ...i, id: String(i.id) })));
                });
            }
        } catch (e) {
            console.error("Lỗi nạp cấu hình sau đăng nhập:", e);
        } finally {
            setIsLoadingData(false);
        }
    }

    // 3. Cập nhật trạng thái User (Lúc này đã có Role và Credit thực tế)
    setUser(finalUser);
    localStorage.setItem('userData', JSON.stringify(finalUser));
    
    // 4. Điều hướng dựa trên Role đã xác định
    if (!finalUser.role) {
        handleNavigate(View.RoleSelection);
        setPostLoginRedirect(null);
        return;
    }

    // Deep-link login: ưu tiên URL đích đã ghi nhớ (từ ?returnUrl hoặc sessionStorage)
    // → sau đăng nhập quay lại đúng trang người dùng định vào (kèm query & hash).
    const returnUrl = consumeReturnUrl();
    if (returnUrl) {
        setPostLoginRedirect(null);
        navigate(returnUrl);
        return;
    }

    if (postLoginRedirect) {
        const payload = postLoginRedirect.payload || {};
        const joinCode = payload.joinCode || (postLoginRedirect as any).joinCode;
        // Học sinh mở link join → chuyển sang trang tìm lớp thay vì ParentDashboard
        if (finalUser.role === 'student' && joinCode) {
            navigate('/tham-gia-lop', { state: { joinCode } });
        } else {
            navigate(ViewPath[postLoginRedirect.view] || '/', { state: payload });
        }
        setPostLoginRedirect(null);
    } else {
        // Sau đăng nhập → vào thẳng Deep Workspace theo role (landing mới).
        const workspaceMap: Record<string, string> = {
            'teacher': '/workspace/giao-vien',
            'parent': '/workspace/phu-huynh',
            'student': '/workspace/hoc-sinh',
        };
        const target = workspaceMap[finalUser.role];
        if (target) navigate(target);
        else handleNavigate(View.Dashboard);
    }
  };

  const handleCreditUsage = useCallback((cost: number, actionName: string): boolean => {
    if (!user) return false;
    const availableCredits = Number(user.totalCredits || 0);
    if (availableCredits < cost) {
        setIsOutOfCreditsModalOpen(true);
        return false;
    }
    return true;
  }, [user]);

  useEffect(() => {
    const handleCreditBalanceUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ balance?: number }>).detail;
      const nextBalance = Number(detail?.balance);
      if (!Number.isFinite(nextBalance)) return;

      setUser(prev => {
        if (!prev) return prev;
        const updated = {
          ...prev,
          totalCredits: nextBalance,
          usedCredits: 0,
        };
        localStorage.setItem('userData', JSON.stringify(updated));
        return updated;
      });
    };

    window.addEventListener(EDUAPP_CREDIT_BALANCE_UPDATED_EVENT, handleCreditBalanceUpdated as EventListener);
    return () => {
      window.removeEventListener(EDUAPP_CREDIT_BALANCE_UPDATED_EVENT, handleCreditBalanceUpdated as EventListener);
    };
  }, []);

  const handleAddNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp' | 'isRead'>, showToast: boolean = false, skipNotification: boolean = false) => {
    const newNotification: Notification = {
        ...notification,
        id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: formatDateForMySQL(new Date()),
        isRead: false,
    };
    if (!skipNotification) {
        setNotifications(prev => [newNotification, ...prev].slice(0, 50)); // Keep last 50
    }
    
    // Chỉ hiển thị popup nếu được yêu cầu (thường là các hành động tương tác trực tiếp)
    if (showToast) {
        const toastType = newNotification.type === 'info' ? 'info' : (newNotification.type === 'deadline' ? 'error' : 'success');
        addToast(newNotification.message, toastType);
    }
  }, [setNotifications, addToast]);

  const handleMarkAsRead = useCallback((id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    if (APP_MODE === 'PRODUCTION') {
      markNotificationAsRead(id).catch(() => {});
    }
  }, [setNotifications]);

  const handleMarkAllAsRead = useCallback(() => {
    setNotifications(prev => {
      const ids = prev.map(n => n.id);
      if (APP_MODE === 'PRODUCTION') markAllNotificationsAsRead(ids).catch(() => {});
      return prev.map(n => ({ ...n, isRead: true }));
    });
  }, [setNotifications]);

  const handleClearRead = useCallback(() => {
    setNotifications(prev => {
      const readIds = prev.filter(n => n.isRead).map(n => n.id);
      if (APP_MODE === 'PRODUCTION') deleteReadNotifications(readIds).catch(() => {});
      return prev.filter(n => !n.isRead);
    });
  }, [setNotifications]);

  const handlePurchasePackage = useCallback((pkg: CreditPackage) => {
    setPaymentModalPackage(pkg);
  }, []);

  const handleCreateOrderForPayment = useCallback(async (pkg: CreditPackage, provider: 'payos' | 'bank' | 'momo', couponCode?: string) => {
    if (APP_MODE !== 'PRODUCTION') return null;
    try {
      const authData = localStorage.getItem('EDUAPP_AUTH');
      const token = authData ? JSON.parse(authData).accessToken : null;
      const response = await fetch(`${API_BASE_URL}/api/edu/credits/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          packageId: pkg.id,
          provider,
          couponCode,
          returnUrl: window.location.origin + '/cua-hang-credit?status=success&isPopup=true',
          cancelUrl: window.location.origin + '/cua-hang-credit?status=cancelled&isPopup=true'
        })
      });
      const data = await response.json();
      if (!response.ok) {
        const validationMessage = data?.errors?.couponCode?.[0] || data?.message || 'Không thể tạo đơn hàng thanh toán';
        throw new Error(validationMessage);
      }
      if (data.orderId) {
        return {
          orderId: data.orderId,
          transactionCode: data.transferNote,
          checkoutUrl: data.checkoutUrl || undefined,
          amountVnd: data.amountVnd,
          baseCredits: data.baseCredits,
          bonusCredits: data.bonusCredits,
          totalCredits: data.totalCredits,
          coupon: data.coupon || null,
        };
      }
    } catch (e) {
      console.error("Lỗi khi tạo đơn hàng thanh toán:", e);
      throw e;
    }
  }, []);

  const handleConfirmPayment = useCallback((successCredits?: number, successMessage?: string) => {
    if (!user || !user.paymentDetails) return;

    const packageId = user.paymentDetails.packageId;
    
    // Tìm gói để lấy số credits hiển thị thông báo
    let pkg = user.paymentDetails.package;
    if (!pkg) pkg = CREDIT_PACKAGES.find(p => p.id === packageId);
    if (!pkg && packageId && typeof packageId === 'string' && packageId.startsWith('onetime_dynamic_')) {
        const credits = parseInt(packageId.replace('onetime_dynamic_', ''), 10);
        if (!isNaN(credits)) {
            pkg = { id: packageId, name: `Gói Tăng tốc ${credits.toLocaleString('vi-VN')} ${(UI_CONFIG.CREDIT_LABEL || 'Tín dụng')}`, credits: credits, priceVND: credits * 1000, category: 'onetime', billingCycle: 'onetime' };
        }
    }

    if (pkg) {
        const displayCredits = successCredits || user.paymentDetails.totalCredits || pkg.credits;
        const msg = successMessage || `Thanh toán thành công! Đã cộng ${displayCredits.toLocaleString('vi-VN')} ${(UI_CONFIG.CREDIT_LABEL || 'Tín dụng')}.`;
        addToast(msg, 'success');
    } else if (successMessage) {
        addToast(successMessage, 'success');
    }

    handleUpdateUserSettings(prevUser => {
        if (!prevUser || !prevUser.paymentDetails) return prevUser;

        const packageId = prevUser.paymentDetails.packageId;
        
        // 1. Ưu tiên dùng thông tin gói đã được lưu trong paymentDetails (đối với gói động hoặc gói từ backend)
        let pkg = prevUser.paymentDetails.package;
        
        // 2. Dự phòng tìm trong danh sách gói tĩnh (nếu không có trong paymentDetails)
        if (!pkg) pkg = CREDIT_PACKAGES.find(p => p.id === packageId);
        
        // 3. Xử lý gói động (Booster) nếu vẫn không tìm thấy
        if (!pkg && packageId && typeof packageId === 'string' && packageId.startsWith('onetime_dynamic_')) {
            const credits = parseInt(packageId.replace('onetime_dynamic_', ''), 10);
            if (!isNaN(credits)) {
                pkg = {
                    id: packageId,
                    name: `Gói Tăng tốc ${credits.toLocaleString('vi-VN')} ${(UI_CONFIG.CREDIT_LABEL || 'Tín dụng')}`,
                    credits: credits,
                    priceVND: credits * 1000,
                    category: 'onetime',
                    billingCycle: 'onetime',
                    tagline: 'Mua lẻ linh hoạt',
                    description: '',
                };
            }
        }

        if (!pkg) {
            console.error("Không tìm thấy gói đã mua trong khi xác nhận. PackageId:", packageId);
            if (APP_MODE === 'PRODUCTION') {
                loadProductionData('core', true);
            }
            return { ...prevUser, paymentDetails: null };
        }
        
        const creditsAdded = prevUser.paymentDetails.totalCredits || pkg.credits;
        const bonusCreditsAdded = prevUser.paymentDetails.bonusCredits || 0;
        const newRecord: PurchaseRecord = {
            packageId: pkg.id,
            packageName: pkg.name,
            purchaseDate: new Date().toISOString(),
            creditsAdded,
            baseCreditsAdded: prevUser.paymentDetails.baseCredits || Math.max(0, creditsAdded - bonusCreditsAdded),
            bonusCreditsAdded,
            couponCode: prevUser.paymentDetails.coupon?.code,
            pricePaid: prevUser.paymentDetails.amountVnd || pkg.priceVND,
            transactionCode: prevUser.paymentDetails.transactionCode,
            isSubscription: pkg.billingCycle !== 'onetime',
            isPremiumEligible: pkg.isPremiumEligible || false,
            canRemoveWatermark: pkg.canRemoveWatermark || false,
            aiTier: pkg.aiTier || 'basic',
        };

        const updatedUser: UserSettings = {
            ...prevUser,
            totalCredits: (prevUser.totalCredits || 0) + creditsAdded,
            purchaseHistory: [...(prevUser.purchaseHistory || []), newRecord],
            storageLimitMB: (prevUser.storageLimitMB || 0) + (pkg.storageLimitMB || 0),
            paymentDetails: null,
            groupStorageLimitMB: pkg.category.startsWith('group') ? (prevUser.groupStorageLimitMB || 0) + (pkg.storageLimitMB || 0) : prevUser.groupStorageLimitMB,
        };
        
        // In production, force a refresh from backend to ensure state is in sync
        if (APP_MODE === 'PRODUCTION') {
            loadProductionData('core', true);
        }

        return updatedUser;
    });
  }, [user, handleUpdateUserSettings, loadProductionData, addToast]);

  const handleCancelPayment = useCallback(() => {
    handleUpdateUserSettings(prevUser => {
        if (!prevUser) return null;
        return { ...prevUser, paymentDetails: null };
    });
    setPaymentModalPackage(null);
  }, [handleUpdateUserSettings]);

  // Tối ưu hóa: Sử dụng Ref để tránh re-create handler và giảm overhead của event listener
  const confirmPaymentRef = useRef(handleConfirmPayment);
  const addToastRef = useRef(addToast);
  
  useEffect(() => {
    confirmPaymentRef.current = handleConfirmPayment;
    addToastRef.current = addToast;
  }, [handleConfirmPayment, addToast]);

  // Master listener: localStorage (storage) + postMessage + BroadcastChannel
  useEffect(() => {
    const lastTsRef = { current: null as number | null };
    const dispatchPayosPayload = (data: any) => {
      if (!data || typeof data !== 'object') return;
      const ts = typeof data._ts === 'number' ? data._ts : null;
      if (ts != null && lastTsRef.current === ts) return;
      const type = data.type;
      if (!type || typeof type !== 'string' || !type.startsWith('PAYOS_')) return;
      if (ts != null) lastTsRef.current = ts;
      const { status, message, credits } = data;
      payosDebugLog('app_receive_payos_payload', {
        type,
        status,
        credits: credits ?? null,
        message: message ?? null,
        ts,
      });
      setTimeout(() => {
        const isSuccess = status === 'success' || status === 'PAID' || type === 'PAYOS_SUCCESS';
        if (isSuccess) {
          payosDebugLog('app_dispatch_payos_success', {
            credits: credits ?? null,
            message: message ?? null,
          });
          confirmPaymentRef.current(credits, message);
          window.dispatchEvent(new CustomEvent('PAYOS_PAYMENT_SUCCESS', { detail: { message, credits } }));
        } else if (status === 'error' || status === 'CANCELLED' || type === 'PAYOS_ERROR') {
          const errorMsg = message || (status === 'CANCELLED' ? 'Giao dịch đã bị hủy.' : 'Thanh toán không thành công.');
          payosDebugWarn('app_dispatch_payos_error', {
            status,
            message: errorMsg,
          });
          addToastRef.current(errorMsg, status === 'CANCELLED' ? 'info' : 'error');
          window.dispatchEvent(new CustomEvent('PAYOS_PAYMENT_ERROR', { detail: { message: errorMsg } }));
        }
      }, 0);
    };

    const handler = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      payosDebugLog('app_receive_postmessage', {
        origin: e.origin,
        type: e.data?.type ?? null,
      });
      dispatchPayosPayload(e.data);
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key !== PAYOS_RESULT_STORAGE_KEY || !e.newValue) return;
      payosDebugLog('app_receive_storage_event', {
        key: e.key,
      });
      try {
        dispatchPayosPayload(JSON.parse(e.newValue));
      } catch (_) {
        /* noop */
      }
    };

    window.addEventListener('message', handler);
    window.addEventListener('storage', onStorage);

    let bc: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== 'undefined') {
      bc = new BroadcastChannel(PAYOS_RESULT_BROADCAST_CHANNEL);
      bc.onmessage = (ev: MessageEvent) => {
        payosDebugLog('app_receive_broadcastchannel', {
          channel: PAYOS_RESULT_BROADCAST_CHANNEL,
          type: ev.data?.type ?? null,
        });
        dispatchPayosPayload(ev.data);
      };
    }

    return () => {
      window.removeEventListener('message', handler);
      window.removeEventListener('storage', onStorage);
      bc?.close();
    };
  }, []);

  const handleBonusCredits = useCallback((recipientEmail: string, amount: number): { success: boolean, message: string } => {
    if (!user) {
        return { success: false, message: "Bạn cần đăng nhập để thực hiện." };
    }
    
    const totalGroupCredits = (user.purchaseHistory || [])
        .filter(p => CREDIT_PACKAGES.find(pkg => pkg.id === p.packageId)?.category.startsWith('group'))
        .reduce((sum, p) => sum + p.creditsAdded, 0);

    const totalDistributed = user.distributedCredits || 0;

    const availableToDistribute = totalGroupCredits - totalDistributed;

    if (amount > availableToDistribute) {
        return { success: false, message: `Số dư có thể cấp không đủ. Bạn chỉ còn ${availableToDistribute.toLocaleString('vi-VN')} ${(UI_CONFIG.CREDIT_LABEL || 'Tín dụng').toLowerCase()}.` };
    }

    try {
        const newShareLog: CreditShareLog = { 
            recipientEmail, 
            amount, 
            timestamp: new Date().toISOString(),
            isBonus: true,
            storageAllocatedMB: 0, // Assuming bonus doesn't allocate storage
        };
        handleUpdateUserSettings(prev => {
            if (!prev) return null;
            return {
                ...prev,
                distributedCredits: (prev.distributedCredits || 0) + amount,
                creditShareLog: [newShareLog, ...(prev.creditShareLog || [])],
            }
        });
        
        return { success: true, message: `Đã cấp thưởng thành công ${amount.toLocaleString('vi-VN')} ${(UI_CONFIG.CREDIT_LABEL || 'Tín dụng').toLowerCase()} cho ${recipientEmail}!` };

    } catch (e) {
        console.error("Lỗi khi cấp thưởng credit (mô phỏng):", e);
        return { success: false, message: "Đã xảy ra lỗi trong quá trình cấp thưởng." };
    }
  }, [user, handleUpdateUserSettings]);

  const handleSave = useCallback(<T extends { id: string | number }>(setter: React.Dispatch<React.SetStateAction<T[]>>, resourceName?: string) => async (item: Omit<T, 'id'> & { id?: string | number }): Promise<T> => {
    // Nếu dữ liệu đã có ID thực (từ backend - ví dụ do AI tự động lưu), chỉ cần cập nhật state
    const isAlreadySynced = item.id !== undefined && item.id !== null && !hasTemporaryClientId(item.id);
    
    if (isAlreadySynced) {
        const syncedItem = item as T;
        setter(prev => {
            const exists = prev.some(i => i.id === syncedItem.id);
            if (exists) return prev.map(i => i.id === syncedItem.id ? syncedItem : i);
            return [syncedItem, ...prev];
        });
        return syncedItem;
    }

    if (resourceName === 'images' && typeof (item as { url?: unknown }).url === 'string') {
        const normalizedTargetUrl = normalizeAssetIdentity((item as { url?: string }).url);
        const existingImage = images.find(img => normalizeAssetIdentity(img.url) === normalizedTargetUrl);
        if (existingImage) {
            const mergedImage = { ...existingImage, ...item, id: existingImage.id } as T;
            setter(prev => prev.map(i => i.id === mergedImage.id ? mergedImage : i));
            return mergedImage;
        }
    }

    const newItem = { ...item, id: `id_${Date.now()}_${Math.random()}` } as T;
    
    if (resourceName && APP_MODE === 'PRODUCTION') {
        if (!navigator.onLine) {
            setter(prev => [newItem, ...prev]);
            queueOfflineAction(resourceName, 'POST', newItem);
            return newItem;
        }

        try {
            // Use syncWithRollback for optimistic update and rollback support
            const synced = await syncWithRollback(
                resourceName, 
                'POST', 
                newItem, 
                undefined,
                (updatedData, action) => {
                    if (action === 'add') {
                        setter(prev => [updatedData, ...prev]);
                    } else if (action === 'update') {
                        setter(prev => prev.map(i => i.id == newItem.id || i.id == updatedData.id ? { ...newItem, ...updatedData } : i));
                    }
                }
            );
            
            // Đảm bảo trả về object có ID thực từ backend (phẳng hóa nếu backend trả về lồng trong data)
            const finalSynced = synced?.data && synced.data.id ? { ...synced, ...synced.data } : synced;
            return { ...newItem, ...finalSynced };
        } catch (error) {
            console.error(`Lỗi khi đồng bộ ${resourceName}:`, error);
            addToast(`Không thể lưu ${resourceName}. Vui lòng thử lại.`, 'error');
            // State update happened optimistically in syncWithRollback if we passed a callback, 
            // but syncWithRollback for POST only calls update on success for the real ID.
            // If it failed before success, we might need to remove the optimistic item.
            setter(prev => prev.filter(i => i.id !== newItem.id));
            throw error;
        }
    }

    // Default behavior for dev mode or when no resource name provided
    setter(prev => [newItem, ...prev]);
    return newItem;
  }, [addToast, images]);

  const handleSaveStudent = useCallback(async (studentData: Omit<Student, 'id'> & { id?: string | number }): Promise<Student> => {
    // Nếu dữ liệu đã có ID thực (từ backend), chỉ cần cập nhật state
    const isAlreadySynced = studentData.id && !String(studentData.id).startsWith('temp_') && !String(studentData.id).startsWith('id_');
    
    if (isAlreadySynced) {
        const syncedStudent = studentData as Student;
        setStudents(prev => {
            const exists = prev.some(s => s.id === syncedStudent.id);
            if (exists) return prev.map(s => s.id === syncedStudent.id ? syncedStudent : s);
            return [syncedStudent, ...prev];
        });
        return syncedStudent;
    }

    const generateLoginCode = () => Math.floor(100000 + Math.random() * 900000).toString();
    const tempId = `id_${Date.now()}_${Math.random()}`;
    const newStudent: Student = { 
        ...studentData, 
        id: tempId,
        loginCode: studentData.loginCode || generateLoginCode()
    };
    
    if (APP_MODE === 'PRODUCTION') {
        if (!navigator.onLine) {
            setStudents(prev => [newStudent, ...prev]);
            queueOfflineAction('students', 'POST', newStudent);
            return newStudent;
        }

        try {
            const synced = await syncWithRollback(
                'students', 
                'POST', 
                studentData,
                undefined,
                (data, action) => {
                    if (action === 'add') {
                        setStudents(prev => [data, ...prev]);
                        if (user?.email) updateLocalResource(user.email, 'students', data, 'add');
                    }
                    else if (action === 'update') {
                        setStudents(prev => prev.map(s => s.id === tempId || s.id === data.id ? { ...newStudent, ...data } : s));
                        if (user?.email) updateLocalResource(user.email, 'students', data, 'update');
                    }
                }
            );
            return { ...newStudent, ...synced };
        } catch (error: any) {
            console.error(`Lỗi khi đồng bộ students:`, error);
            
            let errorMsg = "Không thể lưu học sinh. Vui lòng thử lại.";
            if (error?.message?.includes('Duplicate entry') || error?.message?.includes('1062')) {
                errorMsg = "Mã học sinh hoặc Email đã tồn tại trong hệ thống. Vui lòng kiểm tra lại.";
            }
            
            addToast(errorMsg, 'error');
            setStudents(prev => prev.filter(s => s.id !== tempId));
            throw error;
        }
    }

    setStudents(prev => [newStudent, ...prev]);
    return newStudent;
  }, [setStudents, addToast]);

  const handleUpdate = useCallback(<T extends { id: string | number }>(setter: React.Dispatch<React.SetStateAction<T[]>>, resourceName?: string) => async (updatedItem: T, skipSync: boolean = false): Promise<void> => {
    if (resourceName && APP_MODE === 'PRODUCTION' && !skipSync) {
        const idStr = String(updatedItem.id);
        const isRealId = typeof updatedItem.id === 'number' || 
                        (typeof updatedItem.id === 'string' && /^\d+$/.test(idStr)) || 
                        (typeof updatedItem.id === 'string' && !hasTemporaryClientId(updatedItem.id));

        if (!navigator.onLine) {
            setter(prev => prev.map(item => item.id == updatedItem.id ? updatedItem : item));
            queueOfflineAction(resourceName, 'PATCH', updatedItem);
            return;
        }

        if (isRealId) {
            // Save state for rollback
            let originalItem: T | undefined;
            setter(prev => {
                const exists = prev.some(i => i.id === updatedItem.id);
                originalItem = prev.find(i => i.id === updatedItem.id);
                if (exists) {
                    return prev.map(item => item.id == updatedItem.id ? updatedItem : item);
                } else {
                    return [updatedItem, ...prev];
                }
            });

            // Update local cache immediately
            if (user?.email) {
                updateLocalResource(user.email, resourceName, updatedItem, 'update');
            }

            try {
                await syncWithRollback(
                    resourceName, 
                    'PATCH', 
                    updatedItem, 
                    updatedItem.id,
                    (data) => {
                        setter(prev => prev.map(item => item.id == data.id ? { ...item, ...data } : item));
                    }
                );
            } catch (error) {
                console.error(`Lỗi khi cập nhật ${resourceName}:`, error);
                addToast(`Không thể cập nhật ${resourceName}. Đang hoàn tác...`, 'error');
                // Rollback state
                if (originalItem) {
                    setter(prev => prev.map(item => item.id == updatedItem.id ? originalItem! : item));
                }
            }
            return;
        }
    }
    
    // Default behavior (update state only)
    setter(prev => prev.map(item => item.id == updatedItem.id ? updatedItem : item));
  }, [addToast]);

  const handleDelete = useCallback(<T extends { id: string | number }>(setter: React.Dispatch<React.SetStateAction<T[]>>, resourceName?: string) => async (id: string | number, skipSync: boolean = false) => {
    if (resourceName && APP_MODE === 'PRODUCTION' && !skipSync) {
        const idStr = String(id);
        const isRealId = typeof id === 'number' || (typeof id === 'string' && !hasTemporaryClientId(id));

        if (!navigator.onLine) {
            setter(prev => prev.filter(item => item.id != id));
            queueOfflineAction(resourceName, 'DELETE', { id });
            return;
        }

        if (isRealId) {
            let originalItem: T | undefined;
            setter(prev => {
                originalItem = prev.find(i => i.id === id);
                return prev.filter(item => item.id != id);
            });

            // Update local cache immediately
            if (user?.email) {
                updateLocalResource(user.email, resourceName, { id }, 'delete');
            }

            try {
                await syncWithRollback(
                    resourceName, 
                    'DELETE', 
                    { id }, 
                    id
                );
            } catch (error) {
                console.error(`Lỗi khi xóa ${resourceName}:`, error);
                addToast(`Không thể xóa ${resourceName}. Đang hoàn tác...`, 'error');
                // Rollback
                if (originalItem) {
                    setter(prev => [...prev, originalItem!]);
                }
            }
            return;
        }
    }

    // Default behavior
    setter(prev => prev.filter(item => item.id != id));
  }, [addToast]);

  const handleDuplicate = useCallback(<T extends { id: string | number }>(setter: React.Dispatch<React.SetStateAction<T[]>>, resourceName: string) => async (id: string | number): Promise<T | undefined> => {
    try {
        const newItem = await duplicateResource(resourceName, id) as T;
        setter(prev => [newItem, ...prev]);
        return newItem;
    } catch (error) {
        console.error(`Lỗi khi sao chép ${resourceName}:`, error);
        addToast(`Không thể sao chép. Vui lòng thử lại.`, 'error');
        return undefined;
    }
  }, [addToast]);

  const handleUpdateName = useCallback(<T extends { id: string | number, name?: string, title?: string, topic?: string }>(setter: React.Dispatch<React.SetStateAction<T[]>>, resourceName?: string) => async (id: string | number, newName: string) => {
    let itemToUpdate: T | undefined;
    setter(prev => {
        const item = prev.find(i => i.id == id);
        if (item) {
            itemToUpdate = { ...item };
            if ('title' in itemToUpdate && typeof itemToUpdate.title === 'string') (itemToUpdate as any).title = newName;
            if ('name' in itemToUpdate && typeof itemToUpdate.name === 'string') (itemToUpdate as any).name = newName;
            if ('topic' in itemToUpdate && typeof itemToUpdate.topic === 'string') (itemToUpdate as any).topic = newName;
            return prev.map(i => i.id == id ? itemToUpdate! : i);
        }
        return prev;
    });
    
    const isRealId = typeof id === 'number' || 
                    (typeof id === 'string' && 
                     !id.startsWith('id_') && 
                     !id.startsWith('vproj_') && 
                     !id.startsWith('temp_') &&
                     !id.startsWith('notif_'));

    if (resourceName && isRealId && itemToUpdate) {
        try {
            const synced = await syncResource(resourceName, 'PATCH', itemToUpdate, id);
            if (synced) {
                setter(prev => prev.map(i => i.id == id ? { ...itemToUpdate!, ...synced } : i));
            }
        } catch (error) {
            console.error(`Lỗi khi cập nhật tên ${resourceName}:`, error);
        }
    }
  }, []);

  const handleUpdateSkknName = useCallback(async (id: string | number, newName: string) => {
    const proj = skknProjects.find((p: any) => String(p.id) === String(id));
    if (!proj) return;
    const updated = { ...proj, data: { ...proj.data, title: newName } };
    await handleUpdate(setSkknProjects, 'skkn-projects')(updated);
  }, [skknProjects]);

  const handleSaveClass = useCallback(async (classData: any): Promise<Class> => {
    // Nếu dữ liệu đã có ID thực (từ backend), chỉ cần cập nhật state mà không gọi API sync nữa
    const isAlreadySynced = classData.id && !String(classData.id).startsWith('temp_') && !String(classData.id).startsWith('id_');
    
    if (isAlreadySynced) {
        setClasses(prev => {
            const exists = prev.some(c => c.id === classData.id);
            if (exists) return prev.map(c => c.id === classData.id ? classData : c);
            return [classData, ...prev];
        });
        return classData;
    }

    const tempId = `temp_class_${Date.now()}`;
    const generateInviteCode = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Exclude ambiguous chars like I, O, 0, 1
        let code = '';
        for (let i = 0; i < 6; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return code;
    };

    const newClass: Class = {
      ...classData,
      id: tempId,
      studentIds: [],
      // Mọi lớp đều có mã định danh 6 ký tự (invite_code) duy nhất trên hệ thống
      inviteCode: generateInviteCode()
    };
    
    if (APP_MODE === 'PRODUCTION') {
        if (!navigator.onLine) {
            setClasses(prev => [newClass, ...prev]);
            queueOfflineAction('classes', 'POST', newClass);
            return newClass;
        }

        try {
            const synced = await syncWithRollback(
                'classes', 
                'POST', 
                newClass,
                undefined,
                (data, action) => {
                    if (action === 'add') {
                        setClasses(prev => [data, ...prev]);
                        if (user?.email) updateLocalResource(user.email, 'classes', data, 'add');
                    }
                    else if (action === 'update') {
                        setClasses(prev => prev.map(c => c.id === tempId || c.id === data.id ? { ...newClass, ...data } : c));
                        if (user?.email) updateLocalResource(user.email, 'classes', data, 'update');
                    }
                }
            );
            return { ...newClass, ...synced };
        } catch (error) {
            console.error(`Lỗi khi đồng bộ classes:`, error);
            addToast("Không thể lưu lớp học. Vui lòng thử lại.", 'error');
            setClasses(prev => prev.filter(c => c.id !== tempId));
            throw error;
        }
    }

    setClasses(prev => [newClass, ...prev]);
    return newClass;
  }, [setClasses, addToast]);
  
  const handleSaveMultipleStudents = useCallback(async (classId: string | number, studentList: Partial<Student>[], syncedStudents?: Student[]) => {
      // Nếu đã có danh sách học sinh đã đồng bộ, chỉ cần cập nhật state
      if (syncedStudents && syncedStudents.length > 0) {
          setStudents(prev => {
              const filtered = prev.filter(ps => !syncedStudents.some(ss => ss.id === ps.id));
              return [...filtered, ...syncedStudents];
          });
          
          const newStudentIds = syncedStudents.map(s => String(s.id));
          setClasses(prevClasses => prevClasses.map(cls => 
              cls.id === classId ? { ...cls, studentIds: Array.from(new Set([...cls.studentIds, ...newStudentIds])) } : cls
          ));
          addToast(`Đã thêm thành công ${syncedStudents.length} học sinh.`, 'success');
          return;
      }

      const generateLoginCode = () => Math.floor(100000 + Math.random() * 900000).toString();
      
      const newStudents = studentList.map(s => {
          const parents = [];
          if ((s as any).parent_name || (s as any).parent_name_1) {
              parents.push({
                  id: `temp_parent_${Date.now()}_${Math.random()}`,
                  name: (s as any).parent_name || (s as any).parent_name_1,
                  phoneNumber: (s as any).parent_phone || (s as any).parent_phone_1 || '',
                  relationship: (s as any).parent_relationship || (s as any).parent_rel_1 || 'Phụ huynh 1'
              });
          }
          if ((s as any).parent_name_2) {
              parents.push({
                  id: `temp_parent_2_${Date.now()}_${Math.random()}`,
                  name: (s as any).parent_name_2,
                  phoneNumber: (s as any).parent_phone_2 || '',
                  relationship: (s as any).parent_relationship_2 || (s as any).parent_rel_2 || 'Phụ huynh 2'
              });
          }

          return {
              id: `temp_student_${Date.now()}_${Math.random()}`,
              name: s.name || 'Học sinh mới',
              studentId: s.studentId || (s as any).student_id_code || '',
              email: s.email || '',
              dob: s.dob || '',
              loginCode: generateLoginCode(),
              parents: parents
          };
      });
      
      setStudents(prev => [...prev, ...newStudents]);
      
      if (APP_MODE === 'PRODUCTION') {
          if (!navigator.onLine) {
              // Batch queueing
              newStudents.forEach(s => queueOfflineAction('students', 'POST', s));
          } else {
              // Simulation for production: in reality, we'd send the full list to a batch API
              for (const student of newStudents) {
                  try {
                      const synced = await syncResource('students', 'POST', { 
                          name: student.name,
                          student_id_code: student.studentId,
                          email: student.email,
                          dob: student.dob
                      });
                      if (synced && synced.id) {
                          setStudents(prev => prev.map(s => s.id === student.id ? synced : s));
                      }
                  } catch (e) {
                      queueOfflineAction('students', 'POST', student);
                  }
              }
          }
      }
      
      const newStudentIds = newStudents.map(s => s.id);
      setClasses(prevClasses => prevClasses.map(cls => 
          cls.id === classId ? { ...cls, studentIds: Array.from(new Set([...cls.studentIds, ...newStudentIds])) } : cls
      ));
      addToast(`Đã thêm thành công ${newStudents.length} học sinh.`, 'success');
  }, [setStudents, setClasses]);

  const formatDateForMySQL = (date: Date) => date.toISOString().slice(0, 19).replace('T', ' ');

  const handleCompleteModule = (planId: string | number, moduleId: string, isCorrect: boolean, isPreview: boolean = false) => {
    if (!moduleId) {
        console.error("Không thể lưu lịch sử học tập vì thiếu moduleId.");
        addToast("Lỗi: Không tìm thấy mã bài học. Vui lòng thử lại sau hoặc tải lại trang.", 'error');
        return;
    }

    if (isPreview) {
        // Chế độ xem thử: Chỉ thông báo kết quả, không lưu vào lịch sử và tiến độ
        addToast(isCorrect ? "Xem thử: Trả lời chính xác! (Tiến độ không được ghi nhận)" : "Xem thử: Trả lời chưa đúng! (Tiến độ không được ghi nhận)", 'info');
        return;
    }

    const historyRecord: Omit<LearningHistory, 'id'> = { planId, moduleId, isCorrect, completedAt: formatDateForMySQL(new Date()) };
    handleSave(setLearningHistory, 'learning-history')(historyRecord);
    const planToUpdate = childLearningPlans.find(p => p.id === planId);
    if (planToUpdate) {
        const updatedPlan = JSON.parse(JSON.stringify(planToUpdate)) as ChildLearningPlan;
        for (const day of updatedPlan.dailyPlans) {
            const moduleIndex = day.modules.findIndex(mod => String(mod.id) === String(moduleId));
            if (moduleIndex !== -1) {
                day.modules[moduleIndex].status = 'completed';
                break;
            }
        }
        if (updatedPlan.dailyPlans.every(day => day.modules.every(m => m.status === 'completed'))) {
            updatedPlan.planStatus = PlanStatus.Completed;
        }
        handleUpdate(setChildLearningPlans, 'learning-plans')(updatedPlan);
    }
  };

  const handleAssignHomework = async (assignmentData: Partial<Assignment>, skipSync: boolean = false) => {
    const isUpdate = !!assignmentData.id;
    const tempId = isUpdate ? assignmentData.id! : `asgn_${Date.now()}`;
    
    // Đảm bảo luôn có classId, ưu tiên từ assignmentData sau đó đến navigationPayload
    const classId = assignmentData.classId || navigationPayload.classId || '';
    
    if (!isUpdate && !classId) {
        addToast('Lỗi: Không xác định được lớp học để giao bài.', 'error');
        return;
    }

    const newAssignment: Assignment = { 
        id: tempId, 
        assignedDate: formatDateForMySQL(new Date()),
        homeworkId: '',
        dueDate: '',
        ...assignmentData,
        classId: classId // Đảm bảo không bị ghi đè bởi giá trị rỗng trong assignmentData
    };
    
    if (APP_MODE === 'PRODUCTION' && !skipSync) {
        try {
            // Chuẩn bị dữ liệu gửi lên backend (đảm bảo snake_case cho các trường bắt buộc nếu cần, 
            // mặc dù BaseStoreController đã xử lý camelCase)
            const payload = isUpdate ? assignmentData : newAssignment;
            
            const synced = await syncWithRollback(
                'assignments', 
                isUpdate ? 'PATCH' : 'POST', 
                payload,
                isUpdate ? assignmentData.id : undefined,
                (data, action) => {
                    if (action === 'add') {
                        setAssignments(prev => [data, ...prev]);
                        if (user?.email) updateLocalResource(user.email, 'assignments', data, 'add');
                    }
                    else if (action === 'update') {
                        setAssignments(prev => prev.map(a => a.id === tempId || a.id === data.id ? { ...newAssignment, ...data } : a));
                        if (user?.email) updateLocalResource(user.email, 'assignments', data, 'update');
                    }
                }
            );
            const msg = isUpdate ? 'Cập nhật thành công!' : (assignmentData.type === 'task') ? 'Đã giao nhiệm vụ thành công!' : (assignmentData.type === 'announcement') ? 'Đã đăng thông báo thành công!' : 'Đã giao bài tập thành công!';
            addToast(msg, 'success');
            return { ...newAssignment, ...synced };
        } catch (error) {
            console.error("Lỗi khi giao/cập nhật bài tập:", error);
            const errMsg = isUpdate ? 'Không thể cập nhật. Vui lòng thử lại.' : (assignmentData.type === 'task') ? 'Không thể giao nhiệm vụ. Vui lòng thử lại.' : (assignmentData.type === 'announcement') ? 'Không thể đăng thông báo. Vui lòng thử lại.' : 'Không thể giao bài tập. Vui lòng thử lại.';
            addToast(errMsg, 'error');
            if (!isUpdate) setAssignments(prev => prev.filter(a => a.id !== tempId));
            throw error;
        }
    }

    if (isUpdate) {
        setAssignments(prev => prev.map(a => a.id === assignmentData.id ? { ...a, ...assignmentData } : a));
    } else {
        setAssignments(prev => [newAssignment, ...prev]);
    }
    const msg = isUpdate ? 'Cập nhật thành công!' : (assignmentData.type === 'task') ? 'Đã giao nhiệm vụ thành công!' : (assignmentData.type === 'announcement') ? 'Đã đăng thông báo thành công!' : 'Đã giao bài tập thành công!';
    addToast(msg, 'success');
    return isUpdate ? (assignmentData as Assignment) : newAssignment;
  };

  const handleDeleteAssignment = async (assignmentId: string, force: boolean = false) => {
    if (APP_MODE === 'PRODUCTION') {
        try {
            await syncWithRollback(
                'assignments',
                'DELETE',
                {},
                force ? `${assignmentId}?force=true` : assignmentId,
                () => {
                    setAssignments(prev => prev.filter(a => a.id !== assignmentId));
                    setSubmissions(prev => prev.filter(s => s.assignmentId !== assignmentId));
                }
            );
            addToast('Đã thu hồi bài tập thành công!', 'success');
            return { success: true };
        } catch (error: any) {
            console.error("Lỗi khi thu hồi bài tập:", error);
            if (error.status === 409 || error.message?.includes('conflict')) {
                // Return conflict info for UI to handle
                return { success: false, conflict: true, message: error.message, submissionCount: error.submission_count };
            }
            addToast('Không thể thu hồi bài tập. Vui lòng thử lại.', 'error');
            return { success: false };
        }
    }
    
    setAssignments(prev => prev.filter(a => a.id !== assignmentId));
    setSubmissions(prev => prev.filter(s => s.assignmentId !== assignmentId));
    addToast('Đã thu hồi bài tập (Chế độ Demo)!', 'success');
    return { success: true };
  };

  const handleStudentSubmit = useCallback(async (assignmentId: string, studentId: string, results?: any) => {
    const submittedDate = formatDateForMySQL(new Date());
    const safeResults = results ? { ...results, attachments: undefined } : {};
    const newSubmission: Submission = { 
        id: `sub_${Date.now()}`, 
        assignmentId, 
        studentId, 
        status: SUBMISSION_STATUS.DA_NOP, 
        submittedDate,
        ...safeResults
    };

    // Cập nhật state ngay (optimistic)
    setSubmissions(prev => {
        const existingIndex = prev.findIndex(s => s.assignmentId === assignmentId && String(s.studentId) === String(studentId));
        if (existingIndex > -1) {
            const updated = [...prev];
            updated[existingIndex] = { ...newSubmission, id: prev[existingIndex].id };
            return updated;
        }
        return [newSubmission, ...prev];
    });

    // Đồng bộ lên DB khi PRODUCTION
    let finalSubmissionId = newSubmission.id;
    if (APP_MODE === 'PRODUCTION') {
        try {
            let content: { answers?: Record<string, any>; attachments?: { url: string; type: string; name: string }[] } | undefined;
            const files = results?.attachments as File[] | undefined;
            if (files?.length) {
                addToast('Đang tải file lên...', 'info');
                const uploaded = await Promise.all(files.map((f: File) => uploadSubmissionAttachment(f)));
                content = { answers: results?.answers, attachments: uploaded };
            } else if (results?.answers && Object.keys(results.answers).length > 0) {
                content = { answers: results.answers };
            }
            const result = await submitStudentWork(assignmentId, studentId, submittedDate, content);
            if (result?.id) {
                finalSubmissionId = String(result.id);
                setSubmissions(prev => prev.map(s => 
                    s.assignmentId === assignmentId && String(s.studentId) === String(studentId) ? { ...s, id: finalSubmissionId } : s
                ));
            }
        } catch (err: any) {
            console.error('Lỗi khi lưu bài nộp:', err);
            const msg = err.message || 'Lỗi khi đồng bộ bài nộp.';
            if (msg.includes('thu hồi') || msg.includes('không tồn tại')) {
                addToast(msg, 'error');
                // Hoàn tác state local nếu bài tập không còn hợp lệ
                setSubmissions(prev => prev.filter(s => !(s.assignmentId === assignmentId && String(s.studentId) === String(studentId))));
            } else {
                addToast('Đã nộp bài nhưng chưa đồng bộ lên máy chủ. Vui lòng thử lại.', 'error');
            }
        }
    }

    // Thông báo cho giáo viên
    const assignment = assignments.find(a => a.id === assignmentId);
    const homework = homeworks.find(h => h.id === assignment?.homeworkId);
    const student = students.find(s => s.email === studentId || String(s.id) === String(studentId));
    const classObj = classes.find(c => c.id === assignment?.classId);

    // Tự động chấm điểm chuyên cần nếu là nhiệm vụ 'none' hoặc 'check_only'
    if (assignment?.type === 'task' && (assignment?.submissionType === 'none' || assignment?.submissionType === 'check_only')) {
        const autoScore = classObj?.defaultParticipationScore ?? 10;
        const comment = assignment?.submissionType === 'none' ? 'Hệ thống tự động ghi nhận chuyên cần (Đã xem)' : 'Hệ thống tự động ghi nhận chuyên cần (Đã xác nhận)';
        
        // Cập nhật trạng thái 'Đã chấm' ngay lập tức
        setSubmissions(prev => prev.map(s => 
            s.assignmentId === assignmentId && String(s.studentId) === String(studentId) 
                ? { ...s, id: finalSubmissionId, status: SUBMISSION_STATUS.DA_CHAM, grade: autoScore, teacherComments: comment } 
                : s
        ));

        // Nếu ở PRODUCTION, gọi API chấm điểm luôn
        if (APP_MODE === 'PRODUCTION') {
            try {
                // Đợi một chút để đảm bảo state submissions đã được cập nhật với ID mới
                setTimeout(() => {
                    handleGradeSubmission(finalSubmissionId, autoScore, comment, false);
                }, 500);
            } catch (e) {
                console.error("Lỗi khi tự động chấm điểm chuyên cần:", e);
            }
        }
    }

    const isAnnouncement = assignment?.type === 'announcement';
    const itemTitle = assignment?.title || homework?.title || (isAnnouncement ? 'Thông báo' : 'Bài tập');
    handleAddNotification({
        type: 'info',
        message: isAnnouncement
            ? `Học sinh ${student?.name || studentId} đã xem thông báo "${itemTitle}" trong lớp ${classObj?.name || ''}`
            : `Học sinh ${student?.name || studentId} đã nộp bài "${itemTitle}" trong lớp ${classObj?.name || ''}`,
        icon: isAnnouncement ? 'announcement' : 'homework',
        link: { view: View.ClassManagement, payload: { activeTab: 'students', studentId: student?.id } }
    });

    addToast(isAnnouncement ? "Đã ghi nhận là đã xem thông báo!" : "Nộp bài thành công!", 'success');
  }, [assignments, homeworks, students, classes, handleAddNotification, setSubmissions]);

  const handleGradeSubmission = async (submissionId: string, grade: number, teacherComments: string, skipSync: boolean = false) => {
    const submission = submissions.find(s => s.id === submissionId);
    if (!submission) return;

    const updatedSubmission = { ...submission, status: SUBMISSION_STATUS.DA_CHAM, grade, teacherComments };

    if (APP_MODE === 'PRODUCTION' && !skipSync) {
        try {
            await syncWithRollback(
                'submissions', 
                'PATCH', 
                updatedSubmission,
                submissionId,
                (data) => {
                    setSubmissions(prev => prev.map(s => s.id === submissionId ? { ...s, ...data } : s));
                }
            );
        } catch (error) {
            console.error("Lỗi khi chấm điểm:", error);
            addToast("Không thể lưu điểm. Đang hoàn tác...", 'error');
            setSubmissions(prev => prev.map(s => s.id === submissionId ? submission : s));
            return;
        }
    } else {
        setSubmissions(prev => prev.map(s => s.id === submissionId ? updatedSubmission : s));
    }

    // Thông báo cho học sinh (giữ nguyên logic cũ)
    const assignment = assignments.find(a => a.id === submission?.assignmentId);
    const homework = homeworks.find(h => h.id === assignment?.homeworkId);
    const student = students.find(s => s.id === submission?.studentId || s.email === submission?.studentId);

    if (student) {
        const resultLabel = assignment?.gradingSystem === 'none' 
            ? 'Đã nhận xét' 
            : `Kết quả: ${formatGrade(grade, assignment?.gradingSystem)}`;
            
        handleAddNotification({
            type: 'info',
            message: `Giáo viên đã phản hồi bài tập "${homework?.title || assignment?.title}". ${resultLabel}`,
            icon: 'homework',
            link: { view: View.StudentDashboard }
        }, true);
    }
  };

  // Quick Student Code Login Handler
  useEffect(() => {
    (window as any).handleStudentCodeLogin = async (code: string, dobDigits: string) => {
        // Tìm học sinh khớp cả mã đăng nhập và ngày/tháng sinh (DDMM)
        const student = students.find(s => {
            const matchesCode = s.loginCode === code;
            if (!s.dob) return matchesCode && dobDigits === '0000'; // Fallback if no DOB set
            
            const birthDate = new Date(s.dob);
            const day = birthDate.getDate().toString().padStart(2, '0');
            const month = (birthDate.getMonth() + 1).toString().padStart(2, '0');
            const sDigits = `${day}${month}`;
            
            return matchesCode && sDigits === dobDigits;
        });
        return student || null;
    };
  }, [students]);
  
  const handleFileUploads = async (files: File[]): Promise<Document[]> => {
    if (!user) return [];
    const mapUploadErrorMessage = (error: unknown, fileName?: string): string => {
      const raw = error instanceof Error ? (error.message || '') : String(error || '');
      const msg = raw.toLowerCase();
      const displayName = fileName ? `"${fileName}"` : 'Tệp';
      if (msg.includes('413') || msg.includes('payload') || msg.includes('too large') || msg.includes('quá') && msg.includes('dung lượng')) {
        return `${displayName} vượt quá dung lượng cho phép. Vui lòng nén hoặc chia nhỏ tệp.`;
      }
      if (msg.includes('network') || msg.includes('kết nối') || msg.includes('failed to fetch') || msg.includes('server error')) {
        return `Không thể tải ${displayName} do lỗi mạng hoặc máy chủ. Vui lòng thử lại sau.`;
      }
      if (msg.includes('định dạng') || msg.includes('file type') || msg.includes('unsupported') || msg.includes('mimetype')) {
        return `${displayName} có định dạng không được hỗ trợ.`;
      }
      if (raw && raw.trim().length > 0) return raw;
      return `Tải lên ${displayName} thất bại.`;
    };
    
    // Giới hạn số lượng tệp mỗi lần tải lên (ví dụ: tối đa 5 tệp)
    const MAX_FILES = 5;
    if (files.length > MAX_FILES) {
        alert(`Bạn chỉ có thể tải lên tối đa ${MAX_FILES} tệp mỗi lần. Vui lòng chọn ít tệp hơn.`);
        return [];
    }

    // Giới hạn dung lượng mỗi tệp (ví dụ: 10MB) và tổng dung lượng
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const LARGE_FILE_THRESHOLD = 5 * 1024 * 1024; // 5MB - Cảnh báo tổng hợp
    let hasLargeFile = false;

    for (const file of files) {
        if (file.size > MAX_FILE_SIZE) {
            alert(`Tệp "${file.name}" vượt quá giới hạn 10MB. Vui lòng nén tệp hoặc chia nhỏ trước khi tải lên.`);
            return [];
        }
        if (file.size > LARGE_FILE_THRESHOLD) {
            hasLargeFile = true;
        }
    }

    if (hasLargeFile) {
        const confirmLarge = window.confirm("Hệ thống phát hiện tệp dung lượng lớn. Sau khi tải lên, nếu bạn cần ghép nhiều tệp lại, hãy chọn các tệp ở Thư viện và bấm 'Tổng hợp' — hệ thống sẽ tự động gộp và phân mảnh trong một bước, không tốn thêm credit. Tiếp tục tải lên?");
        if (!confirmLarge) return [];
    }

    const isGroupMember = !!user.memberOfGroupId;
    const storageLimit = (isGroupMember ? user.groupStorageLimitMB : user.storageLimitMB) || 0;
    const storageUsed = (isGroupMember ? user.groupStorageUsedMB : user.storageUsedMB) || 0;
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const availableSpace = (storageLimit * 1024 * 1024) - (storageUsed * 1024 * 1024);
    
    if (totalSize > availableSpace) {
        alert("Dung lượng lưu trữ của bạn (hoặc của nhóm) không đủ. Vui lòng xóa bớt tệp hoặc nâng cấp gói.");
        return [];
    }

    if (APP_MODE === 'PRODUCTION') {
        const results: Document[] = [];
        const failedMessages: string[] = [];
        for (const file of files) {
            const taskId = `upload_${Date.now()}_${Math.random()}`;
            const newTask: UploadTask = { id: taskId, fileName: file.name, progress: 0, status: 'uploading', type: 'document' };
            setActiveUploads(prev => [...prev, newTask]);

            try {
                const newDoc = await uploadLibraryDocument(file, (pct) => {
                    setActiveUploads(prev => prev.map(t => t.id === taskId ? { ...t, progress: pct } : t));
                });
                results.push(newDoc);
                setActiveUploads(prev => prev.map(t => t.id === taskId ? { ...t, status: 'completed', progress: 100 } : t));
                // Tự động xóa task sau 3 giây
                setTimeout(() => setActiveUploads(prev => prev.filter(t => t.id !== taskId)), 3000);
            } catch (error) {
                console.error("Upload error:", error);
                const friendlyError = mapUploadErrorMessage(error, file.name);
                failedMessages.push(friendlyError);
                setActiveUploads(prev => prev.map(t => t.id === taskId ? { ...t, status: 'error', error: friendlyError } : t));
                // Giữ task lỗi lâu hơn (10 giây) hoặc cho tới khi người dùng đóng
                setTimeout(() => setActiveUploads(prev => prev.filter(t => t.id !== taskId)), 10000);
            }
        }
        if (results.length > 0) {
            setDocuments(prev => [...results, ...prev]);
            loadProductionData('library', true, true); // Refresh to get updated storage info
            if (failedMessages.length > 0) {
                addToast(`Đã tải ${results.length} tệp, ${failedMessages.length} tệp thất bại.`, 'error');
            }
        } else if (failedMessages.length > 0) {
            const shortReason = failedMessages[0];
            addToast(shortReason, 'error');
            throw new Error(shortReason);
        }
        return results;
    }

    const promises = Array.from(files).map(file => new Promise<Document>((resolve) => {
        const reader = new FileReader();
        const docType = (file.name.split('.').pop()?.toLowerCase() || 'txt') as Document['type'];
        const validTypes: Document['type'][] = ['pdf', 'docx', 'doc', 'pptx', 'txt'];
        if (file.type === 'text/plain') {
          reader.onload = (e) => resolve({ id: `id_${Date.now()}_${Math.random()}`, name: file.name, type: 'txt', uploadDate: new Date().toLocaleDateString('vi-VN'), status: 'draft', content: e.target?.result as string });
          reader.readAsText(file);
        } else {
          resolve({ id: `id_${Date.now()}_${Math.random()}`, name: file.name, type: validTypes.includes(docType) ? docType : 'txt', uploadDate: new Date().toLocaleDateString('vi-VN'), status: 'draft', content: `[Nội dung cho tệp ${file.name} cần được dán thủ công. Phân tích tự động cho loại tệp này chưa được hỗ trợ.]` });
        }
    }));
    const newDocs = await Promise.all(promises);
    setDocuments(prev => [...newDocs, ...prev]);
    const uploadedSizeMB = totalSize / (1024*1024);
    handleUpdateUserSettings(prev => {
        if (!prev) return null;
        if (isGroupMember) return {...prev, groupStorageUsedMB: (prev.groupStorageUsedMB || 0) + uploadedSizeMB };
        return {...prev, storageUsedMB: (prev.storageUsedMB || 0) + uploadedSizeMB };
    });
    return newDocs;
  };

  const handleImageUploads = async (files: File[]) => {
    if (!user) return;
    if (APP_MODE === 'PRODUCTION') {
        const results = [];
        for (const file of files) {
            const taskId = `upload_${Date.now()}_${Math.random()}`;
            setActiveUploads(prev => [...prev, { id: taskId, fileName: file.name, progress: 0, status: 'uploading', type: 'image' }]);
            
            try {
                const newImg = await uploadLibraryImage(file, (pct) => {
                    setActiveUploads(prev => prev.map(t => t.id === taskId ? { ...t, progress: pct } : t));
                });
                results.push(newImg);
                setActiveUploads(prev => prev.map(t => t.id === taskId ? { ...t, status: 'completed', progress: 100 } : t));
                setTimeout(() => setActiveUploads(prev => prev.filter(t => t.id !== taskId)), 3000);
            } catch (error) {
                setActiveUploads(prev => prev.map(t => t.id === taskId ? { ...t, status: 'error', error: error.message } : t));
                setTimeout(() => setActiveUploads(prev => prev.filter(t => t.id !== taskId)), 10000);
            }
        }
        if (results.length > 0) {
            setImages(prev => [...results, ...prev]);
            // Không gọi loadProductionData ngay lập tức để tránh cache cũ đè lên state mới
            // loadProductionData('library', true, true);
        }
    } else {
        // Mock implementation for dev
        const newImages = files.map(file => ({
            id: `id_${Date.now()}_${Math.random()}`,
            name: file.name,
            url: URL.createObjectURL(file),
            createdAt: new Date().toISOString(),
            source: 'Thư viện'
        }));
        setImages(prev => [...newImages, ...prev]);
    }
  };

  const handleVideoUploads = async (files: File[]) => {
    if (!user) return;
    if (APP_MODE === 'PRODUCTION') {
        const results = [];
        for (const file of files) {
            const taskId = `upload_${Date.now()}_${Math.random()}`;
            setActiveUploads(prev => [...prev, { id: taskId, fileName: file.name, progress: 0, status: 'uploading', type: 'video' }]);

            try {
                const newVid = await uploadLibraryVideo(file, (pct) => {
                    setActiveUploads(prev => prev.map(t => t.id === taskId ? { ...t, progress: pct } : t));
                });
                results.push(newVid);
                setActiveUploads(prev => prev.map(t => t.id === taskId ? { ...t, status: 'completed', progress: 100 } : t));
                setTimeout(() => setActiveUploads(prev => prev.filter(t => t.id !== taskId)), 3000);
            } catch (error) {
                setActiveUploads(prev => prev.map(t => t.id === taskId ? { ...t, status: 'error', error: error.message } : t));
                setTimeout(() => setActiveUploads(prev => prev.filter(t => t.id !== taskId)), 10000);
            }
        }
        if (results.length > 0) {
            setVideoResources(prev => [...results, ...prev]);
            // loadProductionData('library', true, true);
        }
    } else {
        // Mock
        const newVideos = files.map(file => ({
            id: `id_${Date.now()}_${Math.random()}`,
            name: file.name,
            uri: URL.createObjectURL(file),
            status: 'completed',
            createdAt: new Date().toISOString(),
            source: 'Thư viện'
        }));
        setVideoResources(prev => [...newVideos, ...prev]);
    }
  };

  const handleCombineAndSegmentDocuments = async (docIds: string[], newName: string) => {
    if (APP_MODE === 'PRODUCTION') {
        try {
            // Backend đã làm cả 2 việc trong 1 round-trip: ghép markdown + phân
            // mảnh bằng MarkdownSegmenter (free). Doc trả về luôn có `segments`
            // và `status='ready'` nên ta nhảy thẳng vào kết quả phân đoạn,
            // không cần bước "Bắt đầu phân tích" thứ hai.
            const combinedDoc = await combineLibraryDocuments(docIds, newName);
            setDocuments(prev => [combinedDoc, ...prev]);
            const hasReadySegments = Array.isArray(combinedDoc?.segments) && combinedDoc.segments.length > 0;
            handleNavigate(View.DocumentWorkspace, {
                id: combinedDoc.id,
                name: combinedDoc.name,
                step: hasReadySegments ? 'result' : 'preview',
            });
        } catch (error) {
            console.error("Failed to combine documents:", error);
            alert("Lỗi khi tổng hợp tài liệu: " + error.message);
        }
        return;
    }
    const docsToCombine = documents.filter(doc => docIds.includes(doc.id));
    if (docsToCombine.length < 2) { alert("Vui lòng chọn ít nhất hai tài liệu để tổng hợp."); return; }
    const combinedContent = docsToCombine.map(doc => `--- NGUỒN: ${doc.name} ---\n\n${doc.content || ''}`).join('\n\n\n');
    const tempCombinedDoc: Document = { id: `id_combined_${Date.now()}`, name: newName, type: 'txt', uploadDate: new Date().toLocaleDateString('vi-VN'), status: 'draft', content: combinedContent, sourceDocumentIds: docIds, segments: [{ title: "Tài liệu đang được phân tích tối ưu...", content: "AI đang làm việc để tối ưu hóa tài liệu cho các chức năng khác, bạn vẫn có thể tiếp tục làm việc." }] };
    setDocuments(prev => [tempCombinedDoc, ...prev]);
    try {
      const { segments } = await segmentDocument(combinedContent, newName);
      const updatedDocWithSegments: Document = { ...tempCombinedDoc, segments };
      handleUpdate(setDocuments)(updatedDocWithSegments);
    } catch (error) {
       console.error("Failed to segment combined document:", error);
       const updatedDocWithError: Document = { ...tempCombinedDoc, segments: [{ title: "Lỗi Phân tích", content: "Đã xảy ra lỗi khi AI phân tích tài liệu. Vui lòng thử lại." }] };
       handleUpdate(setDocuments)(updatedDocWithError);
    }
  };

  const handleSyncAction = useCallback(async (action: any) => {
    if (APP_MODE !== 'PRODUCTION') return true;
    try {
        const result = await syncResource(action.resource, action.method, action.data, action.data?.id);
        return !!result;
    } catch (error) {
        console.error("Sync action failed in App.tsx", error);
        return false;
    }
  }, []);

  const refreshLibraryImages = useCallback(() => {
    loadProductionData('library', true, true);
  }, [loadProductionData]);

  /**
   * Force-refresh danh sách Document từ backend.
   * Dùng cho các luồng tạo Document mới động (vd: LessonPlanCreator → "Lưu thành tài liệu"
   * từ ảnh SGK đã phân tích) — sau khi backend trả về doc mới, FE cần list cập nhật ngay
   * để mọi tab/tính năng khác (Document Library, Slide Creator, Quiz Creator…) thấy.
   */
  const refreshDocuments = useCallback(() => {
    loadProductionData('documents', true, true);
  }, [loadProductionData]);

  const renderRoutes = () => {
    const commonProps = { onBack: handleBack, onNavigate: handleNavigate, onGoDashboard: () => handleNavigate(activeRoleConfig.dashboardPath), onShowToast: addToast, onRefreshData: () => loadProductionData('library', true, true) };
    const creatorProps = { ...commonProps, userSettings: displayUser!, accountUserSettings: user!, displayUser: displayUser, onFilesSelected: handleFileUploads, onAddNotification: handleAddNotification, onCreditUsage: handleCreditUsage };
    // Dữ liệu + handler THẬT cho các trang Workspace học sinh (cùng nguồn với
    // StudentDashboard cũ) — tái dùng state đã nạp sẵn, không nạp lại.
    const studentWorkspaceProps = {
      user: displayUser,
      onNavigate: handleNavigate,
      onShowToast: addToast,
      student: user,
      joinRequests,
      // Học sinh dùng tập lộ trình ĐỒNG NHẤT (của mình + phụ huynh tạo cho con).
      learningPlans: studentUnifiedPlans,
      assignments,
      homeworks,
      submissions,
      classes,
      students,
      notifications,
      learningHistory,
      onSavePlan: (plan: any) => {
        const created = handleSave(setChildLearningPlans, 'learning-plans')(plan);
        if (created) setStudentUnifiedPlans(prev => [created as ChildLearningPlan, ...prev]);
        return created;
      },
      onDeletePlan: (id: string) => {
        handleDelete(setChildLearningPlans, 'learning-plans')(id);
        setStudentUnifiedPlans(prev => prev.filter(p => String(p.id) !== String(id)));
      },
      onStudentSubmit: handleStudentSubmit,
      onUpdateSettings: handleUpdateUserSettings,
      onRefreshData: () => { loadProductionData('core,student,library,learningPlans,learningHistory', true); refreshUnifiedPlans(); },
      onAddNotification: handleAddNotification,
      onCreditUsage: handleCreditUsage,
      isLoading: isLoadingData,
    };
    const navState = (location.state as any) || {};

    return (
      <Routes>
        {/* Landing công khai khoe thành tích con (viral) — không cần đăng nhập */}
        <Route path="/khoe/:token" element={<SharedAchievementPage />} />
        {/* Landing công khai lời mời (vòng mời chéo) — không cần đăng nhập */}
        <Route path="/moi/:token" element={<InvitePage />} />
        <Route path="/chia-se/:resource/:slugWithId.html" element={<SharedResourceView user={displayUser} onBack={() => navigate('/thu-vien-cong-dong')} onNavigate={handleNavigate} />} />
        <Route path="/chia-se/:resource/:slugWithId" element={<LegacyHtmlDetailRedirect basePath="/chia-se" fallbackPath="/thu-vien-cong-dong" />} />
        
        <Route path="/thu-vien-cong-dong" element={<SharedLibrary user={displayUser} onBack={() => navigate('/')} onNavigate={handleNavigate} />} />
        
        <Route path="/thu-vien-cong-dong/:category" element={<CategoryDetailPage user={displayUser} onBack={() => navigate('/thu-vien-cong-dong')} onNavigate={handleNavigate} />} />
        
        {/* Live Quiz — trang người chơi (public, vào bằng điện thoại) */}
        <Route path="/choi" element={<LiveQuizPlayer />} />
        {/* Hộp công cụ lớp học — public, không cần đăng nhập, mở là dùng ngay */}
        <Route path="/hop-cong-cu-lop" element={<ClassroomToolbox onBack={handleBack} onNavigate={handleNavigate} user={displayUser} />} />
        <Route path="/tham-gia-lop-hoc" element={<JoinClassPage user={displayUser} classes={classes} onNavigate={handleNavigate} />} />
        <Route path="/tham-gia-lop" element={<JoinClassRouteGuard user={displayUser} joinRequests={joinRequests} onLoadData={() => loadProductionData('core,joinRequests', true)} onNavigate={handleNavigate} />} />
        {/* Lớp nhiều giáo viên: GV đã đăng nhập nhận lời mời làm đồng GV (GVCN/GV bộ môn) qua link + token */}
        <Route path="/tham-gia-lop-giao-vien" element={<TeacherJoinInvitationPage user={displayUser} onNavigate={handleNavigate} />} />
        {/* Trường học (School Organization — Phase 5.0): mời hàng loạt GV, nền cho credit pool/dashboard/bulk-import */}
        <Route path="/tham-gia-truong" element={<SchoolJoinInvitationPage user={displayUser} onNavigate={handleNavigate} />} />
        <Route path="/quan-ly-truong-hoc" element={user ? <SchoolManagement user={displayUser} onNavigate={handleNavigate} onBack={handleBack} /> : <LoginRedirect />} />
        <Route path="/dang-nhap" element={<Login onLogin={handleLogin} onBack={handleBack} onNavigate={handleNavigate} isLoadingData={isLoadingData} />} />
        <Route path="/chon-vai-tro" element={user ? <RoleSelection user={displayUser} onUpdateSettings={handleUpdateUserSettings} onNavigate={handleNavigate} /> : <LoginRedirect />} />
        <Route path="/dieu-khoan" element={<TermsOfUse {...commonProps} />} />
        <Route path="/chinh-sach-bao-mat" element={<PrivacyPolicy {...commonProps} />} />
        {/* Hướng dẫn: hub liệt kê + trang chi tiết từng chức năng (public, SEO) */}
        <Route path="/huong-dan" element={<GuideHub />} />
        <Route path="/huong-dan/:slug" element={<GuideDetail />} />
        <Route path="/gioi-thieu" element={<UserGuide onNavigate={handleNavigate} onBack={handleBack} isIntroOnly={true} />} />
        <Route path="/sap-ra-mat" element={<ComingSoon onBack={handleBack} />} />

        {/* Protected Routes */}
        {/* Quiz Thẻ Giấy — màn điều khiển/quét: public, vào bằng mã PIN, không cần
            đăng nhập (điện thoại quét có thể là bất kỳ máy nào, kể cả mở trong
            webview Zalo/Facebook chặn đăng nhập Google) */}
        <Route path="/quiz-the-giay/dieu-khien" element={<QrCardQuizScanner />} />
        <Route path="/so-do-tu-duy" element={user ? <MindmapStudio onNavigate={handleNavigate} onBack={handleBack} onShowToast={addToast} /> : <LoginRedirect />} />
        <Route path="/cua-hang-credit" element={user && user.authType !== 'student_code' ? <CreditStore {...commonProps} user={displayUser} onPurchase={handlePurchasePackage} onShowToast={(t) => addToast(t.message, t.type)} onRefreshData={() => loadProductionData('core', true)} /> : user?.authType === 'student_code' ? <Navigate to="/workspace/hoc-sinh" replace /> : <LoginRedirect />} />
        <Route path="/dac-quyen-premium" element={<PremiumBenefits onBack={handleBack} onNavigate={handleNavigate} />} />

        {/* === Deep Workspace (Phase 0) — không gian đa role, có AI Panel thường trực === */}
        {/* Root redirect → Tổng quan (landing sau login). CanvasManager tại /bai-giang. */}
        <Route path="/workspace/giao-vien" element={user ? <Navigate to="/workspace/giao-vien/tong-quan" replace /> : <LoginRedirect />} />
        <Route path="/workspace/giao-vien/tong-quan" element={user ? <TeacherWorkspacePage user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} section="overview" /> : <LoginRedirect />} />
        <Route path="/workspace/giao-vien/bai-giang" element={user ? <TeacherWorkspacePage user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} /> : <LoginRedirect />} />
        <Route path="/workspace/giao-vien/ngan-hang-cau-hoi" element={user ? <TeacherWorkspacePage user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} section="question-bank" /> : <LoginRedirect />} />
        <Route path="/workspace/giao-vien/phan-tich" element={user ? <TeacherWorkspacePage user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} section="analytics" /> : <LoginRedirect />} />
        <Route path="/workspace/giao-vien/so-diem" element={user ? <TeacherWorkspacePage user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} section="grade-book" /> : <LoginRedirect />} />
        <Route path="/workspace/giao-vien/diem-danh" element={user ? <TeacherWorkspacePage user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} section="attendance" /> : <LoginRedirect />} />
        <Route path="/workspace/giao-vien/bao-cao-phu-huynh" element={user ? <TeacherWorkspacePage user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} section="parent-report" /> : <LoginRedirect />} />
        <Route path="/workspace/giao-vien/lich-day" element={user ? <TeacherWorkspacePage user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} section="schedule" /> : <LoginRedirect />} />
        <Route path="/workspace/giao-vien/thu-cung" element={user ? <TeacherWorkspacePage user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} section="pet-classroom" /> : <LoginRedirect />} />
        <Route path="/workspace/giao-vien/:canvasId" element={user ? <TeacherWorkspacePage user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} /> : <LoginRedirect />} />
        <Route path="/workspace/hoc-sinh/lich-hoc"  element={user ? <StudentSchedulePage   {...studentWorkspaceProps} /> : <LoginRedirect />} />
        <Route path="/workspace/hoc-sinh/muc-tieu" element={user ? <StudentGoalsTasksPage {...studentWorkspaceProps} /> : <LoginRedirect />} />
        <Route path="/workspace/hoc-sinh/tien-do"  element={user ? <StudentProgressPage   {...studentWorkspaceProps} /> : <LoginRedirect />} />
        <Route path="/workspace/hoc-sinh/gia-su"   element={user ? <StudentTutorPage      {...studentWorkspaceProps} /> : <LoginRedirect />} />
        <Route path="/workspace/hoc-sinh/lo-trinh" element={user ? <StudentLearningPathPage {...studentWorkspaceProps} /> : <LoginRedirect />} />
        <Route path="/workspace/hoc-sinh/luyen-de" element={user ? <StudentPracticePage {...studentWorkspaceProps} /> : <LoginRedirect />} />
        <Route path="/workspace/hoc-sinh" element={user ? <StudentWorkspacePage {...studentWorkspaceProps} /> : <LoginRedirect />} />
        <Route path="/workspace/phu-huynh/tien-do"   element={user ? <ParentProgressPage  user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} /> : <LoginRedirect />} />
        <Route path="/workspace/phu-huynh/ket-qua"  element={user ? <ParentGradesPage    user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} /> : <LoginRedirect />} />
        <Route path="/workspace/phu-huynh/hoat-dong"element={user ? <ParentActivityPage  user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} /> : <LoginRedirect />} />
        <Route path="/workspace/phu-huynh/canh-bao" element={user ? <ParentAlertsPage    user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} /> : <LoginRedirect />} />
        <Route path="/workspace/phu-huynh/ke-hoach"     element={user ? <ParentPlanPage      user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} /> : <LoginRedirect />} />
        <Route path="/workspace/phu-huynh/huong-nghiep" element={user ? <ParentCareerPage    user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} /> : <LoginRedirect />} />
        <Route path="/workspace/phu-huynh/quan-ly-con"  element={user ? <ParentChildrenPage  user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} /> : <LoginRedirect />} />
        <Route path="/workspace/phu-huynh/bai-tap"      element={user ? <ParentAssignmentsPage user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} /> : <LoginRedirect />} />
        <Route path="/workspace/phu-huynh/lo-trinh"     element={user ? <ParentLearningPathPage user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} /> : <LoginRedirect />} />
        <Route path="/workspace/phu-huynh" element={user ? <ParentWorkspacePage user={displayUser} onNavigate={handleNavigate} onShowToast={addToast} /> : <LoginRedirect />} />
        <Route path="/affiliate-dashboard" element={user ? <AffiliateDashboard user={displayUser} onBack={handleBack} onNavigate={handleNavigate} onShowToast={(t) => addToast(t.message, t.type)} /> : <LoginRedirect />} />
        <Route path="/affiliate-org" element={user ? <AffiliateOrgDashboard user={displayUser} onBack={handleBack} onNavigate={handleNavigate} onShowToast={(t) => addToast(t.message, t.type)} /> : <LoginRedirect />} />
        <Route path="/affiliate-org/apply" element={user ? <AffiliateOrgDashboard user={displayUser} mode="apply" onBack={handleBack} onNavigate={handleNavigate} onShowToast={(t) => addToast(t.message, t.type)} /> : <LoginRedirect />} />
        <Route path="/affiliate-org/join/:token" element={<AffiliateOrgJoin user={displayUser} onShowToast={(t) => addToast(t.message, t.type)} />} />
        <Route path="/affiliate-org/:orgId" element={user ? <AffiliateOrgDashboardRouteWrapper user={displayUser} onBack={handleBack} onNavigate={handleNavigate} onShowToast={(t) => addToast(t.message, t.type)} /> : <LoginRedirect />} />
        <Route path="/luyen-phat-am" element={user ? <PronunciationGuide {...creatorProps} user={displayUser} onUpdateSettings={handleUpdateUserSettings} /> : <LoginRedirect />} />
        <Route path="/hoc-ve" element={FEATURE_FLAGS.IMAGE_GENERATION === false ? <Navigate to="/" replace /> : (user ? <DrawingStudio {...creatorProps} drawingLessons={drawingLessons} classes={classes} onSaveDrawingLesson={handleSave(setDrawingLessons, 'drawing-lessons')} onUpdateDrawingLesson={handleUpdate(setDrawingLessons, 'drawing-lessons')} onSaveImage={handleSave(setImages, 'images')} /> : <LoginRedirect />)} />
        <Route path="/ke-chuyen-lich-su" element={FEATURE_FLAGS.HISTORY_ASSISTANT === false ? <Navigate to="/" replace /> : (user ? <HistoryStoryteller {...creatorProps} /> : <LoginRedirect />)} />
        <Route path="/tro-ly-van-hoc" element={FEATURE_FLAGS.LITERATURE_ASSISTANT === false ? <Navigate to="/" replace /> : (user ? <LiteratureAssistant {...creatorProps} /> : <LoginRedirect />)} />
        <Route path="/giai-thich-khoa-hoc" element={FEATURE_FLAGS.SCIENCE_ASSISTANT === false ? <Navigate to="/" replace /> : (user ? <ScienceExplainer {...creatorProps} /> : <LoginRedirect />)} />
        
        <Route path="/bai-hoc/:planSlugWithId/:moduleSlugWithId" element={user ? <LearningModuleViewRouteWrapper {...commonProps} navigationPayload={navigationPayload} onComplete={handleCompleteModule} learningPlans={displayUser?.role === 'student' ? [...studentUnifiedPlans, ...childLearningPlans] : childLearningPlans} planBackPath={displayUser?.role === 'parent' ? '/workspace/phu-huynh/lo-trinh' : displayUser?.role === 'student' ? '/workspace/hoc-sinh/lo-trinh' : undefined} /> : <LoginRedirect />} />
        
        <Route path="/bang-hoc-cua-con/:slugWithId?" element={
          <ChildDashboardRouteWrapper 
            user={displayUser}
            children={children}
            navigationPayload={navigationPayload}
            creatorProps={creatorProps}
            assignments={assignments}
            homeworks={homeworks}
            submissions={submissions}
            classes={classes}
            students={students}
            notifications={notifications}
            joinRequests={joinRequests}
            childLearningPlans={childLearningPlans}
            learningHistory={learningHistory}
            onSavePlan={handleSave(setChildLearningPlans, 'learning-plans')} 
            onUpdatePlan={handleUpdate(setChildLearningPlans, 'learning-plans')} 
            onDeletePlan={handleDelete(setChildLearningPlans, 'learning-plans')} 
            onStudentSubmit={handleStudentSubmit}
            onNotificationRead={handleMarkAsRead}
            isLoadingData={isLoadingData}
            resourceLoadingStates={resourceLoadingStates}
          />
        } />
        
        <Route path="/quan-ly-lop-hoc/:slugWithId?" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.ClassManagement} 
          Component={ClassManagementRouteWrapper}
          creatorProps={creatorProps}
          navState={navState}
          user={displayUser} 
          classes={classes} 
          students={students} 
          homeworks={homeworks} 
          assignments={assignments} 
          submissions={submissions} 
          joinRequests={joinRequests}
          onSaveClass={handleSaveClass} 
          onUpdateClass={handleUpdate(setClasses, 'classes')} 
          onDeleteClass={handleDelete(setClasses, 'classes')} 
          onSaveStudent={handleSaveStudent} 
          onSaveMultipleStudents={handleSaveMultipleStudents} 
          onUpdateStudent={handleUpdate(setStudents, 'students')} 
          onDeleteStudent={handleDelete(setStudents, 'students')} 
          onAssignHomework={handleAssignHomework} 
          onDeleteAssignment={handleDeleteAssignment}
          onGradeSubmission={handleGradeSubmission} 
          navigationPayload={navigationPayload} 
          onRefreshData={() => loadProductionData('core,teacher', true, true)}
          onRefreshJoinRequests={() => loadProductionData('joinRequests', true, true)}
          onRefreshClassUpdates={loadClassUpdates} 
          onUpdateSettings={handleUpdateUserSettings as React.Dispatch<React.SetStateAction<UserSettings | null>>}
          isLoading={isLoadingData} 
        /> : <LoginRedirect />} />
        
        <Route path="/tao-giao-an/:slugWithId?" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.LessonPlanCreator} Component={LessonPlanCreator} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onRefreshDocuments={refreshDocuments} onSave={handleSave(setLessonPlans, 'lesson-plans')} onUpdate={handleUpdate(setLessonPlans, 'lesson-plans')} onUpdateSettings={handleUpdateUserSettings as React.Dispatch<React.SetStateAction<UserSettings | null>>} onSaveImage={handleSave(setImages, 'images')} /> : <LoginRedirect />} />
        
        <Route path="/tao-de-kiem-tra/:slugWithId?" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.QuizCreator} Component={QuizCreator} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} images={images} children={children} classes={classes} students={students} onRefreshLibraryImages={refreshLibraryImages} onSave={handleSave(setQuizzes, 'quizzes')} onUpdate={handleUpdate(setQuizzes, 'quizzes')} onUpdateSettings={handleUpdateUserSettings as React.Dispatch<React.SetStateAction<UserSettings | null>>} onCreditUsage={handleCreditUsage} /> : <LoginRedirect />} />
        {/* Alias học sinh — cùng element, chỉ khác URL để nằm trong họ /workspace/hoc-sinh/luyen-de/* */}
        <Route path="/workspace/hoc-sinh/luyen-de/tao-de/:slugWithId?" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.QuizCreator} Component={QuizCreator} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} images={images} children={children} classes={classes} students={students} onRefreshLibraryImages={refreshLibraryImages} onSave={handleSave(setQuizzes, 'quizzes')} onUpdate={handleUpdate(setQuizzes, 'quizzes')} onUpdateSettings={handleUpdateUserSettings as React.Dispatch<React.SetStateAction<UserSettings | null>>} onCreditUsage={handleCreditUsage} /> : <LoginRedirect />} />

        <Route path="/tao-tro-choi/:slugWithId?" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.GameCreator} Component={GameCreator} creatorProps={creatorProps} navState={navState} games={games} onSave={handleSave(setGames, 'games')} onUpdate={handleUpdate(setGames, 'games')} onUpdateSettings={handleUpdateUserSettings as React.Dispatch<React.SetStateAction<UserSettings | null>>} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} homeworks={homeworks} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onRefreshDocuments={refreshDocuments} onCreditUsage={handleCreditUsage} isPlaying={navState.isPlaying} /> : <LoginRedirect />} />
        
        <Route path="/tao-trinh-chieu/:slugWithId?" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.SlideCreator} Component={SlideCreator} creatorProps={creatorProps} navState={navState} images={images} onRefreshLibraryImages={refreshLibraryImages} onRefreshDocuments={refreshDocuments} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} onSave={handleSave(setPresentations, 'presentations')} onUpdate={handleUpdate(setPresentations, 'presentations')} onUpdateSettings={handleUpdateUserSettings as React.Dispatch<React.SetStateAction<UserSettings | null>>} onSaveImage={handleSave(setImages, 'images')} onCreditUsage={handleCreditUsage} /> : <LoginRedirect />} />
        
        <Route path="/tao-bai-tap/:slugWithId?" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.HomeworkCreator} Component={HomeworkCreator} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} homeworks={homeworks} quizzes={quizzes} games={games} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onSave={handleSave(setHomeworks, 'homeworks')} onUpdate={handleUpdate(setHomeworks, 'homeworks')} onUpdateSettings={handleUpdateUserSettings as React.Dispatch<React.SetStateAction<UserSettings | null>>} onCreditUsage={handleCreditUsage} onSaveImage={handleSave(setImages, 'images')} /> : <LoginRedirect />} />
        
        <Route path="/hoat-dong-sang-tao/:slugWithId?" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.ActivityCreator} Component={ActivityCreator} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} homeworks={homeworks} quizzes={quizzes} games={games} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onRefreshDocuments={refreshDocuments} onSave={handleSave(setActivities, 'activities')} onUpdate={handleUpdate(setActivities, 'activities')} onUpdateSettings={handleUpdateUserSettings as React.Dispatch<React.SetStateAction<UserSettings | null>>} onCreditUsage={handleCreditUsage} lastActivity={lastActivity} onSetLastActivity={setLastActivity} /> : <LoginRedirect />} />
        
        <Route path="/phong-lab-ai" element={user ? <AILab onBack={handleBack} /> : <LoginRedirect />} />
        {/* SEO friendly URL khi xem/chỉnh sửa 1 phiên cụ thể: /phong-lab-ai/{slug}-i{id}.html[/edit] */}
        <Route path="/phong-lab-ai/:slugWithId.html" element={user ? <AILab onBack={handleBack} /> : <LoginRedirect />} />
        <Route path="/phong-lab-ai/:slugWithId.html/edit" element={user ? <AILab onBack={handleBack} /> : <LoginRedirect />} />
        <Route path="/thu-thach-dao-duc-ai" element={user ? <AIEthics onBack={handleBack} /> : <LoginRedirect />} />
        {/* SEO friendly URL khi xem/chỉnh sửa 1 phiên cụ thể: /thu-thach-dao-duc-ai/{slug}-i{id}.html[/edit] */}
        <Route path="/thu-thach-dao-duc-ai/:slugWithId.html" element={user ? <AIEthics onBack={handleBack} /> : <LoginRedirect />} />
        <Route path="/thu-thach-dao-duc-ai/:slugWithId.html/edit" element={user ? <AIEthics onBack={handleBack} /> : <LoginRedirect />} />
        <Route path="/kiem-chung-thong-tin-ai" element={user ? <AIFactCheck onBack={handleBack} /> : <LoginRedirect />} />

        {/* Hành trình khám phá - 4 audience layers (Primary, Secondary, HighSchool, University) */}
        {/* `:slugWithId` capture cả `.html` (URL cũ leaf) lẫn không (.html ở leaf con) */}
        <Route path="/hanh-trinh-kham-pha" element={user ? <CareerJourney onBack={handleBack} childrenList={children} onRefreshParentData={handleRefreshChildren} /> : <LoginRedirect />} />
        <Route path="/hanh-trinh-kham-pha/ho-so/moi" element={user ? <CareerJourney onBack={handleBack} childrenList={children} onRefreshParentData={handleRefreshChildren} /> : <LoginRedirect />} />
        <Route path="/hanh-trinh-kham-pha/ho-so/:slugWithId" element={user ? <CareerJourney onBack={handleBack} childrenList={children} onRefreshParentData={handleRefreshChildren} /> : <LoginRedirect />} />
        {/* Dashboard (leaf): `{slug}-i{id}.html` */}
        <Route path="/hanh-trinh-kham-pha/:slugWithId" element={user ? <CareerJourney onBack={handleBack} childrenList={children} onRefreshParentData={handleRefreshChildren} /> : <LoginRedirect />} />
        {/* Career Explorer (leaf): URL mới `kham-pha.html` + URL cũ `kham-pha` (back-compat) */}
        <Route path="/hanh-trinh-kham-pha/:slugWithId/kham-pha.html" element={user ? <CareerJourney onBack={handleBack} childrenList={children} onRefreshParentData={handleRefreshChildren} /> : <LoginRedirect />} />
        <Route path="/hanh-trinh-kham-pha/:slugWithId/kham-pha" element={user ? <CareerJourney onBack={handleBack} childrenList={children} onRefreshParentData={handleRefreshChildren} /> : <LoginRedirect />} />
        {/* Khám phá bản thân (Self-discovery / RIASEC) - leaf section theo profile */}
        <Route path="/hanh-trinh-kham-pha/:slugWithId/kham-pha-ban-than.html" element={user ? <CareerJourney onBack={handleBack} childrenList={children} onRefreshParentData={handleRefreshChildren} /> : <LoginRedirect />} />
        <Route path="/hanh-trinh-kham-pha/:slugWithId/kham-pha-ban-than" element={user ? <CareerJourney onBack={handleBack} childrenList={children} onRefreshParentData={handleRefreshChildren} /> : <LoginRedirect />} />
        {/* Career Detail (leaf): `nghe-slug/slug-profile.html` (mới - thân thiện SEO) */}
        <Route path="/hanh-trinh-kham-pha/:careerSlug/:slugWithId" element={user ? <CareerJourney onBack={handleBack} childrenList={children} onRefreshParentData={handleRefreshChildren} /> : <LoginRedirect />} />
        {/* Career Detail (old back-compat): `slug-profile/nghe/careerCode.html` */}
        <Route path="/hanh-trinh-kham-pha/:slugWithId/nghe/:careerCode" element={user ? <CareerJourney onBack={handleBack} childrenList={children} onRefreshParentData={handleRefreshChildren} /> : <LoginRedirect />} />

        {/* Backward compat: /phong-thi-nghiem-ai (old) -> /phong-lab-ai (new) */}
        <Route path="/phong-thi-nghiem-ai" element={<Navigate to="/phong-lab-ai" replace />} />
        <Route path="/ai-lab" element={<Navigate to="/phong-lab-ai" replace />} />
        <Route path="/ai-ethics" element={<Navigate to="/thu-thach-dao-duc-ai" replace />} />
        <Route path="/ai-fact-check" element={<Navigate to="/kiem-chung-thong-tin-ai" replace />} />
        <Route path="/career-journey" element={<Navigate to="/hanh-trinh-kham-pha" replace />} />
        
        <Route path="/kiem-tra-kien-thuc/tao" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.OralQuizCreator} Component={OralQuizCreator} creatorProps={creatorProps} navState={navState} oralQuizzes={oralQuizzes} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onRefreshDocuments={refreshDocuments} onSave={handleSave(setOralQuizzes, 'oral-quizzes')} onUpdate={handleUpdate(setOralQuizzes, 'oral-quizzes')} onCreditUsage={handleCreditUsage} onFilesSelected={handleFileUploads} onAddNotification={handleAddNotification} /> : <LoginRedirect />} />
        <Route path="/mo-phong-khoa-hoc/tao" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.ScientificSimulationCreator} Component={ScientificSimulationPage} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onFilesSelected={handleFileUploads} /> : <LoginRedirect />} />
        <Route path="/mo-phong-khoa-hoc/:slugWithId.html" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.ScientificSimulationView} Component={ScientificSimulationPage} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onFilesSelected={handleFileUploads} /> : <LoginRedirect />} />
        <Route path="/mo-phong-khoa-hoc/:slugWithId.html/edit" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.ScientificSimulationEdit} Component={ScientificSimulationPage} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onFilesSelected={handleFileUploads} /> : <LoginRedirect />} />
        
        {/* Thiết kế ứng dụng 3D routes */}
        <Route path="/thiet-ke-3d/tao" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.ThietKe3DCreator} Component={ThietKe3DPage} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} onFilesSelected={handleFileUploads} /> : <LoginRedirect />} />
        <Route path="/thiet-ke-3d/:slugWithId.html" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.ThietKe3DView} Component={ThietKe3DPage} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} onFilesSelected={handleFileUploads} /> : <LoginRedirect />} />
        <Route path="/thiet-ke-3d/:slugWithId.html/edit" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.ThietKe3DEdit} Component={ThietKe3DPage} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} onFilesSelected={handleFileUploads} /> : <LoginRedirect />} />
        <Route path="/video-tuong-tac/:slugWithId.html" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.InteractiveVideo} Component={InteractiveVideoPage} creatorProps={creatorProps} navState={navState} userSettings={displayUser} onCreditUsage={handleCreditUsage} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} classes={classes} students={students} onAssignHomework={handleAssignHomework} onStudentSubmit={handleStudentSubmit} submissions={submissions} onFilesSelected={handleFileUploads} isEditModeFromUrl={false} /> : <LoginRedirect />} />
        <Route path="/video-tuong-tac/:slugWithId.html/edit" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.InteractiveVideoEdit} Component={InteractiveVideoPage} creatorProps={creatorProps} navState={navState} userSettings={displayUser} onCreditUsage={handleCreditUsage} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} classes={classes} students={students} onAssignHomework={handleAssignHomework} onStudentSubmit={handleStudentSubmit} submissions={submissions} onFilesSelected={handleFileUploads} isEditModeFromUrl={true} /> : <LoginRedirect />} />
        <Route path="/video-tuong-tac/:slugWithId?" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.InteractiveVideo} Component={InteractiveVideoPage} creatorProps={creatorProps} navState={navState} userSettings={displayUser} onCreditUsage={handleCreditUsage} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} classes={classes} students={students} onAssignHomework={handleAssignHomework} onStudentSubmit={handleStudentSubmit} submissions={submissions} onFilesSelected={handleFileUploads} /> : <LoginRedirect />} />
        <Route path="/thiet-ke-3d/:slugWithId.html/xem" element={<ThietKe3DPlayer />} />
        <Route path="/mo-phong-khoa-hoc/:slugWithId/edit" element={<LegacyScientificSimulationRedirect />} />
        <Route path="/mo-phong-khoa-hoc/:slugWithId" element={<LegacyScientificSimulationRedirect />} />
        
        <Route path="/kiem-tra-kien-thuc/chay/:slugWithId?" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.OralQuizRunner} Component={OralQuizRunner} creatorProps={creatorProps} navState={navState} oralQuizzes={oralQuizzes} oralQuizSessions={oralQuizSessions} classes={classes} students={students} onSaveSession={handleSave(setOralQuizSessions, 'oral-quiz-sessions')} onUpdateSession={handleUpdate(setOralQuizSessions, 'oral-quiz-sessions')} /> : <LoginRedirect />} />

        {/* Phase 2 — S2: làm đề online + AI chấm */}
        <Route path="/lam-de/:slugWithId?" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.OnlineQuizTaker} Component={OnlineQuizTaker} creatorProps={creatorProps} navState={navState} /> : <LoginRedirect />} />
        <Route path="/workspace/hoc-sinh/luyen-de/lam-bai/:slugWithId?" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.OnlineQuizTaker} Component={OnlineQuizTaker} creatorProps={creatorProps} navState={navState} /> : <LoginRedirect />} />
        <Route path="/ket-qua-de/:attemptId.html?" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.QuizAttemptResult} Component={QuizAttemptResult} creatorProps={creatorProps} navState={navState} /> : <LoginRedirect />} />
        <Route path="/workspace/hoc-sinh/luyen-de/ket-qua/:attemptId?" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.QuizAttemptResult} Component={QuizAttemptResult} creatorProps={creatorProps} navState={navState} /> : <LoginRedirect />} />
        <Route path="/ket-qua-de/:attemptId" element={<LegacyHtmlDetailRedirect basePath="/ket-qua-de" fallbackPath="/workspace/hoc-sinh" />} />
        <Route path="/kiem-tra-kien-thuc/:slugWithId.html" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.OralQuizView} Component={OralQuizCreator} creatorProps={creatorProps} navState={navState} oralQuizzes={oralQuizzes} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onRefreshDocuments={refreshDocuments} onSave={handleSave(setOralQuizzes, 'oral-quizzes')} onUpdate={handleUpdate(setOralQuizzes, 'oral-quizzes')} onCreditUsage={handleCreditUsage} onFilesSelected={handleFileUploads} onAddNotification={handleAddNotification} isEditModeFromUrl={false} /> : <LoginRedirect />} />
        <Route path="/kiem-tra-kien-thuc/:slugWithId.html/edit" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.OralQuizEdit} Component={OralQuizCreator} creatorProps={creatorProps} navState={navState} oralQuizzes={oralQuizzes} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onRefreshDocuments={refreshDocuments} onSave={handleSave(setOralQuizzes, 'oral-quizzes')} onUpdate={handleUpdate(setOralQuizzes, 'oral-quizzes')} onCreditUsage={handleCreditUsage} onFilesSelected={handleFileUploads} onAddNotification={handleAddNotification} isEditModeFromUrl={true} /> : <LoginRedirect />} />
        <Route path="/kiem-tra-kien-thuc/:slugWithId/edit" element={<LegacyHtmlDetailRedirect basePath="/kiem-tra-kien-thuc" fallbackPath="/kiem-tra-kien-thuc/tao" />} />
        <Route path="/kiem-tra-kien-thuc/:slugWithId" element={<LegacyHtmlDetailRedirect basePath="/kiem-tra-kien-thuc" fallbackPath="/kiem-tra-kien-thuc/tao" />} />

        <Route path="/kiem-tra-mieng/tao" element={<LegacyOralQuizRedirect />} />
        <Route path="/kiem-tra-mieng/chay/:slugWithId?" element={<LegacyOralQuizRedirect />} />
        <Route path="/kiem-tra-mieng/:slugWithId/edit" element={<LegacyOralQuizRedirect />} />
        <Route path="/kiem-tra-mieng/:slugWithId" element={<LegacyOralQuizRedirect />} />
        
        {/* --- Resource View Routes (Existing Content) --- */}
        <Route path="/giao-an/:slugWithId.html" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.LessonPlanView} Component={LessonPlanCreator} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onRefreshDocuments={refreshDocuments} onSave={handleSave(setLessonPlans, 'lesson-plans')} onUpdate={handleUpdate(setLessonPlans, 'lesson-plans')} onSaveImage={handleSave(setImages, 'images')} /> : <LoginRedirect />} />
        <Route path="/giao-an/:slugWithId.html/edit" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.LessonPlanEdit} Component={LessonPlanCreator} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onRefreshDocuments={refreshDocuments} onSave={handleSave(setLessonPlans, 'lesson-plans')} onUpdate={handleUpdate(setLessonPlans, 'lesson-plans')} onSaveImage={handleSave(setImages, 'images')} /> : <LoginRedirect />} />
        <Route path="/giao-an/:slugWithId/edit" element={<LegacyHtmlDetailRedirect basePath="/giao-an" fallbackPath="/tao-giao-an" />} />
        <Route path="/giao-an/:slugWithId" element={<LegacyHtmlDetailRedirect basePath="/giao-an" fallbackPath="/tao-giao-an" />} />
        
        <Route path="/de-kiem-tra/:slugWithId.html" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.QuizView} Component={QuizCreator} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} images={images} children={children} classes={classes} students={students} onRefreshLibraryImages={refreshLibraryImages} onSave={handleSave(setQuizzes, 'quizzes')} onUpdate={handleUpdate(setQuizzes, 'quizzes')} onUpdateSettings={handleUpdateUserSettings as React.Dispatch<React.SetStateAction<UserSettings | null>>} onCreditUsage={handleCreditUsage} isEditModeFromUrl={false} /> : <LoginRedirect />} />
        <Route path="/de-kiem-tra/:slugWithId.html/edit" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.QuizEdit} Component={QuizCreator} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} images={images} children={children} classes={classes} students={students} onRefreshLibraryImages={refreshLibraryImages} onSave={handleSave(setQuizzes, 'quizzes')} onUpdate={handleUpdate(setQuizzes, 'quizzes')} onUpdateSettings={handleUpdateUserSettings as React.Dispatch<React.SetStateAction<UserSettings | null>>} onCreditUsage={handleCreditUsage} isEditModeFromUrl={true} /> : <LoginRedirect />} />
        <Route path="/de-kiem-tra/:slugWithId/edit" element={<LegacyHtmlDetailRedirect basePath="/de-kiem-tra" fallbackPath="/tao-de-kiem-tra" />} />
        <Route path="/de-kiem-tra/:slugWithId" element={<LegacyHtmlDetailRedirect basePath="/de-kiem-tra" fallbackPath="/tao-de-kiem-tra" />} />
        
        <Route path="/tro-choi/:slugWithId.html" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.GameView} Component={GameCreator} creatorProps={creatorProps} navState={navState} games={games} onSave={handleSave(setGames, 'games')} onUpdate={handleUpdate(setGames, 'games')} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} homeworks={homeworks} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onRefreshDocuments={refreshDocuments} onCreditUsage={handleCreditUsage} isPlaying={navState.isPlaying ?? true} /> : <LoginRedirect />} />
        <Route path="/tro-choi/:slugWithId.html/edit" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.GameEdit} Component={GameCreator} creatorProps={creatorProps} navState={navState} games={games} onSave={handleSave(setGames, 'games')} onUpdate={handleUpdate(setGames, 'games')} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} homeworks={homeworks} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onRefreshDocuments={refreshDocuments} onCreditUsage={handleCreditUsage} isPlaying={navState.isPlaying} /> : <LoginRedirect />} />
        <Route path="/tro-choi/:slugWithId/edit" element={<LegacyHtmlDetailRedirect basePath="/tro-choi" fallbackPath="/tao-tro-choi" />} />
        <Route path="/tro-choi/:slugWithId" element={<LegacyHtmlDetailRedirect basePath="/tro-choi" fallbackPath="/tao-tro-choi" />} />
        
        <Route path="/trinh-chieu/:slugWithId.html" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.SlideView} Component={SlideCreator} creatorProps={creatorProps} navState={navState} images={images} onRefreshLibraryImages={refreshLibraryImages} onRefreshDocuments={refreshDocuments} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} onSave={handleSave(setPresentations, 'presentations')} onUpdate={handleUpdate(setPresentations, 'presentations')} onUpdateSettings={handleUpdateUserSettings as React.Dispatch<React.SetStateAction<UserSettings>>} onSaveImage={handleSave(setImages, 'images')} onCreditUsage={handleCreditUsage} /> : <LoginRedirect />} />
        <Route path="/trinh-chieu/:slugWithId.html/edit" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.SlideEdit} Component={SlideCreator} creatorProps={creatorProps} navState={navState} images={images} onRefreshLibraryImages={refreshLibraryImages} onRefreshDocuments={refreshDocuments} documents={documents} lessonPlans={lessonPlans} presentations={presentations} quizzes={quizzes} games={games} homeworks={homeworks} activities={activities} onSave={handleSave(setPresentations, 'presentations')} onUpdate={handleUpdate(setPresentations, 'presentations')} onUpdateSettings={handleUpdateUserSettings as React.Dispatch<React.SetStateAction<UserSettings>>} onSaveImage={handleSave(setImages, 'images')} onCreditUsage={handleCreditUsage} /> : <LoginRedirect />} />
        <Route path="/trinh-chieu/:slugWithId/edit" element={<LegacyHtmlDetailRedirect basePath="/trinh-chieu" fallbackPath="/tao-trinh-chieu" />} />
        <Route path="/trinh-chieu/:slugWithId" element={<LegacyHtmlDetailRedirect basePath="/trinh-chieu" fallbackPath="/tao-trinh-chieu" />} />
        
        <Route path="/kho-bai-tap/:slugWithId.html" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.HomeworkView} Component={HomeworkCreator} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} homeworks={homeworks} quizzes={quizzes} games={games} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onSave={handleSave(setHomeworks, 'homeworks')} onUpdate={handleUpdate(setHomeworks, 'homeworks')} onCreditUsage={handleCreditUsage} onSaveImage={handleSave(setImages, 'images')} /> : <LoginRedirect />} />
        <Route path="/kho-bai-tap/:slugWithId.html/edit" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.HomeworkEdit} Component={HomeworkCreator} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} homeworks={homeworks} quizzes={quizzes} games={games} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onSave={handleSave(setHomeworks, 'homeworks')} onUpdate={handleUpdate(setHomeworks, 'homeworks')} onCreditUsage={handleCreditUsage} onSaveImage={handleSave(setImages, 'images')} /> : <LoginRedirect />} />
        <Route path="/kho-bai-tap/:slugWithId/edit" element={<LegacyHtmlDetailRedirect basePath="/kho-bai-tap" fallbackPath="/tao-bai-tap" />} />
        <Route path="/kho-bai-tap/:slugWithId" element={<LegacyHtmlDetailRedirect basePath="/kho-bai-tap" fallbackPath="/tao-bai-tap" />} />
        
        <Route path="/kho-hoat-dong/:slugWithId.html" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.ActivityView} Component={ActivityCreator} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} homeworks={homeworks} quizzes={quizzes} games={games} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onRefreshDocuments={refreshDocuments} onSave={handleSave(setActivities, 'activities')} onUpdate={handleUpdate(setActivities, 'activities')} onCreditUsage={handleCreditUsage} lastActivity={lastActivity} onSetLastActivity={setLastActivity} /> : <LoginRedirect />} />
        <Route path="/kho-hoat-dong/:slugWithId.html/edit" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.ActivityEdit} Component={ActivityCreator} creatorProps={creatorProps} navState={navState} documents={documents} lessonPlans={lessonPlans} presentations={presentations} homeworks={homeworks} quizzes={quizzes} games={games} activities={activities} images={images} onRefreshLibraryImages={refreshLibraryImages} onRefreshDocuments={refreshDocuments} onSave={handleSave(setActivities, 'activities')} onUpdate={handleUpdate(setActivities, 'activities')} onCreditUsage={handleCreditUsage} lastActivity={lastActivity} onSetLastActivity={setLastActivity} /> : <LoginRedirect />} />
        <Route path="/kho-hoat-dong/:slugWithId/edit" element={<LegacyHtmlDetailRedirect basePath="/kho-hoat-dong" fallbackPath="/hoat-dong-sang-tao" />} />
        <Route path="/kho-hoat-dong/:slugWithId" element={<LegacyHtmlDetailRedirect basePath="/kho-hoat-dong" fallbackPath="/hoat-dong-sang-tao" />} />
        <Route path="/bai-hoc-ve/:slugWithId.html" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.DrawingLessonView} Component={DrawingLessonViewer} creatorProps={creatorProps} navState={navState} drawingLessons={drawingLessons} classes={classes} onUpdate={handleUpdate(setDrawingLessons, 'drawing-lessons')} /> : <LoginRedirect />} />
        <Route path="/bai-hoc-ve/:slugWithId.html/edit" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.DrawingLessonEdit} Component={DrawingStudio} creatorProps={creatorProps} navState={navState} drawingLessons={drawingLessons} classes={classes} onSaveDrawingLesson={handleSave(setDrawingLessons, 'drawing-lessons')} onUpdateDrawingLesson={handleUpdate(setDrawingLessons, 'drawing-lessons')} onSaveImage={handleSave(setImages, 'images')} /> : <LoginRedirect />} />
        <Route path="/bai-hoc-ve/:slugWithId/edit" element={<LegacyHtmlDetailRedirect basePath="/bai-hoc-ve" fallbackPath="/hoc-ve" />} />
        <Route path="/bai-hoc-ve/:slugWithId" element={<LegacyHtmlDetailRedirect basePath="/bai-hoc-ve" fallbackPath="/hoc-ve" />} />
        
        {/* --- Student Assignment Routes (Public/Class based) --- */}
        <Route path="/thong-bao/:classIdentifier/:slugWithId.html" element={<AssignmentDetailView user={displayUser} onNavigate={handleNavigate} onStudentSubmit={handleStudentSubmit} homeworks={homeworks} submissions={submissions} />} />
        <Route path="/thong-bao/:slugWithId.html" element={<AssignmentDetailView user={displayUser} onNavigate={handleNavigate} onStudentSubmit={handleStudentSubmit} homeworks={homeworks} submissions={submissions} />} />
        <Route path="/bai-tap/:classIdentifier/:slugWithId.html" element={<AssignmentDetailView user={displayUser} onNavigate={handleNavigate} onStudentSubmit={handleStudentSubmit} homeworks={homeworks} submissions={submissions} />} />
        <Route path="/bai-tap/:slugWithId.html" element={<AssignmentDetailView user={displayUser} onNavigate={handleNavigate} onStudentSubmit={handleStudentSubmit} homeworks={homeworks} submissions={submissions} />} />
        <Route path="/nhiem-vu/:classIdentifier/:slugWithId.html" element={<AssignmentDetailView user={displayUser} onNavigate={handleNavigate} onStudentSubmit={handleStudentSubmit} homeworks={homeworks} submissions={submissions} />} />
        <Route path="/nhiem-vu/:slugWithId.html" element={<AssignmentDetailView user={displayUser} onNavigate={handleNavigate} onStudentSubmit={handleStudentSubmit} homeworks={homeworks} submissions={submissions} />} />
        
        {/* Legacy redirects for assignments */}
        <Route path="/thong-bao/:classIdentifier/:slugWithId" element={<LegacyHtmlDetailRedirect basePath="/thong-bao" fallbackPath="/" />} />
        <Route path="/thong-bao/:slugWithId" element={<LegacyHtmlDetailRedirect basePath="/thong-bao" fallbackPath="/" />} />
        <Route path="/bai-tap/:classIdentifier/:slugWithId" element={<LegacyHtmlDetailRedirect basePath="/bai-tap" fallbackPath="/" />} />
        <Route path="/bai-tap/:slugWithId" element={<LegacyHtmlDetailRedirect basePath="/bai-tap" fallbackPath="/" />} />
        <Route path="/nhiem-vu/:classIdentifier/:slugWithId" element={<LegacyHtmlDetailRedirect basePath="/nhiem-vu" fallbackPath="/" />} />
        <Route path="/nhiem-vu/:slugWithId" element={<LegacyHtmlDetailRedirect basePath="/nhiem-vu" fallbackPath="/" />} />
        
        <Route path="/tao-video/:slugWithId?" element={FEATURE_FLAGS.VIDEO_GENERATION === false ? <Navigate to="/" replace /> : (user ? <CreatorRouteWrapper displayUser={displayUser} view={View.VideoCreator} Component={VideoCreator} creatorProps={creatorProps} navState={navState} videos={videoResources} videoProjects={videoProjects} onSave={handleSave(setVideoResources, 'video-resources')} onUpdate={handleUpdate(setVideoResources, 'video-resources')} onSaveProject={handleSave(setVideoProjects, 'video-projects')} onUpdateProject={handleUpdate(setVideoProjects, 'video-projects')} onCreditUsage={handleCreditUsage} /> : <LoginRedirect />)} />
        
        <Route path="/slide-mau" element={user ? <MasterSlideCreator {...commonProps} presentations={presentations} quizzes={quizzes} homeworks={homeworks} onSave={handleSave(setPresentations, 'presentations')} /> : <LoginRedirect />} />
        
        <Route path="/cai-dat" element={user ? <Settings {...commonProps} userSettings={user} onUpdateSettings={handleUpdateUserSettings as React.Dispatch<React.SetStateAction<UserSettings>>} onBonusCredits={handleBonusCredits} onShowToast={(t) => addToast(t.message, t.type)} onRefreshData={() => loadProductionData('all', true)} /> : <LoginRedirect />} />
        
        <Route path="/soan-thao/:slugWithId?" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.DocumentWorkspace} Component={DocumentWorkspace} creatorProps={creatorProps} navState={navState} documents={documents} onUpdateDocument={handleUpdate(setDocuments, 'documents')} isLoadingData={isLoadingData} /> : <LoginRedirect />} />
        <Route path="/thiet-ke" element={FEATURE_FLAGS.IMAGE_GENERATION === false ? <Navigate to="/" replace /> : (user ? <DesignCenter {...creatorProps} onSaveImage={handleSave(setImages, 'images')} /> : <LoginRedirect />)} />
        <Route path="/lich-su-giao-dich" element={user ? <TransactionHistory onBack={handleBack} onNavigate={handleNavigate} user={displayUser} /> : <LoginRedirect />} />
        <Route path="/tao-skkn/*" element={<LegacySkknRedirect />} />
        <Route path="/tao-sang-kien-kinh-nghiem/:slugWithId?" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.SKKNCreator} Component={SKKNCreator} creatorProps={creatorProps} navState={navState} skknProjects={skknProjects} onSave={handleSave(setSkknProjects, 'skkn-projects')} onUpdate={handleUpdate(setSkknProjects, 'skkn-projects')} onBack={handleBack} onNavigate={handleNavigate} userSettings={displayUser} /> : <LoginRedirect />} />
        <Route path="/ke-hoach-nam" element={user ? <Suspense fallback={null}><YearPlanCreator onBack={handleBack} onShowToast={addToast} /></Suspense> : <LoginRedirect />} />
        <Route path="/bien-ban-hop-to" element={user ? <Suspense fallback={null}><MeetingMinutesCreator onBack={handleBack} onShowToast={addToast} /></Suspense> : <LoginRedirect />} />
        <Route path="/ho-so-chuyen-mon" element={user ? <Suspense fallback={null}><ProfessionalPortfolio onBack={handleBack} onShowToast={addToast} user={displayUser} /></Suspense> : <LoginRedirect />} />
        
        <Route path="/thu-vien" element={user ? <CreatorRouteWrapper displayUser={displayUser} view={View.DocumentLibrary} Component={DocumentLibrary}
          creatorProps={creatorProps}
          navState={navState}
          user={displayUser} 
          classes={classes}
          onBack={() => handleNavigate(View.Dashboard)} 
          onNavigate={handleNavigate} 
          isLoading={isLoadingData}
          activeTab={navState.activeTab}
          documents={documents} 
          lessonPlans={lessonPlans} 
          quizzes={quizzes} 
          oralQuizzes={oralQuizzes}
          games={games} 
          presentations={presentations} 
          scientificSimulations={scientificSimulations}
          homeworks={homeworks} 
          activities={activities} 
          images={images} 
          videoProjects={videoProjects} 
          videoResources={videoResources} 
          onDeleteDocument={handleDelete(setDocuments, 'documents')}
          onUpdateDocumentName={handleUpdateName(setDocuments, 'documents')}
          onDuplicateDocument={handleDuplicate(setDocuments, 'documents')}
          onDeleteLessonPlan={handleDelete(setLessonPlans, 'lesson-plans')}
          onUpdateLessonPlanName={handleUpdateName(setLessonPlans, 'lesson-plans')}
          onDuplicateLessonPlan={handleDuplicate(setLessonPlans, 'lesson-plans')}
          onDeleteQuiz={handleDelete(setQuizzes, 'quizzes')}
          onUpdateQuizName={handleUpdateName(setQuizzes, 'quizzes')}
          onDuplicateQuiz={handleDuplicate(setQuizzes, 'quizzes')}
          onDeleteOralQuiz={handleDelete(setOralQuizzes, 'oral-quizzes')}
          onUpdateOralQuizName={handleUpdateName(setOralQuizzes, 'oral-quizzes')}
          onDeleteGame={handleDelete(setGames, 'games')}
          onUpdateGameName={handleUpdateName(setGames, 'games')}
          onDuplicateGame={handleDuplicate(setGames, 'games')}
          onDeletePresentation={handleDelete(setPresentations, 'presentations')}
          onUpdatePresentationName={handleUpdateName(setPresentations, 'presentations')}
          onDuplicatePresentation={handleDuplicate(setPresentations, 'presentations')}
          onDeleteScientificSimulation={handleDelete(setScientificSimulations, 'scientific-simulations')}
          onUpdateScientificSimulationName={handleUpdateName(setScientificSimulations, 'scientific-simulations')}
          onDuplicateScientificSimulation={handleDuplicate(setScientificSimulations, 'scientific-simulations')}
          onDeleteHomework={handleDelete(setHomeworks, 'homeworks')}
          onUpdateHomeworkName={handleUpdateName(setHomeworks, 'homeworks')}
          onDuplicateHomework={handleDuplicate(setHomeworks, 'homeworks')}
          onDeleteActivity={handleDelete(setActivities, 'activities')}
          onUpdateActivityName={handleUpdateName(setActivities, 'activities')}
          onDuplicateActivity={handleDuplicate(setActivities, 'activities')}
          onFileUploads={handleFileUploads}
          onImageUploads={handleImageUploads}
          onVideoUploads={handleVideoUploads}
          onCombineDocuments={handleCombineAndSegmentDocuments}
          onUpdateImageName={handleUpdateName(setImages, 'images')}
          onDeleteImage={handleDelete(setImages, 'images')}
          onUpdateVideoProjectName={handleUpdateName(setVideoProjects, 'video-projects')}
          onDeleteVideoProject={handleDelete(setVideoProjects, 'video-projects')}
          onDuplicateVideoProject={handleDuplicate(setVideoProjects, 'video-projects')}
          onUpdateVideoResourceName={handleUpdateName(setVideoResources, 'video-resources')}
          onDeleteVideoResource={handleDelete(setVideoResources, 'video-resources')}
          skknProjects={skknProjects}
          students={students}
          onAssignHomework={handleAssignHomework}
          onDeleteSkkn={handleDelete(setSkknProjects, 'skkn-projects')}
          onUpdateSkknName={handleUpdateSkknName}
          onRefreshData={() => loadProductionData('library', true, true)}
          activeUploads={activeUploads}
          onDismissUpload={(id) => setActiveUploads(prev => prev.filter(t => t.id !== id))}
          onShowToast={(msg, type) => addToast(msg, type)}
        /> : <LoginRedirect />} />

        <Route path="/cong-cu-giao-vien" element={activeRoleConfig.isTeacher ? <TeacherDashboard onNavigate={handleNavigate} user={displayUser} isLoading={isLoadingData} onUpdateSettings={handleUpdateUserSettings as React.Dispatch<React.SetStateAction<UserSettings | null>>} /> : <Navigate to="/" />} />
        
        <Route path="/bang-dieu-khien-phu-huynh" element={activeRoleConfig.isParent ? <ParentDashboard 
              onNavigate={handleNavigate} 
              user={displayUser} 
              customGreeting={activeRoleConfig.isRoleSwitched ? undefined : displayUser?.greeting}
              children={children} 
              classes={classes}
              students={students}
              homeworks={homeworks}
              assignments={assignments}
              submissions={submissions}
              studentStats={studentStats}
              notifications={notifications}
              joinRequests={joinRequests}
              onSaveChild={handleSave(setChildren, 'children')} 
              onUpdateChild={handleUpdate(setChildren, 'children')} 
              onDeleteChild={handleDelete(setChildren, 'children')} 
              onUpdateSettings={handleUpdateUserSettings as React.Dispatch<React.SetStateAction<UserSettings | null>>}
              onRefreshData={() => loadProductionData('core,parent', true)}
              onAddNotification={handleAddNotification}
              isLoading={isLoadingData}
          /> : (user?.role === 'teacher' ? <ParentRouteAutoSwitch /> : <Navigate to="/" />)} />

        {/* Trang Lộ trình học sinh cũ đã chuyển sang Deep Workspace → redirect về workspace mới. */}
        <Route path="/lo-trinh-hoc-tap" element={activeRoleConfig.isStudent ? <Navigate to="/workspace/hoc-sinh" replace /> : !user ? <LoginRedirect /> : !user.role ? <Navigate to="/chon-vai-tro" replace /> : <Navigate to="/" />} />
        
        {/* Public feature landing pages - SEO friendly, ad-eligible */}
        <Route path="/tinh-nang" element={<FeatureLandingHub />} />
        <Route path="/tinh-nang/:slug" element={<FeatureLanding />} />

        {/* Landing page theo nhóm người dùng (giáo viên / phụ huynh / học sinh) — public, SEO */}
        <Route path="/cho-giao-vien" element={<AudienceLanding audience="teacher" />} />
        <Route path="/cho-phu-huynh" element={<AudienceLanding audience="parent" />} />
        <Route path="/cho-hoc-sinh" element={<AudienceLanding audience="student" />} />

        {/* ─── Guest Try Pages — không cần đăng nhập, SEO-friendly, có AdSense ─── */}
        {/* Khách: dùng chính creator thật (chế độ khách). Đã đăng nhập: chuyển sang creator đầy đủ. */}
        <Route path="/thu-tao/giao-an"    element={user ? <Navigate to="/tao-giao-an" replace /> : <GuestCreatorHost type="lesson-plan" adsConfig={adsConfig} />} />
        <Route path="/thu-tao/bai-tap"    element={user ? <Navigate to="/tao-bai-tap" replace /> : <GuestCreatorHost type="homework" adsConfig={adsConfig} />} />
        <Route path="/thu-tao/de-kiem-tra" element={user ? <Navigate to="/tao-de-kiem-tra" replace /> : <GuestCreatorHost type="quiz" adsConfig={adsConfig} />} />
        <Route path="/thu-tao/tro-choi"   element={user ? <Navigate to="/tao-tro-choi" replace /> : <GuestCreatorHost type="game" adsConfig={adsConfig} />} />
        <Route path="/thu-tao/hoat-dong"  element={user ? <Navigate to="/hoat-dong-sang-tao" replace /> : <GuestCreatorHost type="activity" adsConfig={adsConfig} />} />

        {/* User đã đăng nhập → Deep Workspace theo role; khách → trang chủ marketing */}
        <Route path="/" element={user ? <Navigate to={activeRoleConfig.workspacePath} replace /> : <Dashboard user={displayUser} onNavigate={handleNavigate} />} />

        <Route path="*" element={<NotFound404 user={displayUser} />} />
      </Routes>
    );
  };

  // === Bare Frame ===
  // Vài route là "màn điều khiển" độc lập (điện thoại giáo viên quét thẻ QR khi
  // đứng lớp) — không cần header/footer marketing, quảng cáo, hay widget nổi
  // (chat góp ý...) vốn chỉ gây rối và có thể đè lên cụm nút điều khiển cố định
  // đáy màn hình. Render bare, edge-to-edge, bất kể trạng thái đăng nhập.
  const BARE_PREFIXES = ['/quiz-the-giay/dieu-khien'];
  const isBareRoute = BARE_PREFIXES.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/'),
  );

  if (isBareRoute) {
    return (
      <ToastProvider value={addToast}>
        <div className="fixed top-20 right-4 z-[9999] flex flex-col gap-3 pointer-events-none">
          {toasts.map(t => (
            <div key={t.id} className="pointer-events-auto">
              <Toast message={t.message} type={t.type} onClose={() => removeToast(t.id)} />
            </div>
          ))}
        </div>
        <Suspense key={location.pathname} fallback={<RouteTransitionFallback />}>
          {renderRoutes()}
        </Suspense>
      </ToastProvider>
    );
  }

  // === App Workspace Frame ===
  // Mọi route của user đã ĐĂNG NHẬP render trong shell workspace (sidebar + AI panel
  // luôn hiển thị, dùng Header gốc). Click nav KHÔNG thoát workspace. Khách & vài route
  // đứng riêng (đăng nhập, chọn vai trò) vẫn dùng giao diện marketing bên dưới.
  const STANDALONE_PREFIXES = ['/dang-nhap', '/chon-vai-tro'];
  const isStandaloneRoute = STANDALONE_PREFIXES.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/'),
  );
  const useAppFrame = !!user && !isStandaloneRoute;

  if (useAppFrame) {
    const workspaceMode = displayUser?.role === 'parent'
      ? 'parent'
      : displayUser?.role === 'student'
        ? 'student'
        : 'teacher';

    return (
      <ToastProvider value={addToast}>
        {/* Toast Container */}
        <div className="fixed top-20 right-4 z-[9999] flex flex-col gap-3 pointer-events-none">
          {toasts.map(t => (
            <div key={t.id} className="pointer-events-auto">
              <Toast message={t.message} type={t.type} onClose={() => removeToast(t.id)} />
            </div>
          ))}
        </div>
        <OutOfCreditsModal isOpen={isOutOfCreditsModalOpen} onClose={() => setIsOutOfCreditsModalOpen(false)} onNavigateToStore={() => { setIsOutOfCreditsModalOpen(false); handleNavigate(View.CreditStore); }} reason={outOfCreditsInfo.reason} resetAt={outOfCreditsInfo.resetAt} />
        {banners.length > 0 && <BannerPopup banners={banners} userId={user?.id ?? null} onNavigate={handleNavigate} />}
        {paymentModalPackage && user && <PaymentModal isOpen={!!paymentModalPackage} onClose={handleCancelPayment} onConfirm={handleConfirmPayment} pkg={paymentModalPackage} user={displayUser} onCreateOrder={handleCreateOrderForPayment} onOrderCreated={(d) => handleUpdateUserSettings(prev => prev ? { ...prev, paymentDetails: { packageId: d.packageId, transactionCode: d.transactionCode, orderId: d.orderId, package: paymentModalPackage, coupon: d.coupon ?? null, baseCredits: d.baseCredits, bonusCredits: d.bonusCredits, totalCredits: d.totalCredits, amountVnd: d.amountVnd } } : prev)} />}

        {workspaceMode === 'teacher' && (
          <TeacherOnboardingWizard
            onDone={() => {}}
            onSaveProfile={(profile) => handleUpdateUserSettings(prev => prev ? {
              ...prev,
              teachingProfile: profile,
              // Đồng bộ sang các preference cũ để mọi form creator prefill ngay
              mainSubject: profile.subjects[0] || prev.mainSubject,
              defaultGradeLevel: profile.schoolLevel,
              preferredClassLevel: profile.grades[0] ? String(profile.grades[0]) : prev.preferredClassLevel,
            } : prev)}
          />
        )}
        {workspaceMode === 'parent' && <ParentOnboardingWizard />}
        <AppWorkspaceShell
          mode={workspaceMode}
          userName={displayUser?.teacherName}
          onNavigate={handleNavigate}
          onShowToast={addToast}
          user={displayUser}
          notifications={notifications}
          onLogout={handleLogout}
          onMarkAsRead={handleMarkAsRead}
          onMarkAllAsRead={handleMarkAllAsRead}
          onClearRead={handleClearRead}
          onSwitchRole={handleSwitchRole}
          activeRoleConfig={activeRoleConfig}
          sidebarFooter={undefined}
        >
          <Suspense key={location.pathname} fallback={<RouteTransitionFallback />}>
            {renderRoutes()}
          </Suspense>
        </AppWorkspaceShell>

        {/* FAB Hỗ trợ / Góp ý cũ đã chuyển vào cuối sidebar workspace (WorkspaceSidebar) */}
        <OfflineSyncManager onSyncAction={handleSyncAction} userId={user?.id || null} />
        <UpgradeModal />
      </ToastProvider>
    );
  }

  return (
    <ToastProvider value={addToast}>
    <div className="min-h-screen overflow-x-hidden bg-slate-50">
      {/* Toast Container */}
      <div className="fixed top-20 right-4 z-[9999] flex flex-col gap-3 pointer-events-none">
        {toasts.map(t => (
          <div key={t.id} className="pointer-events-auto">
            <Toast message={t.message} type={t.type} onClose={() => removeToast(t.id)} />
          </div>
        ))}
      </div>
      <OutOfCreditsModal isOpen={isOutOfCreditsModalOpen} onClose={() => setIsOutOfCreditsModalOpen(false)} onNavigateToStore={() => { setIsOutOfCreditsModalOpen(false); handleNavigate(View.CreditStore); }} reason={outOfCreditsInfo.reason} resetAt={outOfCreditsInfo.resetAt} />
      {user && requiresPasswordChange && <MustChangePasswordModal onDone={() => setRequiresPasswordChange(false)} />}
      {banners.length > 0 && <BannerPopup banners={banners} userId={user?.id ?? null} onNavigate={handleNavigate} />}
      {paymentModalPackage && user && <PaymentModal isOpen={!!paymentModalPackage} onClose={handleCancelPayment} onConfirm={handleConfirmPayment} pkg={paymentModalPackage} user={displayUser} onCreateOrder={handleCreateOrderForPayment} onOrderCreated={(d) => handleUpdateUserSettings(prev => prev ? { ...prev, paymentDetails: { packageId: d.packageId, transactionCode: d.transactionCode, orderId: d.orderId, package: paymentModalPackage, coupon: d.coupon ?? null, baseCredits: d.baseCredits, bonusCredits: d.bonusCredits, totalCredits: d.totalCredits, amountVnd: d.amountVnd } } : prev)} />}
      <Header onNavigate={handleNavigate} currentView={currentView} user={location.pathname === '/dang-nhap' ? null : displayUser} onLogout={handleLogout} notifications={user ? notifications : guestAnnouncements} onMarkAsRead={handleMarkAsRead} onMarkAllAsRead={handleMarkAllAsRead} onClearRead={handleClearRead} protectedViews={protectedViews} showAppNameInHeader={headerConfig.showAppName} appName={headerConfig.appName} onSwitchRole={handleSwitchRole} activeRoleConfig={activeRoleConfig} />
      {/* Header banner ad - only on public content pages approved by ad policy */}
      {showDisplayAds && adsConfig?.slots?.header_banner?.enabled && canShowAdSlot(location.pathname, 'header_banner', displayUser) && (
        <div className="flex justify-center px-4">
          <AdSlot slot={adsConfig.slots.header_banner} slotId="header_banner" user={displayUser} />
        </div>
      )}
      {user && activeRoleConfig.role && (user.showFloatingMenu ?? true) && <FloatingMenu user={displayUser} onNavigate={handleNavigate} onUpdateSettings={handleUpdateUserSettings as React.Dispatch<React.SetStateAction<UserSettings>>} onSwitchRole={handleSwitchRole} activeRoleConfig={activeRoleConfig} />}
      <ContactButtons />
      {user && <Feedback currentView={currentView} />}
      <OfflineSyncManager onSyncAction={handleSyncAction} userId={user?.id || null} />
      <div className="relative flex w-full max-w-full min-w-0 justify-center gap-4 px-4 sm:px-6 md:gap-6 lg:gap-8 lg:px-8">
        {/* Left Skyscraper Ad */}
        <div className="hidden w-40 flex-shrink-0 2xl:block">
            <div className="sticky top-24">
                 {showDisplayAds && adsConfig?.slots?.skyscraper_left?.enabled && canShowAdSlot(location.pathname, 'skyscraper_left', displayUser) && <AdSlot slot={adsConfig.slots.skyscraper_left} slotId="skyscraper_left" user={displayUser} />}
            </div>
        </div>

        {/* Main Content */}
        <main className="relative min-w-0 w-full max-w-7xl flex-grow py-4 sm:py-6 lg:py-8">
          <Breadcrumbs items={buildBreadcrumbs()} />
          {isBackgroundLoading && !isLoadingData && location.pathname !== '/dang-nhap' && (
            <div className="absolute top-24 left-1/2 -translate-x-1/2 z-[100] pointer-events-none">
              <LoadingDataIndicator />
            </div>
          )}
          {/* key theo pathname: RR v7 + startTransition giữ UI cũ trong Suspense đã resolve
              cho đến khi chunk route mới tải xong → URL đổi nhưng vẫn thấy trang cũ (vd QuizCreator).
              Remount boundary khi pathname đổi; giữ nguyên khi chỉ đổi query (step=, buoc=). */}
          <Suspense key={location.pathname} fallback={<RouteTransitionFallback />}>
            {renderRoutes()}
          </Suspense>
        </main>

        {/* Right Skyscraper Ad */}
        <div className="hidden w-40 flex-shrink-0 2xl:block">
             <div className="sticky top-24">
                {showDisplayAds && adsConfig?.slots?.skyscraper_right?.enabled && canShowAdSlot(location.pathname, 'skyscraper_right', displayUser) && <AdSlot slot={adsConfig.slots.skyscraper_right} slotId="skyscraper_right" user={displayUser} />}
            </div>
        </div>
      </div>
      {/* Footer banner ad */}
      {showDisplayAds && adsConfig?.slots?.footer_banner?.enabled && canShowAdSlot(location.pathname, 'footer_banner', displayUser) && (
        <div className="flex justify-center px-4">
          <AdSlot slot={adsConfig.slots.footer_banner} slotId="footer_banner" user={displayUser} />
        </div>
      )}
      <Footer user={displayUser} onNavigate={handleNavigate} />
      <UpgradeModal />
    </div>
    </ToastProvider>
  );
};

export default App;